import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  CalendarBlank,
  Camera,
  Gift,
  Heart,
  House,
  MapPin,
  Pause,
  PencilSimple,
  Phone,
  Play,
  Sparkle,
  Ticket,
  TShirt,
} from '@phosphor-icons/react';
import { resolveMediaUrl } from '../../utils/media';

const FALLBACK_THEME = {
  primary_color: '#5D7061',
  secondary_color: '#D4AF37',
  accent_color: '#B76E79',
  background_color: '#FFFDF8',
  text_color: '#2C2926',
  heading_font: 'Playfair Display',
  body_font: 'Inter',
  surface_style: 'soft',
  corner_style: 'rounded',
  spacing: 'comfortable',
};

const FALLBACK_LAYOUT = {
  mode: 'stacked',
  hero_panel_position: 'left',
  hero_panel_width: 46,
  background_image_url: null,
  frame_image_url: null,
  decorative_image_url: null,
  background_position: 'center',
  show_frame: false,
  overlay_strength: 0.72,
  section_style: 'default',
  section_divider: 'none',
  mobile_hero_height: 'tall',
};

const FALLBACK_OPENING = {
  enabled: true,
  eyebrow: 'A celebration awaits',
  title: 'Something beautiful begins',
  message: 'Open the invitation and step into the celebration.',
  action_label: 'Unfold the invitation',
};

const FALLBACK_MUSIC = {
  enabled: true,
  track_id: 'soft_piano',
  custom_audio_url: null,
  custom_audio_name: null,
  volume: 0.32,
};

const SAMPLE_COUPLE_IMAGES = [
  '/assets/web-invites/sample-couple/garden-walk.jpg',
  '/assets/web-invites/sample-couple/linked-hands.jpg',
];

function runtimeLayout(config) {
  const layout = { ...FALLBACK_LAYOUT, ...(config?.layout || {}) };
  if (config?.template_id !== 'galago.south-indian-temple-brass') return layout;
  if (layout.background_image_url === '/assets/web-invites/south-indian-temple-brass/temple-courtyard-mobile.jpg') layout.background_image_url = '/assets/web-invites/south-indian-temple-brass/temple-dawn-courtyard-v2.jpg';
  if (layout.frame_image_url === '/assets/web-invites/south-indian-temple-brass/brass-torana-mobile.png') {
    layout.frame_image_url = null;
    layout.show_frame = false;
  }
  if (layout.decorative_image_url === '/assets/web-invites/south-indian-temple-brass/deepam-urli-mobile.png') layout.decorative_image_url = '/assets/web-invites/south-indian-temple-brass/temple-bell-v2.png';
  if (layout.section_style === 'temple_parchment') layout.section_style = 'temple_courtyard';
  if (layout.section_divider === 'temple_arch') layout.section_divider = 'temple_steps';
  return layout;
}

const BOTANICAL_PETAL_SECTIONS = new Set(['story', 'itinerary', 'rsvp']);
const BOTANICAL_SPRAY_SECTIONS = new Set(['itinerary', 'gallery', 'contacts']);
const HERO_ECHO_SECTIONS = new Set(['story', 'venue', 'rsvp']);
const PAPER_PANEL_SECTIONS = new Set(['countdown', 'itinerary', 'wishes']);

const BUILT_IN_MUSIC_URLS = {
  garden_waltz: '/assets/web-invites/music/garden-waltz.mp3',
  soft_piano: '/assets/web-invites/music/soft-piano.mp3',
  moonlit_strings: '/assets/web-invites/music/moonlit-strings.mp3',
  // Preserve existing temple invites while the built-in library has three recordings.
  temple_veena: '/assets/web-invites/music/garden-waltz.mp3',
};

const EFFECT_SYMBOLS = {
  snow: '❄',
  petals: '✿',
  hearts: '♥',
  fireflies: '•',
  confetti: '◆',
  golden_dust: '✦',
  sakura: '❀',
  celestial: '✧',
  diyas: '🪔',
};

const SPACING = { compact: '20px', comfortable: '36px', airy: '52px' };
const RADIUS = { square: '0px', soft: '10px', rounded: '22px', pill: '999px' };

function alpha(hex, opacity) {
  const safe = /^#[0-9a-f]{6}$/i.test(hex || '') ? hex : '#000000';
  const red = parseInt(safe.slice(1, 3), 16);
  const green = parseInt(safe.slice(3, 5), 16);
  const blue = parseInt(safe.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
}

function readableDate(value, options = {}) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric', ...options,
  }).format(date);
}

function readableShortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(date);
}

function readableInviteDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
}

function displayText(value) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function venueLabel(value) {
  if (value == null) return '';
  const direct = displayText(value);
  if (direct) return direct;
  if (typeof value !== 'object') return '';
  return [value.name, value.hall_name, value.address, value.floor, value.notes]
    .map(displayText)
    .find(Boolean) || '';
}

function venueAddress(value) {
  if (!value || typeof value !== 'object') return '';
  const address = displayText(value.address);
  if (address) return address;
  const details = [value.hall_name, value.floor].map(displayText).filter(Boolean).join(', ');
  return details || displayText(value.name);
}

function venueFullAddress(value) {
  if (!value || typeof value !== 'object') return '';
  const parts = [value.hall_name, value.floor, value.address, value.notes]
    .map(displayText)
    .map((item) => item.trim())
    .filter(Boolean);
  const seen = new Set();
  return parts.filter((part) => {
    const key = part.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).join(', ');
}

function displayNames(data = {}) {
  if (data.bride_name || data.groom_name) {
    return [data.bride_name, data.groom_name].filter(Boolean).join(' & ');
  }
  const people = data.honoree_names?.length ? data.honoree_names : data.host_names;
  return (people || []).filter(Boolean).join(' & ') || data.event_name || data.name || 'Our Celebration';
}

function ordinalSuffix(value) {
  const number = Number.parseInt(value, 10);
  if (!Number.isFinite(number)) return 'th';
  const mod100 = number % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  const mod10 = number % 10;
  if (mod10 === 1) return 'st';
  if (mod10 === 2) return 'nd';
  if (mod10 === 3) return 'rd';
  return 'th';
}

function CoupleNameStack({ names, className = '', ampersandClassName = '' }) {
  const parts = String(names || '').split(/\s*&\s*/).filter(Boolean);
  if (parts.length < 2) return <span className={className}>{names}</span>;
  return (
    <span className={`inline-flex flex-col items-center ${className}`}>
      <span>{parts[0]}</span>
      <span className={`my-0.5 text-[0.52em] leading-none opacity-70 ${ampersandClassName}`}>&amp;</span>
      <span>{parts.slice(1).join(' & ')}</span>
    </span>
  );
}

function eventSource(section, eventData) {
  if (section.source_mode === 'custom') return {};
  const content = section.content || {};
  if (section.type === 'hero') return { ...eventData, ...content };
  return content;
}

function usesEventContent(section) {
  return section.source_mode !== 'custom';
}

function sectionText(section, key, fallback = '') {
  const value = section.overrides?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function findScrollParent(node) {
  let parent = node?.parentElement || null;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
}

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [target]);

  const targetTime = target ? new Date(target).getTime() : Number.NaN;
  const remaining = Number.isNaN(targetTime) ? 0 : Math.max(0, targetTime - now);
  const seconds = Math.floor(remaining / 1000);
  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    isPast: Boolean(target && remaining === 0),
  };
}

function DecorativeEffect({ effect, intensity = 0.8, speed = 0.5, accent }) {
  const symbol = EFFECT_SYMBOLS[effect];
  const particles = useMemo(() => {
    if (!symbol) return [];
    const count = Math.round(7 + intensity * 17);
    return Array.from({ length: count }, (_, index) => ({
      id: index,
      left: `${(index * 37 + 11) % 97}%`,
      delay: `${-((index * 1.37) % 9)}s`,
      duration: `${11 - speed * 5 + (index % 5)}s`,
      size: `${10 + (index % 4) * 4}px`,
      opacity: 0.28 + (index % 5) * 0.1,
    }));
  }, [intensity, speed, symbol]);

  if (!symbol) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden" aria-hidden="true">
      {particles.map((particle) => (
        <span
          key={particle.id}
          className="web-invite-particle absolute -top-8"
          style={{
            left: particle.left,
            color: effect === 'snow' ? '#FFFFFF' : accent,
            animationDelay: particle.delay,
            animationDuration: particle.duration,
            fontSize: particle.size,
            opacity: particle.opacity,
          }}
        >
          {symbol}
        </span>
      ))}
    </div>
  );
}

function heroArtwork(section, eventData, layout) {
  if (!section) return layout.background_image_url;
  const content = eventSource(section, eventData);
  const heroMedia = content.hero_media || content.guest_experience?.hero_media || [];
  const visibleMedia = heroMedia.find((item) => item.visible !== false && item.url);
  return section.overrides?.image_url
    || layout.background_image_url
    || visibleMedia?.url
    || content.couple_photo_url
    || content.guest_experience?.couple_photo_url
    || content.wedding_photos_preview?.[0];
}

function coupleHeroPhoto(section, eventData) {
  if (!section) return null;
  const content = eventSource(section, eventData);
  const heroMedia = content.hero_media || content.guest_experience?.hero_media || [];
  const visibleMedia = heroMedia.find((item) => item.visible !== false && item.url);
  return section.overrides?.image_url
    || content.couple_photo_url
    || content.guest_experience?.couple_photo_url
    || content.wedding_photos_preview?.[0]
    || visibleMedia?.url
    || null;
}

function RiverRippleMark({ color, className = '' }) {
  return (
    <svg viewBox="0 0 180 54" className={className} fill="none" aria-hidden="true">
      <path d="M8 15C34 4 55 27 82 15C108 4 130 27 172 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M25 28C51 17 72 40 99 28C124 17 143 37 160 29" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.66" />
      <path d="M49 41C68 33 84 49 103 41C120 34 132 44 143 41" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

function BirthdaySealMark({ color, accent, className = '' }) {
  return (
    <div className={`relative grid place-items-center rounded-full border-[5px] border-double ${className}`} style={{ borderColor: accent, background: `radial-gradient(circle at 35% 30%, ${alpha(accent, 0.95)}, ${color} 68%)` }} aria-hidden="true">
      <Sparkle className="h-[38%] w-[38%] text-white/90" weight="fill" />
    </div>
  );
}

function BalloonDinnerTitle({ eyebrow, title, honoreeName, milestone, theme, compact = false }) {
  return (
    <div className="text-center text-white drop-shadow-2xl">
      <p className={`${compact ? 'text-[0.68rem]' : 'text-[0.82rem]'} font-black uppercase tracking-[0.1em]`}>{eyebrow}</p>
      <p className={`${compact ? 'mt-2 text-[3.2rem]' : 'mt-3 text-[4.35rem]'} font-normal leading-[0.72]`} style={{ fontFamily: '"Great Vibes", "Playfair Display", cursive' }}>Birthday</p>
      <p className={`${compact ? 'mt-1 text-[2.5rem]' : 'mt-1 text-[3.25rem]'} font-black uppercase leading-none tracking-[0.02em]`} style={{ fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>{title.replace(/^Birthday\s*/i, '') || 'Dinner'}</p>
      <p className={`${compact ? 'mt-7 text-[2.8rem]' : 'mt-9 text-[3.5rem]'} font-normal leading-[0.74]`} style={{ fontFamily: '"Great Vibes", "Playfair Display", cursive' }}>{honoreeName}</p>
      <p className={`${compact ? 'mt-2 text-[2.25rem]' : 'mt-3 text-[2.75rem]'} font-black uppercase leading-none`} style={{ fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>{milestone}{ordinalSuffix(milestone)} Birthday</p>
    </div>
  );
}

const BIRTHDAY_BALLOON_RELEASE_PIECES = [
  { asset: 8, x: 344, y: 164, width: 259, height: 325, string: 'M474 484C453 650 495 820 468 970C452 1082 485 1172 472 1250', origin: '50.4% 29%', delay: '.04s', duration: '3.35s', x1: '0px', x2: '0px', x3: '0px', x5: '0px', r1: '0deg', r2: '0deg', r3: '0deg', r4: '0deg' },
  { asset: 2, x: 174, y: 282, width: 218, height: 285, string: 'M283 562C292 690 339 798 350 908C365 1035 438 1137 462 1250', origin: '30.1% 33.6%', delay: '.18s', duration: '3.05s', x1: '-11px', x2: '-21px', x3: '-32px', x5: '-42px', r1: '-.5deg', r2: '-1deg', r3: '-1.5deg', r4: '-2deg' },
  { asset: 0, x: 533, y: 306, width: 230, height: 294, string: 'M648 595C637 708 593 812 581 925C569 1039 514 1149 500 1250', origin: '68.9% 35.6%', delay: '.30s', duration: '2.85s', x1: '11px', x2: '21px', x3: '32px', x5: '42px', r1: '.5deg', r2: '1deg', r3: '1.5deg', r4: '2deg' },
  { asset: 1, x: 244, y: 409, width: 244, height: 304, string: 'M366 708C359 823 407 924 414 1033C421 1134 459 1201 468 1250', origin: '38.9% 42.3%', delay: '.08s', duration: '3.55s', x1: '-6px', x2: '-12px', x3: '-18px', x5: '-24px', r1: '-.4deg', r2: '-.8deg', r3: '-1.2deg', r4: '-1.6deg' },
  { asset: 4, x: 92, y: 542, width: 225, height: 296, string: 'M205 833C229 905 292 945 313 1014C339 1091 426 1161 454 1250', origin: '21.8% 49.8%', delay: '.42s', duration: '3.15s', x1: '-14px', x2: '-27px', x3: '-41px', x5: '-54px', r1: '-.7deg', r2: '-1.4deg', r3: '-2.1deg', r4: '-2.8deg' },
  { asset: 3, x: 453, y: 517, width: 230, height: 303, string: 'M568 815C578 900 537 967 550 1040C565 1122 511 1196 494 1250', origin: '60.4% 48.7%', delay: '.22s', duration: '3.45s', x1: '5px', x2: '9px', x3: '14px', x5: '18px', r1: '.3deg', r2: '.6deg', r3: '.9deg', r4: '1.2deg' },
  { asset: 7, x: 638, y: 542, width: 216, height: 286, string: 'M746 823C721 897 674 956 649 1024C621 1095 548 1164 514 1250', origin: '79.4% 49.2%', delay: '.50s', duration: '2.95s', x1: '13px', x2: '25px', x3: '38px', x5: '50px', r1: '.6deg', r2: '1.2deg', r3: '1.8deg', r4: '2.4deg' },
  { asset: 6, x: 250, y: 652, width: 278, height: 367, string: 'M389 1014C414 1061 397 1114 429 1159C450 1194 462 1227 474 1250', origin: '41.4% 60.4%', delay: '.12s', duration: '3.75s', x1: '0px', x2: '0px', x3: '0px', x5: '0px', r1: '0deg', r2: '0deg', r3: '0deg', r4: '0deg' },
  { asset: 5, x: 524, y: 765, width: 210, height: 278, string: 'M629 1038C620 1081 584 1113 582 1150C579 1194 527 1229 506 1250', origin: '66.9% 61.8%', delay: '.36s', duration: '3.25s', x1: '6px', x2: '13px', x3: '19px', x5: '25px', r1: '.4deg', r2: '.8deg', r3: '1.2deg', r4: '1.6deg' },
];

function OpeningScreen({ opening, heroSection, eventData, theme, effects, layout, onEnter, onReveal, withMusic }) {
  const openingHeight = 'var(--invite-scrollport-height, 100svh)';
  const artwork = heroArtwork(heroSection, eventData, layout);
  const birthdaySurprise = heroSection?.variant === 'birthday_milestone';
  const birthdayChampagne = heroSection?.variant === 'birthday_champagne_toast';
  const birthdayBalloonDinner = heroSection?.variant === 'birthday_balloon_dinner';
  const gardenFrame = heroSection?.variant === 'garden_frame';
  const templeBell = heroSection?.variant === 'temple_courtyard' || heroSection?.variant === 'temple_gateway';
  const royalTapestry = heroSection?.variant === 'royal_tapestry';
  const kashiRiverfront = heroSection?.variant === 'kashi_riverfront';
  const [birthdayOpening, setBirthdayOpening] = useState(false);
  const [champagneOpening, setChampagneOpening] = useState(false);
  const [balloonReleasing, setBalloonReleasing] = useState(false);
  const [royalOpening, setRoyalOpening] = useState(false);
  const [templeRinging, setTempleRinging] = useState(false);
  const [kashiCrossing, setKashiCrossing] = useState(false);
  const [kashiRevealing, setKashiRevealing] = useState(false);
  const openingTimerRef = useRef(null);
  const revealTimerRef = useRef(null);

  useEffect(() => () => {
    if (openingTimerRef.current != null) window.clearTimeout(openingTimerRef.current);
    if (revealTimerRef.current != null) window.clearTimeout(revealTimerRef.current);
  }, []);

  const enter = () => {
    if (!birthdaySurprise && !birthdayChampagne && !birthdayBalloonDinner && !royalTapestry && !templeBell && !kashiRiverfront) {
      onEnter();
      return;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      onEnter();
      return;
    }
    if (templeBell) {
      setTempleRinging(true);
      openingTimerRef.current = window.setTimeout(onEnter, 920);
      return;
    }
    if (kashiRiverfront) {
      setKashiCrossing(true);
      revealTimerRef.current = window.setTimeout(() => {
        setKashiRevealing(true);
        onReveal?.();
      }, 900);
      openingTimerRef.current = window.setTimeout(onEnter, 3800);
      return;
    }
    if (birthdaySurprise) {
      setBirthdayOpening(true);
      revealTimerRef.current = window.setTimeout(() => onReveal?.(), 980);
      openingTimerRef.current = window.setTimeout(onEnter, 1780);
      return;
    }
    if (birthdayChampagne) {
      setChampagneOpening(true);
      revealTimerRef.current = window.setTimeout(() => onReveal?.(), 560);
      openingTimerRef.current = window.setTimeout(onEnter, 1650);
      return;
    }
    if (birthdayBalloonDinner) {
      setBalloonReleasing(true);
      revealTimerRef.current = window.setTimeout(() => onReveal?.(), 980);
      openingTimerRef.current = window.setTimeout(onEnter, 3650);
      return;
    }
    setRoyalOpening(true);
    openingTimerRef.current = window.setTimeout(onEnter, 820);
  };

  if (birthdayBalloonDinner) {
    const content = eventSource(heroSection, eventData);
    const honoreeName = sectionText(heroSection, 'name', displayNames(content));
    const milestone = sectionText(heroSection, 'milestone', displayText(content.turning) || displayText(content.age) || '30');
    const title = sectionText(heroSection, 'headline', opening.title || 'Birthday Dinner');
    const eyebrow = sectionText(heroSection, 'eyebrow', opening.eyebrow || 'You are invited to');
    const cleanPlateArtwork = '/assets/web-invites/birthday-balloon-dinner/balloon-gift-clean-plate.png';
    return (
      <div
        className={`absolute inset-0 z-[70] overflow-hidden text-center ${balloonReleasing ? 'web-invite-balloon-opening-releasing' : ''}`}
        style={{
          minHeight: openingHeight,
          color: '#FFF7EA',
          backgroundColor: theme.primary_color,
          backgroundImage: `linear-gradient(180deg, #506173, #172332)`,
          backgroundPosition: layout.background_position,
          backgroundSize: 'cover',
        }}
      >
        <div className="web-invite-balloon-clean-scene pointer-events-none absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${resolveMediaUrl(cleanPlateArtwork)})` }} aria-hidden="true" />
        <div className="web-invite-balloon-release-scene pointer-events-none absolute inset-0 bg-cover bg-center" style={{ backgroundImage: artwork ? `url(${resolveMediaUrl(artwork)})` : undefined }} aria-hidden="true" />
        <div className="web-invite-balloon-release-layer pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
          <div className="web-invite-balloon-artwork-rig absolute inset-0">
            {BIRTHDAY_BALLOON_RELEASE_PIECES.map((piece, index) => {
              return (
                <svg
                  key={`${piece.asset}-${index}`}
                  className="web-invite-balloon-source-piece absolute inset-0 h-full w-full"
                  viewBox="0 0 940 1672"
                  preserveAspectRatio="xMidYMid slice"
                  focusable="false"
                  style={{
                    '--balloon-delay': piece.delay,
                    '--balloon-duration': piece.duration,
                    '--balloon-x1': piece.x1,
                    '--balloon-x2': piece.x2,
                    '--balloon-x3': piece.x3,
                    '--balloon-x5': piece.x5,
                    '--balloon-r1': piece.r1,
                    '--balloon-r2': piece.r2,
                    '--balloon-r3': piece.r3,
                    '--balloon-r4': piece.r4,
                    transformOrigin: piece.origin,
                  }}
                >
                  <path className="web-invite-balloon-string-shadow" d={piece.string} fill="none" vectorEffect="non-scaling-stroke" />
                  <path className="web-invite-balloon-string-highlight" d={piece.string} fill="none" vectorEffect="non-scaling-stroke" />
                  <image
                    className="web-invite-balloon-complete-image"
                    href={resolveMediaUrl(`/assets/web-invites/birthday-balloon-dinner/balloon-complete-${piece.asset}.png`)}
                    x={piece.x}
                    y={piece.y}
                    width={piece.width}
                    height={piece.height}
                    preserveAspectRatio="xMidYMid meet"
                  />
                </svg>
              );
            })}
            <span className="web-invite-balloon-release-knot absolute" />
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/8 via-black/8 to-black/38" aria-hidden="true" />
        <div className="relative z-[3] flex flex-col items-center justify-between px-7 py-8" style={{ minHeight: openingHeight }}>
          <div className={`web-invite-balloon-title-card pt-4 ${balloonReleasing ? 'web-invite-balloon-title-leaving' : ''}`}>
            <BalloonDinnerTitle eyebrow={eyebrow} title={title} honoreeName={honoreeName} milestone={milestone} theme={theme} />
          </div>
          <button
            type="button"
            onClick={enter}
            disabled={balloonReleasing}
            className={`web-invite-balloon-release-action inline-flex items-center gap-2 rounded-full border px-6 py-3 text-[0.76rem] font-black uppercase tracking-[0.14em] shadow-2xl backdrop-blur-md transition duration-500 ${balloonReleasing ? 'translate-y-4 opacity-0' : 'opacity-100'}`}
            style={{ color: theme.primary_color, background: alpha('#FFF7EA', 0.94), borderColor: alpha(theme.secondary_color, 0.72) }}
            aria-label={opening.action_label}
          >
            {opening.action_label}<ArrowRight className="h-4 w-4" />
          </button>
          <div className={`web-invite-balloon-note max-w-[19rem] rounded-2xl border px-5 py-4 text-white shadow-xl backdrop-blur-md transition duration-700 ${balloonReleasing ? 'translate-y-4 opacity-0' : 'opacity-100'}`} style={{ background: alpha('#0A1421', 0.46), borderColor: alpha(theme.secondary_color, 0.42) }}>
            <p className="text-[0.95rem] font-semibold leading-7">{opening.message}</p>
            <p className="mt-3 text-[0.62rem] font-black uppercase tracking-[0.18em]" style={{ color: theme.secondary_color }}>{withMusic ? 'Dinner music starts after release' : 'Animated dinner details'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (birthdayChampagne) {
    const honoreeName = sectionText(heroSection, 'name', displayNames(eventSource(heroSection, eventData)));
    const milestone = sectionText(heroSection, 'milestone', displayText(eventData.turning) || displayText(eventData.age) || '50');
    const milestoneWithSuffix = `${milestone}${ordinalSuffix(milestone)}`;
    const defaultOpeningTitle = 'A milestone birthday toast';
    const legacyOpeningTitle = "Debbie's 50th Birthday";
    const openingTitle = opening.title && ![defaultOpeningTitle, legacyOpeningTitle].includes(opening.title)
      ? opening.title
      : `${honoreeName}${String(honoreeName).endsWith('s') ? "'" : "'s"} ${milestoneWithSuffix} Birthday`;
    const bottleArtwork = layout.bottle_image_url || '/assets/web-invites/birthday-champagne-bokeh/champagne-bottle-premium-burgundy-static.png';
    const corkArtwork = layout.cork_image_url || '/assets/web-invites/birthday-champagne-bokeh/champagne-cork-only.png';
    const bubbles = Array.from({ length: 18 }, (_, index) => ({
      id: index,
      left: `${6 + ((index * 17) % 88)}%`,
      size: `${8 + (index % 5) * 5}px`,
      delay: `${(index % 6) * 70}ms`,
    }));
    const pressureDrops = Array.from({ length: 6 }, (_, index) => ({
      id: index,
      left: `${50 + (index - 2.5) * 3.2}%`,
      delay: `${index * 54}ms`,
      drift: `${(index - 2.5) * 6}px`,
      size: `${3 + (index % 3)}px`,
    }));
    return (
      <div
        className={`absolute inset-0 z-[70] overflow-x-hidden overflow-y-auto text-center ${champagneOpening ? 'web-invite-toast-opening-popped' : ''}`}
        style={{
          minHeight: openingHeight,
          color: '#FFF7EA',
          backgroundColor: theme.primary_color,
          backgroundImage: artwork
            ? `linear-gradient(180deg, ${alpha('#180711', 0.18)}, ${alpha(theme.primary_color, 0.12)} 44%, ${alpha('#180711', 0.78)}), url(${resolveMediaUrl(artwork)})`
            : `radial-gradient(circle at 24% 18%, ${alpha(theme.secondary_color, 0.32)}, transparent 30%), linear-gradient(160deg, ${theme.primary_color}, #260713)`,
          backgroundPosition: layout.background_position,
          backgroundSize: 'cover',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/8 via-transparent to-black/42" aria-hidden="true" />
        <div className="web-invite-toast-bubble-field pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
          {bubbles.map((bubble) => (
            <span
              key={bubble.id}
              className="web-invite-toast-bubble absolute bottom-24 rounded-full border"
              style={{
                left: bubble.left,
                width: bubble.size,
                height: bubble.size,
                animationDelay: champagneOpening ? bubble.delay : `${Number.parseInt(bubble.delay, 10) + 900}ms`,
                borderColor: alpha(theme.secondary_color, 0.58),
                background: alpha('#FFF7EA', 0.18),
              }}
            />
          ))}
        </div>
        <div className="relative z-[3] flex flex-col items-center justify-between px-7 py-9" style={{ minHeight: openingHeight }}>
          <div className={`web-invite-toast-opening-copy max-w-[21rem] transition duration-700 ${champagneOpening ? '-translate-y-5 opacity-0' : 'opacity-100'}`}>
            <p className="text-[0.7rem] font-black uppercase tracking-[0.26em]" style={{ color: theme.secondary_color, textShadow: '0 2px 16px rgba(0,0,0,.65)' }}>{opening.eyebrow}</p>
            <h1 className="mx-auto mt-4 max-w-[18rem] text-[2.35rem] font-bold leading-[0.96] drop-shadow-2xl" style={{ fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif`, textWrap: 'balance' }}>{openingTitle}</h1>
          </div>
          <button
            type="button"
            onClick={enter}
            disabled={champagneOpening}
            className="group relative my-5 grid w-full max-w-[21rem] place-items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
            style={{ outlineColor: '#FFF7EA' }}
            aria-label={opening.action_label}
          >
            <div className="relative w-full" style={{ height: 'clamp(9rem, calc(var(--invite-scrollport-height, 100svh) * .4), 25rem)' }} aria-hidden="true">
              <div className="web-invite-toast-spark pointer-events-none absolute left-1/2 top-[8.5%] h-10 w-10 -translate-x-1/2 rounded-full border" style={{ borderColor: alpha(theme.secondary_color, 0.34), background: `radial-gradient(circle, ${alpha(theme.secondary_color, 0.2)}, transparent 62%)` }} />
              <div className="web-invite-real-bottle relative mx-auto h-full w-[15.5rem]">
                <div className="web-invite-bottle-static absolute inset-0 z-[1]">
                  <img src={resolveMediaUrl(bottleArtwork)} alt="" className="web-invite-real-bottle-layer absolute inset-0 h-full w-full object-contain drop-shadow-2xl" />
                </div>
                <span className="web-invite-real-pressure absolute left-1/2 top-[2.2%] z-[3] h-10 w-6 -translate-x-1/2 rounded-full opacity-0" style={{ background: `radial-gradient(ellipse at 50% 88%, ${alpha('#FFF7EA', 0.72)} 0%, ${alpha('#FFF7EA', 0.34)} 27%, ${alpha('#FFF7EA', 0.1)} 50%, transparent 76%)` }} />
                <span className="web-invite-real-mouth-glint absolute left-1/2 top-[4.7%] z-[4] h-1.5 w-4 -translate-x-1/2 rounded-full opacity-60" style={{ background: alpha('#FFF7EA', 0.38), boxShadow: `0 0 5px ${alpha(theme.secondary_color, 0.34)}` }} />
                {pressureDrops.map((drop) => (
                  <span
                    key={drop.id}
                    className="web-invite-mouth-droplet absolute top-[4.5%] z-[4] rounded-full opacity-0"
                    style={{
                      left: drop.left,
                      height: drop.size,
                      width: drop.size,
                      background: alpha('#FFF7EA', 0.9),
                      '--drop-delay': drop.delay,
                      '--drop-drift': drop.drift,
                    }}
                  />
                ))}
                <img src={resolveMediaUrl(corkArtwork)} alt="" className={`web-invite-toast-cork web-invite-real-cork absolute left-1/2 top-[-1.2%] z-[5] h-[9.5%] w-[11%] -translate-x-1/2 object-contain drop-shadow-md ${champagneOpening ? 'web-invite-toast-cork-popped' : ''}`} />
                <div className="absolute left-1/2 top-[68%] z-[5] flex h-[6.2rem] w-[7rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center text-center" style={{ color: theme.primary_color }}>
                  <p className="text-[1.95rem] font-black leading-[0.82]" style={{ color: theme.secondary_color, fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif' }}>{milestoneWithSuffix}</p>
                  <p className="mt-1 text-[0.68rem] font-black uppercase leading-[0.86] tracking-[0.035em]" style={{ color: alpha(theme.primary_color, 0.76) }}>
                    Birthday<br />Toast
                  </p>
                </div>
              </div>
            </div>
            <span className={`mt-5 inline-flex items-center gap-2 rounded-full border px-6 py-3 text-[0.76rem] font-semibold uppercase tracking-[0.14em] shadow-xl backdrop-blur-sm transition duration-500 ${champagneOpening ? 'translate-y-3 opacity-0' : 'opacity-100'}`} style={{ color: theme.primary_color, background: '#FFF7EA', borderColor: alpha(theme.secondary_color, 0.78) }}>
              {opening.action_label}<Sparkle className="h-4 w-4" weight="fill" />
            </span>
          </button>
          <div className={`web-invite-toast-opening-note max-w-[20rem] rounded-2xl border px-5 py-4 shadow-xl backdrop-blur-md transition duration-700 ${champagneOpening ? 'translate-y-5 opacity-0' : 'opacity-100'}`} style={{ background: alpha('#220814', 0.7), borderColor: alpha(theme.secondary_color, 0.46) }}>
            <p className="text-[0.98rem] font-semibold leading-7 text-white">{opening.message}</p>
            <p className="mt-3 text-[0.62rem] font-black uppercase tracking-[0.18em]" style={{ color: theme.secondary_color }}>{withMusic ? 'Music starts after the pop' : 'Birthday toast reveal'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (birthdaySurprise) {
    const content = eventSource(heroSection, eventData);
    const honoreeName = displayNames(content) || displayNames(eventData) || 'Guest of honor';
    const milestone = sectionText(heroSection, 'headline', displayText(content.turning) || displayText(content.age) || '60');
    const eventDate = readableShortDate(content.start_date || eventData.start_date);
    const venue = content.venue || eventData.venues?.[0];
    const venueName = venueLabel(venue);
    return (
      <div
        className="absolute inset-0 z-[70] overflow-hidden text-center"
        style={{
          minHeight: openingHeight,
          color: '#FFF7EA',
          backgroundColor: theme.primary_color,
          backgroundImage: artwork
            ? `linear-gradient(180deg, ${alpha('#14060B', 0.42)} 0%, ${alpha(theme.primary_color, 0.24)} 42%, ${alpha('#14060B', 0.74)} 100%), url(${resolveMediaUrl(artwork)})`
            : `radial-gradient(circle at 20% 16%, ${alpha(theme.secondary_color, 0.28)}, transparent 30%), linear-gradient(160deg, ${theme.primary_color}, #1B0810)`,
          backgroundPosition: layout.background_position,
          backgroundSize: 'cover',
        }}
      >
        <DecorativeEffect effect={effects.hero_effect} intensity={Math.min(0.95, effects.intensity)} speed={effects.speed} accent={theme.secondary_color} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/58" aria-hidden="true" />
        <div className="relative z-[3] flex flex-col items-center justify-between px-7 py-9" style={{ minHeight: openingHeight }}>
          <div className={`rounded-2xl border px-5 py-3 shadow-2xl backdrop-blur-md transition duration-700 ${birthdayOpening ? '-translate-y-3 opacity-0' : 'opacity-100'}`} style={{ background: alpha('#220814', 0.72), borderColor: alpha(theme.secondary_color, 0.36) }}>
            <p className="text-[0.7rem] font-black uppercase tracking-[0.34em]" style={{ color: theme.secondary_color, textShadow: '0 2px 16px rgba(0,0,0,.65)' }}>{opening.eyebrow}</p>
            <h1 className="mt-3 max-w-[19rem] text-[2.72rem] font-normal leading-[0.92] drop-shadow-2xl" style={{ fontFamily: `${theme.heading_font}, serif` }}>{opening.title}</h1>
          </div>
          <div
            className="group relative my-7 flex w-full max-w-[21rem] flex-col items-center"
            style={{ outlineColor: '#FFF7EA', perspective: '1200px', perspectiveOrigin: '50% 38%' }}
          >
            <div
              className={`web-invite-velvet-envelope relative aspect-[1.25/1] w-full overflow-visible rounded-sm border shadow-2xl ${birthdayOpening ? 'translate-y-1 scale-[1.015]' : 'opacity-100 group-hover:-translate-y-1'}`}
              style={{
                borderColor: alpha(theme.secondary_color, 0.64),
                background: `linear-gradient(145deg, ${alpha(theme.primary_color, 0.96)}, #2B0718)`,
                boxShadow: birthdayOpening
                  ? `0 30px 86px ${alpha('#12060B', 0.52)}`
                  : `0 24px 80px ${alpha('#12060B', 0.58)}`,
                transition: 'transform 1180ms cubic-bezier(.18,.82,.2,1), box-shadow 1180ms ease',
                transformStyle: 'preserve-3d',
                willChange: birthdayOpening ? 'transform' : undefined,
              }}
            >
              <div className="web-invite-velvet-envelope-back absolute inset-0 overflow-hidden rounded-sm" style={{ background: `linear-gradient(145deg, ${alpha('#8A2148', 0.88)}, #3A071B)` }} aria-hidden="true" />
              <div
                className={`web-invite-velvet-invitation-card absolute inset-x-8 top-[39%] z-[1] flex h-[56%] flex-col items-center justify-center rounded-sm border px-4 py-5 text-center shadow-xl ${birthdayOpening ? 'web-invite-velvet-card-rising' : ''}`}
                style={{
                  color: theme.primary_color,
                  background: `linear-gradient(180deg, #FFF7EA, #F7E5C8)`,
                  borderColor: alpha(theme.secondary_color, 0.62),
                  boxShadow: birthdayOpening
                    ? `0 20px 42px ${alpha('#12060B', 0.34)}`
                    : `0 8px 22px ${alpha('#12060B', 0.18)}`,
                  transform: birthdayOpening
                    ? 'translate3d(0, -118%, 34px) rotateZ(0deg) scale(1.08)'
                    : 'translate3d(0, 0%, -10px) rotateZ(-1.2deg) scale(.86)',
                  opacity: birthdayOpening ? 1 : 0.98,
                  transition: 'transform 1280ms cubic-bezier(.18,.78,.18,1) 420ms, box-shadow 1180ms ease 420ms, opacity 500ms ease',
                  transformStyle: 'preserve-3d',
                  willChange: birthdayOpening ? 'transform' : undefined,
                }}
              >
                <p className="text-[0.5rem] font-black uppercase tracking-[0.18em]" style={{ color: theme.secondary_color }}>The invitation</p>
                <p className="mt-2 text-[3rem] font-normal leading-[0.78]" style={{ fontFamily: `${theme.heading_font}, serif`, color: theme.primary_color }}>{milestone}</p>
                <p className="mt-2 max-w-[9rem] text-[1.7rem] font-normal leading-[0.9]" style={{ fontFamily: '"Great Vibes", "Playfair Display", cursive', color: theme.primary_color, overflowWrap: 'anywhere' }}>{honoreeName}</p>
                {(eventDate || venueName) && (
                  <p className="mt-3 max-w-[10rem] text-[0.44rem] font-black uppercase leading-4 tracking-[0.14em]" style={{ color: alpha(theme.primary_color, 0.7) }}>
                    {[eventDate, venueName].filter(Boolean).join(' | ')}
                  </p>
                )}
              </div>
              <div
                className={`web-invite-velvet-card-shadow pointer-events-none absolute inset-x-10 top-[44%] z-[2] h-10 rounded-full blur-xl transition duration-700 ${birthdayOpening ? 'opacity-40' : 'opacity-0'}`}
                style={{ background: alpha('#12060B', 0.42), transform: birthdayOpening ? 'translateY(-36px) scaleX(.86)' : 'translateY(22px) scaleX(.72)' }}
                aria-hidden="true"
              />
              <div className="web-invite-velvet-pocket absolute inset-x-0 bottom-0 z-[3] h-[64%] overflow-hidden rounded-b-sm" aria-hidden="true">
                <div className="absolute inset-x-0 bottom-0 h-full" style={{ clipPath: 'polygon(0 100%, 50% 19%, 100% 100%)', background: `linear-gradient(0deg, #2B0718, ${alpha(theme.primary_color, 0.98)})` }} />
                <div className="absolute inset-y-0 left-0 w-[51%]" style={{ clipPath: 'polygon(0 0, 100% 50%, 0 100%)', background: `linear-gradient(135deg, ${alpha('#4A071F', 0.76)}, ${alpha('#2B0718', 0.42)})` }} />
                <div className="absolute inset-y-0 right-0 w-[51%]" style={{ clipPath: 'polygon(100% 0, 0 50%, 100% 100%)', background: `linear-gradient(225deg, ${alpha('#260513', 0.64)}, ${alpha('#5B0F2E', 0.34)})` }} />
                <div className="absolute inset-x-12 top-[31%] h-px bg-[#F5DFA5]/70" />
              </div>
              <div
                className="web-invite-velvet-flap-front absolute inset-x-0 top-0 z-[4] h-[58%] origin-top"
                style={{
                  clipPath: 'polygon(0 0, 50% 72%, 100% 0)',
                  background: `linear-gradient(180deg, ${alpha('#9B2A50', 0.95)}, ${alpha(theme.primary_color, 0.95)})`,
                  boxShadow: birthdayOpening
                    ? `0 -22px 36px ${alpha('#12060B', 0.2)}, inset 0 10px 18px ${alpha('#FFF1C9', 0.12)}`
                    : `0 12px 28px ${alpha('#12060B', 0.2)}`,
                  transform: birthdayOpening ? 'rotateX(-172deg) translateY(-2px) translateZ(2px)' : 'rotateX(0deg) translateZ(0)',
                  transition: 'transform 1380ms cubic-bezier(.16,.74,.18,1), box-shadow 1180ms ease, filter 1180ms ease',
                  transformStyle: 'preserve-3d',
                  backfaceVisibility: 'visible',
                  transformOrigin: '50% 1%',
                  filter: birthdayOpening ? 'brightness(1.05)' : 'brightness(1)',
                  willChange: birthdayOpening ? 'transform' : undefined,
                }}
                aria-hidden="true"
              />
              <div
                className="web-invite-velvet-flap-back absolute inset-x-0 top-0 z-[2] h-[58%] origin-top"
                style={{
                  clipPath: 'polygon(0 0, 50% 72%, 100% 0)',
                  background: `linear-gradient(180deg, ${alpha('#F2C879', 0.96)}, ${alpha('#7A173A', 0.94)})`,
                  transform: birthdayOpening ? 'rotateX(-166deg) translateY(-3px) translateZ(1px)' : 'rotateX(0deg) translateZ(0)',
                  opacity: birthdayOpening ? 0.95 : 0,
                  transition: 'transform 1420ms cubic-bezier(.16,.74,.18,1), opacity 520ms ease 140ms',
                  transformStyle: 'preserve-3d',
                  transformOrigin: '50% 1%',
                }}
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={enter}
                disabled={birthdayOpening}
                className={`web-invite-velvet-stamp-button absolute left-1/2 top-1/2 z-[4] h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 ${birthdayOpening ? 'pointer-events-none scale-125 opacity-0' : 'opacity-100 hover:scale-105'}`}
                style={{
                  outlineColor: '#FFF7EA',
                  transition: 'transform 760ms cubic-bezier(.2,.72,.18,1), opacity 620ms ease 120ms, filter 620ms ease',
                  filter: birthdayOpening ? 'blur(.4px)' : 'none',
                }}
                aria-label={opening.action_label}
              >
                <BirthdaySealMark color={theme.primary_color} accent={theme.secondary_color} className="h-full w-full shadow-2xl" />
              </button>
              <div className={`pointer-events-none absolute inset-x-12 top-[36%] z-[5] h-px bg-[#F5DFA5]/70 transition duration-700 ${birthdayOpening ? 'scale-x-125 opacity-0' : 'opacity-70'}`} aria-hidden="true" />
            </div>
            <div className={`web-invite-velvet-entry-cue mt-6 flex flex-col items-center transition duration-700 ${birthdayOpening ? 'translate-y-3 opacity-0' : 'opacity-100'}`}>
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-white/85">A birthday surprise is waiting</p>
              <button
                type="button"
                onClick={enter}
                disabled={birthdayOpening}
                className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border px-5 py-2.5 text-[0.68rem] font-black uppercase tracking-[0.12em] shadow-xl backdrop-blur-sm transition hover:-translate-y-0.5 hover:brightness-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
                style={{ color: theme.primary_color, background: alpha('#FFF7EA', 0.97), borderColor: alpha(theme.secondary_color, 0.86), outlineColor: '#FFF7EA' }}
                aria-label="Click here to open the birthday surprise"
              >
                <Gift className="h-4 w-4" weight="fill" />
                Click here to open the surprise
                <Sparkle className="h-3.5 w-3.5" weight="fill" />
              </button>
            </div>
          </div>
          <div className={`max-w-[20rem] rounded-2xl border px-5 py-4 shadow-xl backdrop-blur-md transition duration-700 ${birthdayOpening ? 'translate-y-3 opacity-0' : 'opacity-100'}`} style={{ background: alpha('#220814', 0.76), borderColor: alpha(theme.secondary_color, 0.5) }}>
            <p className="text-[0.98rem] font-semibold leading-7 text-white">{opening.message}</p>
            <p className="mt-3 text-[0.62rem] font-black uppercase tracking-[0.18em]" style={{ color: theme.secondary_color }}>{withMusic ? 'Opens with music' : 'Keep it secret'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (kashiRiverfront) {
    const riverArtwork = layout.background_image_url || artwork;
    const boatArtwork = layout.decorative_image_url || '/assets/web-invites/kashi-riverlight/kashi-boat-white-v2.png';
    const portalArtwork = layout.show_frame
      ? layout.frame_image_url || '/assets/web-invites/kashi-riverlight/kashi-banarasi-portal-v1.png'
      : null;
    return (
      <div
        className={`absolute inset-0 z-[70] overflow-hidden text-center ${kashiCrossing ? 'web-invite-kashi-opening-crossing' : ''} ${kashiRevealing ? 'web-invite-kashi-opening-revealing' : ''}`}
        style={{
          minHeight: openingHeight,
          color: theme.text_color,
        }}
      >
        <div
          className="web-invite-kashi-opening-scene pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundColor: theme.background_color,
            backgroundImage: riverArtwork
              ? `linear-gradient(180deg, rgba(243,231,207,.08) 0%, rgba(243,231,207,.02) 48%, rgba(18,59,65,.22) 100%), url(${resolveMediaUrl(riverArtwork)})`
              : `linear-gradient(180deg, ${theme.background_color}, ${theme.primary_color})`,
            backgroundPosition: layout.background_position,
            backgroundSize: 'cover',
          }}
          aria-hidden="true"
        />
        <div className={`web-invite-kashi-mist pointer-events-none absolute inset-0 z-[1] ${kashiCrossing ? 'web-invite-kashi-mist-clearing' : ''}`} aria-hidden="true" />
        {portalArtwork && (
          <div
            className="web-invite-kashi-portal pointer-events-none absolute inset-1 z-[5] border-solid"
            style={{
              borderImageSource: `url(${resolveMediaUrl(portalArtwork)})`,
              borderImageSlice: '25% 12% 11% 12%',
              borderImageWidth: 1,
              borderImageRepeat: 'stretch',
              borderTopWidth: 'clamp(96px, calc(var(--invite-scrollport-height, 100svh) * .18), 138px)',
              borderRightWidth: 'clamp(36px, 10vw, 50px)',
              borderBottomWidth: 'clamp(48px, calc(var(--invite-scrollport-height, 100svh) * .1), 78px)',
              borderLeftWidth: 'clamp(36px, 10vw, 50px)',
              mixBlendMode: 'multiply',
            }}
            aria-hidden="true"
          />
        )}
        <div
          className={`relative z-[3] flex flex-col items-center ${portalArtwork ? 'px-10' : 'px-6 pb-6 pt-8'}`}
          style={{
            minHeight: openingHeight,
            ...(portalArtwork ? {
              paddingTop: 'clamp(5rem, calc(var(--invite-scrollport-height, 100svh) * .17), 8rem)',
              paddingBottom: 'clamp(4.25rem, calc(var(--invite-scrollport-height, 100svh) * .14), 8rem)',
            } : {}),
          }}
        >
          <div className={`web-invite-kashi-opening-copy ${kashiCrossing ? 'web-invite-kashi-opening-copy-leaving' : ''}`}>
            <p className="text-[0.66rem] font-semibold uppercase tracking-[0.28em]" style={{ color: theme.primary_color }}>{opening.eyebrow}</p>
            <h1 className="mt-3 max-w-[20rem] leading-[0.96]" style={{ fontSize: 'clamp(1.85rem, calc(var(--invite-scrollport-height, 100svh) * .06), 2.65rem)', fontFamily: `${theme.heading_font}, serif`, textShadow: '0 2px 18px rgba(243,231,207,.96)' }}>{opening.title}</h1>
          </div>
          <div className={`${portalArtwork ? 'min-h-0' : 'min-h-20'} flex-1`} />
          <button
            type="button"
            onClick={enter}
            disabled={kashiCrossing}
            className="group relative flex w-full flex-col items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
            style={{ outlineColor: theme.primary_color }}
            aria-label={opening.action_label}
          >
            <div className="relative w-full overflow-visible" style={{ height: 'clamp(96px, calc(var(--invite-scrollport-height, 100svh) * .2), 128px)' }} aria-hidden="true">
              <div className={`web-invite-kashi-voyage absolute bottom-0 left-1/2 w-[88%] max-w-[360px] ${kashiCrossing ? 'web-invite-kashi-voyage-crossing' : ''}`}>
                <span className={`web-invite-kashi-wake absolute bottom-[16%] left-[6%] -z-[1] h-8 w-[68%] rounded-[50%] border-t ${kashiCrossing ? 'web-invite-kashi-wake-active' : ''}`} style={{ borderColor: alpha(theme.background_color, 0.72) }} />
                <img src={resolveMediaUrl(boatArtwork)} alt="" className="web-invite-kashi-boat block w-full" style={{ mixBlendMode: 'multiply' }} />
              </div>
            </div>
            <span className={`web-invite-kashi-entry-action mt-1 inline-flex min-h-11 items-center gap-2 rounded-full border px-6 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.13em] shadow-xl backdrop-blur-sm ${kashiCrossing ? 'web-invite-kashi-entry-action-leaving' : ''}`} style={{ color: '#FFF9EA', background: alpha(theme.primary_color, 0.97), borderColor: alpha(theme.secondary_color, 0.9) }}>
              {opening.action_label}<ArrowRight className="h-4 w-4" />
            </span>
          </button>
          <div className={`web-invite-kashi-entry-note mt-3 max-w-[20rem] rounded-2xl border px-5 py-2 shadow-lg backdrop-blur-md ${kashiCrossing ? 'web-invite-kashi-entry-note-leaving' : ''}`} style={{ color: '#302621', background: 'rgba(255,249,234,.94)', borderColor: 'rgba(169,86,56,.38)' }}>
            <p className="text-[0.88rem] leading-6">{opening.message}</p>
            <p className="mt-2 text-[0.58rem] font-semibold uppercase tracking-[0.13em] opacity-70">{withMusic ? 'The crossing opens with music' : 'Cross into the invitation'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (templeBell) {
    const courtyardArtwork = layout.background_image_url || artwork;
    return (
      <div
        className="absolute inset-0 z-[70] overflow-hidden text-center text-white"
        style={{
          minHeight: openingHeight,
          backgroundColor: theme.text_color,
          backgroundImage: courtyardArtwork
            ? `linear-gradient(180deg, rgba(25,18,12,.72) 0%, rgba(35,20,13,.18) 42%, rgba(24,16,11,.82) 100%), url(${resolveMediaUrl(courtyardArtwork)})`
            : `linear-gradient(180deg, ${theme.text_color}, ${theme.primary_color})`,
          backgroundPosition: layout.background_position,
          backgroundSize: 'cover',
        }}
      >
        <DecorativeEffect effect={effects.hero_effect} intensity={Math.min(0.7, effects.intensity)} speed={effects.speed} accent={theme.secondary_color} />
        <div className="relative z-[3] flex flex-col items-center px-6 pb-9 pt-10" style={{ minHeight: openingHeight }}>
          <p className="text-[0.66rem] font-semibold uppercase tracking-[0.28em] text-[#F2D998] drop-shadow-lg">{opening.eyebrow}</p>
          <h1 className="mt-3 max-w-[20rem] text-[2.8rem] leading-[0.94] drop-shadow-2xl" style={{ fontFamily: `${theme.heading_font}, serif` }}>{opening.title}</h1>
          <button
            type="button"
            onClick={enter}
            disabled={templeRinging}
            className="group relative mt-2 flex min-h-0 flex-1 flex-col items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
            style={{ outlineColor: '#FFF6DD' }}
            aria-label={opening.action_label}
          >
            {templeRinging && <><span className="web-invite-temple-ring absolute top-[40%] h-28 w-28 rounded-full border border-[#F2D998]/70" /><span className="web-invite-temple-ring absolute top-[40%] h-28 w-28 rounded-full border border-[#F2D998]/45 [animation-delay:180ms]" /></>}
            {layout.decorative_image_url ? (
              <img
                src={resolveMediaUrl(layout.decorative_image_url)}
                alt=""
                aria-hidden="true"
                className={`web-invite-temple-bell w-auto origin-top object-contain drop-shadow-2xl transition-transform group-hover:scale-[1.02] ${templeRinging ? 'web-invite-temple-bell-ringing' : ''}`}
                style={{ height: 'clamp(180px, calc(var(--invite-scrollport-height, 100svh) * .4), 360px)' }}
              />
            ) : <Bell className={`h-40 w-40 text-[#D4A949] ${templeRinging ? 'web-invite-temple-bell-ringing' : ''}`} weight="duotone" />}
            <span className="-mt-7 inline-flex items-center gap-2 border border-[#E4C474] bg-[#4A1712]/95 px-5 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.13em] text-[#FFF6DD] shadow-2xl backdrop-blur-sm">
              <Bell className="h-4 w-4" weight="fill" />{opening.action_label}
            </span>
          </button>
          <p className="max-w-[18rem] text-sm leading-6 text-white/85 drop-shadow-lg">{opening.message}</p>
          <p className="mt-3 text-[0.62rem] uppercase tracking-[0.14em] text-white/65">{withMusic ? 'The bell opens with music' : 'Tap the bell to enter'}</p>
        </div>
      </div>
    );
  }

  if (royalTapestry) {
    const curtainArtwork = layout.background_image_url || artwork;
    const curtainImage = curtainArtwork ? `url(${resolveMediaUrl(curtainArtwork)})` : null;
    const curtainBase = {
      backgroundColor: theme.primary_color,
      backgroundImage: curtainImage
        ? `linear-gradient(90deg, rgba(5,8,24,.3), transparent 28%, rgba(255,220,146,.08) 52%, rgba(4,7,20,.42)), ${curtainImage}`
        : `linear-gradient(145deg, ${theme.primary_color}, ${theme.accent_color})`,
      backgroundSize: 'cover',
      transition: 'transform 900ms cubic-bezier(.72,0,.2,1)',
    };
    return (
      <div className="absolute inset-0 z-[70] overflow-hidden" style={{ background: '#0D112A', minHeight: openingHeight }}>
        <div
          className="web-invite-curtain absolute inset-y-0 left-0 z-[1] w-[52%] border-r"
          style={{ ...curtainBase, backgroundPosition: 'left center', borderColor: alpha(theme.secondary_color, 0.42), transform: royalOpening ? 'translateX(-104%)' : 'translateX(0)' }}
          aria-hidden="true"
        />
        <div
          className="web-invite-curtain absolute inset-y-0 right-0 z-[1] w-[52%] border-l"
          style={{ ...curtainBase, backgroundPosition: 'right center', borderColor: alpha(theme.secondary_color, 0.42), transform: royalOpening ? 'translateX(104%)' : 'translateX(0)' }}
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-y-0 left-1/2 z-[2] w-8 -translate-x-1/2 bg-gradient-to-r from-transparent via-black/50 to-transparent" aria-hidden="true" />
        <div
          className="web-invite-curtain-copy relative z-[3] flex flex-col items-center justify-center px-8 py-14 text-center"
          style={{ minHeight: openingHeight, color: '#FFF8E8', opacity: royalOpening ? 0 : 1, transform: royalOpening ? 'scale(.94)' : 'scale(1)', transition: 'opacity 420ms ease, transform 600ms ease' }}
        >
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.28em]" style={{ color: '#F1D38E', textShadow: '0 2px 14px rgba(0,0,0,.9)' }}>{opening.eyebrow}</p>
          <h1 className="mt-5 max-w-[18rem] text-[2.65rem] leading-[0.98] drop-shadow-2xl" style={{ fontFamily: `${theme.heading_font}, serif` }}>{opening.title}</h1>
          <button
            type="button"
            onClick={enter}
            disabled={royalOpening}
            className="mt-8 grid h-36 w-36 place-items-center rounded-full border-[6px] border-double px-5 text-center text-[0.78rem] font-semibold uppercase leading-5 tracking-[0.12em] shadow-2xl transition-transform hover:scale-[1.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
            style={{ color: '#FFF8E8', background: 'radial-gradient(circle at 38% 32%, #A43A4B, #641827 68%, #321018)', borderColor: '#E6C675', boxShadow: '0 0 0 5px rgba(21,20,48,.78), 0 20px 55px rgba(0,0,0,.62)', outlineColor: '#FFF8E8' }}
          >
            <span>{opening.action_label}</span>
          </button>
          <p className="mt-6 max-w-[17rem] text-sm leading-6 text-white/85" style={{ textShadow: '0 2px 14px rgba(0,0,0,.95)' }}>{opening.message}</p>
          <p className="mt-4 text-[0.64rem] uppercase tracking-[0.15em] text-white/75">{withMusic ? 'Opens with music' : 'Open the invitation'}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 z-[70] flex items-center justify-center overflow-hidden px-8 py-16 text-center text-white"
      style={{
        minHeight: openingHeight,
        backgroundImage: artwork
          ? `linear-gradient(145deg, ${alpha(theme.primary_color, gardenFrame ? 0.42 : 0.82)}, ${alpha(theme.text_color, gardenFrame ? 0.58 : 0.72)}), url(${resolveMediaUrl(artwork)})`
          : `radial-gradient(circle at 70% 20%, ${alpha(theme.secondary_color, 0.52)}, transparent 32%), linear-gradient(145deg, ${theme.primary_color}, ${theme.text_color})`,
        backgroundPosition: layout.background_position,
        backgroundSize: 'cover',
      }}
    >
      <DecorativeEffect effect={effects.hero_effect} intensity={Math.min(2, effects.intensity)} speed={effects.speed} accent={theme.secondary_color} />
      {layout.show_frame && layout.frame_image_url && (
        <img
          src={resolveMediaUrl(layout.frame_image_url)}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 z-[2] h-auto w-full max-w-[408px] -translate-x-1/2 -translate-y-1/2 object-contain"
        />
      )}
      <div className={`relative z-[3] mx-auto ${gardenFrame ? 'max-w-[17.5rem]' : 'max-w-sm'}`}>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">{opening.eyebrow}</p>
        <h1 className="mt-5 text-5xl leading-[0.95] drop-shadow-lg" style={{ fontFamily: `${theme.heading_font}, serif` }}>{opening.title}</h1>
        <p className="mx-auto mt-6 max-w-xs text-base leading-8 text-white/85">{opening.message}</p>
        <button
          type="button"
          onClick={enter}
          className="mx-auto mt-9 inline-flex items-center gap-2 rounded-full border px-7 py-3.5 text-base font-semibold shadow-xl transition-transform hover:scale-[1.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
          style={{ color: '#241B18', backgroundColor: '#FFFDF8', borderColor: alpha(theme.secondary_color, 0.8), boxShadow: '0 16px 38px rgba(0,0,0,.34)', outlineColor: '#FFFFFF' }}
        >
          {opening.action_label}<ArrowRight className="h-4 w-4" />
        </button>
        <p className="mt-4 text-[0.7rem] uppercase tracking-[0.16em] text-white/65">{withMusic ? 'Tap to open with music' : 'Tap to open the invitation'}</p>
      </div>
    </div>
  );
}

function BotanicalSpray({ url, index }) {
  if (!url) return null;
  const right = index % 2 === 0;
  return (
    <div className={`web-invite-scroll-drift pointer-events-none absolute z-[1] w-48 max-w-[48%] opacity-55 ${right ? '-right-14 -top-8' : '-bottom-10 -left-16'}`} aria-hidden="true">
      <img
        src={resolveMediaUrl(url)}
        alt=""
        className="w-full"
        style={{ transform: right ? 'scaleX(-1) rotate(-5deg)' : 'rotate(4deg)' }}
      />
    </div>
  );
}

function HeroEcho({ url, index, theme }) {
  if (!url) return null;
  const right = index % 2 === 1;
  return (
    <div
      className={`web-invite-scroll-drift-reverse pointer-events-none absolute top-10 z-[1] h-28 w-20 overflow-hidden opacity-25 ${right ? '-right-3' : '-left-3'}`}
      style={{
        borderRadius: '999px 999px 22px 22px',
        border: `1px solid ${alpha(theme.accent_color, 0.35)}`,
        WebkitMaskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
        maskImage: 'linear-gradient(to bottom, black 40%, transparent 100%)',
      }}
      aria-hidden="true"
    >
      <img
        src={resolveMediaUrl(url)}
        alt=""
        className="h-full w-full object-cover"
        style={{ objectPosition: right ? '70% center' : '30% center' }}
      />
    </div>
  );
}

function SectionPetalBurst({ active, color }) {
  if (!active) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-[4] h-36 overflow-hidden" aria-hidden="true">
      {Array.from({ length: 7 }, (_, index) => (
        <span
          key={index}
          className="web-invite-section-petal absolute -top-6"
          style={{
            left: `${10 + ((index * 17) % 82)}%`,
            color,
            animationDelay: `${index * 0.11}s`,
            animationDuration: `${1.65 + (index % 3) * 0.28}s`,
            fontSize: `${11 + (index % 3) * 3}px`,
          }}
        >
          {index % 2 ? '❀' : '❦'}
        </span>
      ))}
    </div>
  );
}

function RevealSection({ children, effect, petalBurst, petalColor, sectionId, sectionType, onEdit, className = '', style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(effect === 'none');

  useEffect(() => {
    if (effect === 'none') {
      setVisible(true);
      return undefined;
    }
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, { threshold: 0.12, rootMargin: '-5% 0px -8% 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [effect]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    let frame = null;
    const updateMotion = () => {
      frame = null;
      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const progress = Math.min(1, Math.max(0, (viewportHeight - rect.top) / Math.max(1, viewportHeight + rect.height)));
      const centered = progress - 0.5;
      node.style.setProperty('--invite-scroll-shift', `${centered * 54}px`);
      node.style.setProperty('--invite-scroll-shift-reverse', `${centered * -42}px`);
      node.style.setProperty('--invite-scroll-rotate', `${centered * 8}deg`);
      node.style.setProperty('--invite-scroll-rotate-reverse', `${centered * -8}deg`);
      node.style.setProperty('--invite-scroll-scale', `${0.97 + progress * 0.04}`);
    };
    const requestMotionUpdate = () => {
      if (frame == null) frame = window.requestAnimationFrame(updateMotion);
    };
    updateMotion();
    window.addEventListener('scroll', requestMotionUpdate, true);
    window.addEventListener('resize', requestMotionUpdate);
    return () => {
      window.removeEventListener('scroll', requestMotionUpdate, true);
      window.removeEventListener('resize', requestMotionUpdate);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-invite-section-id={sectionId}
      className={`relative isolate overflow-hidden ${visible ? `web-invite-visible web-invite-reveal-${effect}` : 'web-invite-awaiting'} ${className}`}
      style={style}
    >
      <SectionPetalBurst active={visible && petalBurst} color={petalColor} />
      {onEdit && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(sectionId);
          }}
          className={`absolute top-3 z-[70] inline-flex min-h-10 items-center gap-1.5 rounded-full border border-white/70 bg-white/90 px-3 text-xs font-semibold text-slate-800 shadow-lg backdrop-blur transition hover:bg-white ${sectionType === 'hero' ? 'right-16' : 'right-3'}`}
          aria-label={`Edit ${sectionId} section`}
        >
          <PencilSimple className="h-4 w-4" />
          Edit
        </button>
      )}
      {children}
    </div>
  );
}

function SectionHeading({ eyebrow, title, body, align = 'center', theme }) {
  return (
    <div className={align === 'left' ? 'text-left' : 'mx-auto max-w-2xl text-center'}>
      {eyebrow && (
        <p className="text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: theme.accent_color }}>
          {eyebrow}
        </p>
      )}
      <h2 className="mt-2 text-[2.4rem] font-normal leading-[1.02]" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, cursive` }}>
        {title}
      </h2>
      {body && <p className="mt-2 text-[0.96rem] leading-6 opacity-75">{body}</p>}
    </div>
  );
}

function Surface({ children, theme, className = '', style = {} }) {
  const backgrounds = {
    flat: theme.background_color,
    soft: alpha(theme.primary_color, 0.065),
    glass: 'rgba(255,255,255,0.58)',
    paper: '#FFFFFF',
  };
  return (
    <div
      className={`border ${theme.surface_style === 'glass' ? 'backdrop-blur-xl' : ''} ${className}`}
      style={{
        borderColor: alpha(theme.primary_color, 0.14),
        background: backgrounds[theme.surface_style] || backgrounds.soft,
        borderRadius: RADIUS[theme.corner_style],
        boxShadow: theme.surface_style === 'flat' ? 'none' : `0 16px 50px ${alpha(theme.primary_color, 0.10)}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function BotanicalDivider({ theme, sectionType }) {
  if (sectionType !== 'footer') return null;
  return <div className="relative z-[3] -mb-px h-16" style={{ background: `linear-gradient(to bottom, transparent, ${theme.primary_color})` }} aria-hidden="true" />;
}

function TempleKolamMark({ color, className = '' }) {
  return (
    <svg viewBox="0 0 120 120" className={className} style={{ color }} fill="none" aria-hidden="true">
      <circle cx="60" cy="60" r="4" fill="currentColor" />
      <circle cx="60" cy="60" r="30" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 5" />
      <path d="M60 12C49 24 49 36 60 48C71 36 71 24 60 12ZM60 108C49 96 49 84 60 72C71 84 71 96 60 108ZM12 60C24 49 36 49 48 60C36 71 24 71 12 60ZM108 60C96 49 84 49 72 60C84 71 96 71 108 60Z" stroke="currentColor" strokeWidth="2" />
      <path d="M27 27C43 26 52 35 51 51C35 52 26 43 27 27ZM93 27C94 43 85 52 69 51C68 35 77 26 93 27ZM27 93C26 77 35 68 51 69C52 85 43 94 27 93ZM93 93C77 94 68 85 69 69C85 68 94 77 93 93Z" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function TempleStepDivider({ theme }) {
  return (
    <div className="relative z-[3] -my-2 h-12 overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-x-0 top-1/2 h-9 -translate-y-1/2"
        style={{
          background: `linear-gradient(90deg, ${alpha(theme.primary_color, 0.12)}, ${alpha(theme.secondary_color, 0.34)}, ${alpha(theme.primary_color, 0.12)})`,
          clipPath: 'polygon(0 38%, 16% 38%, 16% 27%, 32% 27%, 32% 14%, 44% 14%, 44% 0, 56% 0, 56% 14%, 68% 14%, 68% 27%, 84% 27%, 84% 38%, 100% 38%, 100% 62%, 84% 62%, 84% 73%, 68% 73%, 68% 86%, 56% 86%, 56% 100%, 44% 100%, 44% 86%, 32% 86%, 32% 73%, 16% 73%, 16% 62%, 0 62%)',
        }}
      />
    </div>
  );
}

function WovenBandDivider({ theme, layout }) {
  return (
    <div
      className="relative z-[3] -my-4 h-20 bg-cover bg-center"
      style={{
        backgroundColor: theme.primary_color,
         backgroundImage: layout.background_image_url
           ? `linear-gradient(90deg, ${alpha(theme.primary_color, 0.28)}, transparent, ${alpha(theme.accent_color, 0.24)}), url(${resolveMediaUrl(layout.background_image_url)})`
           : `repeating-linear-gradient(90deg, ${theme.primary_color} 0 12px, ${theme.secondary_color} 12px 15px, ${theme.accent_color} 15px 27px)`,
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
      }}
      aria-hidden="true"
    />
  );
}

function WaterlineDivider({ theme, sectionType }) {
  const closing = sectionType === 'footer';
  return (
    <div className="relative z-[3] -my-3 h-14 overflow-hidden" aria-hidden="true">
      <svg viewBox="0 0 430 56" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <path d="M-12 18C53 1 102 37 167 20C235 2 286 37 442 12" stroke={alpha(theme.primary_color, closing ? 0.48 : 0.28)} strokeWidth="1.5" fill="none" />
        <path d="M-20 31C56 14 112 48 188 30C267 12 328 43 450 24" stroke={alpha(theme.secondary_color, closing ? 0.72 : 0.46)} strokeWidth="2" fill="none" />
        <path d="M-8 44C65 31 121 57 194 43C264 29 326 52 438 36" stroke={alpha(theme.primary_color, closing ? 0.36 : 0.2)} strokeWidth="1" fill="none" />
      </svg>
    </div>
  );
}

function BubbleStreamDivider({ theme }) {
  return (
    <div className="relative z-[3] -my-4 h-20 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-x-0 top-1/2 h-px" style={{ background: `linear-gradient(90deg, transparent, ${alpha(theme.secondary_color, 0.66)}, transparent)` }} />
      {Array.from({ length: 9 }, (_, index) => (
        <span
          key={index}
          className="web-invite-toast-divider-bubble absolute rounded-full border"
          style={{
            left: `${8 + index * 10}%`,
            bottom: `${16 + (index % 3) * 12}px`,
            width: `${7 + (index % 4) * 4}px`,
            height: `${7 + (index % 4) * 4}px`,
            borderColor: alpha(theme.secondary_color, 0.5),
            background: alpha('#FFFFFF', 0.18),
            animationDelay: `${index * 90}ms`,
          }}
        />
      ))}
    </div>
  );
}

function paperPanelStyle(theme, index) {
  const alternate = index % 2 === 0;
  return {
    background: alternate ? alpha(theme.secondary_color, 0.1) : alpha(theme.accent_color, 0.07),
    clipPath: alternate
      ? 'polygon(0 3%, 9% 1%, 19% 3.5%, 30% 1.4%, 41% 3.2%, 53% 1%, 65% 3.6%, 77% 1.3%, 89% 3.4%, 100% 1.5%, 100% 97%, 90% 99%, 79% 96.7%, 67% 99%, 55% 96.5%, 43% 99%, 31% 96.8%, 19% 99%, 8% 96.7%, 0 98.5%)'
      : 'polygon(0 1.5%, 10% 3.4%, 22% 1.2%, 34% 3.6%, 47% 1%, 59% 3.3%, 72% 1.4%, 84% 3.5%, 94% 1.2%, 100% 3%, 100% 98.5%, 91% 96.6%, 80% 99%, 68% 96.5%, 56% 99%, 44% 96.8%, 32% 99%, 20% 96.4%, 9% 99%, 0 97%)',
    marginTop: '-8px',
    marginBottom: '-8px',
    paddingTop: '8px',
    paddingBottom: '8px',
  };
}

function HeroSection({ section, eventData, theme, effects, layout, splitPanel = false }) {
  const content = eventSource(section, eventData);
  const names = sectionText(section, 'headline', displayNames(content));
  const eventName = sectionText(section, 'eyebrow', content.event_name || content.name || 'You are invited');
  const body = sectionText(section, 'body', 'Together with our families, we would love for you to join the celebration.');
  const heroImage = heroArtwork(section, eventData, layout);
  const startDate = content.start_date || (usesEventContent(section) ? eventData.start_date : null);
  const venue = content.venue || (usesEventContent(section) ? eventData.venues?.[0] : null);
  const venueName = venueLabel(venue);
  const birthdayMilestone = section.variant === 'birthday_milestone';
  const birthdayChampagne = section.variant === 'birthday_champagne_toast';
  const birthdayBalloonDinner = section.variant === 'birthday_balloon_dinner';
  const cinematic = section.variant === 'cinematic';
  const editorial = section.variant === 'editorial';
  const gardenFrame = section.variant === 'garden_frame';
  const templeCourtyard = section.variant === 'temple_courtyard' || section.variant === 'temple_gateway';
  const royalTapestry = section.variant === 'royal_tapestry';
  const kashiRiverfront = section.variant === 'kashi_riverfront';
  const overlayStrength = Math.min(1, Math.max(0, layout.overlay_strength ?? 0.72));
  const mobileHeight = {
    compact: 'min(520px, var(--invite-scrollport-height, 100svh))',
    tall: 'min(680px, var(--invite-scrollport-height, 100svh))',
    full: 'var(--invite-scrollport-height, 100svh)',
  }[layout.mobile_hero_height] || 'min(680px, var(--invite-scrollport-height, 100svh))';
  const heroColor = gardenFrame ? theme.primary_color : '#FFFFFF';

  if (birthdayBalloonDinner) {
    const honoreeName = sectionText(section, 'name', displayNames(content));
    const milestone = sectionText(section, 'milestone', displayText(content.turning) || displayText(content.age) || '30');
    const title = sectionText(section, 'headline', 'Birthday Dinner');
    const dinnerDate = sectionText(section, 'date_label', readableInviteDate(startDate) || 'Date to be announced');
    const timeLabel = sectionText(section, 'time_label', content.start_time || eventData.start_time || 'Time to be announced');
    const locationLabel = venueName || sectionText(section, 'location', 'Venue to be announced');
    const addressLabel = venueFullAddress(venue) || sectionText(section, 'address', '');
    const attireLabel = sectionText(section, 'attire', 'Chic black and white attire');
    const rsvpText = sectionText(section, 'body', 'RSVP details will be shared soon.');
    const detailCards = [
      { title: 'Date & Time', body: [dinnerDate, timeLabel].filter(Boolean).join('\n'), Icon: CalendarBlank },
      { title: 'Location', body: [locationLabel, addressLabel].filter(Boolean).join('\n'), Icon: MapPin },
      { title: 'Attire', body: attireLabel, Icon: TShirt },
    ];
    return (
      <section
        id="invite-hero"
        className="web-invite-balloon-details relative isolate flex items-center justify-center overflow-hidden px-5 py-9 text-center"
        style={{
          minHeight: mobileHeight,
          color: '#FFF7EA',
          backgroundColor: theme.primary_color,
          backgroundImage: heroImage
            ? `linear-gradient(180deg, ${alpha('#0A1421', 0.2)}, ${alpha('#0A1421', 0.28)} 46%, ${alpha('#0A1421', 0.58)}), url(${resolveMediaUrl(heroImage)})`
            : `linear-gradient(180deg, #506173, #172332)`,
          backgroundPosition: layout.background_position || 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/8 via-transparent to-black/42" aria-hidden="true" />
        <div className="web-invite-balloon-floating-copy pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
          {Array.from({ length: 8 }, (_, index) => (
            <span
              key={index}
              className="web-invite-balloon-orb absolute rounded-full border"
              style={{
                left: `${8 + ((index * 19) % 84)}%`,
                top: `${8 + ((index * 13) % 66)}%`,
                width: `${22 + (index % 4) * 8}px`,
                height: `${28 + (index % 4) * 10}px`,
                borderColor: alpha('#FFF7EA', 0.32),
                background: alpha(index % 2 ? theme.secondary_color : '#FFFFFF', 0.13),
                animationDelay: `${index * 180}ms`,
              }}
            />
          ))}
        </div>
        <div className="relative z-[3] mx-auto w-full max-w-sm">
          <div className="web-invite-balloon-details-heading">
            <p className="text-[2.55rem] font-normal leading-none" style={{ fontFamily: '"Great Vibes", "Playfair Display", cursive' }}>The</p>
            <h1 className="-mt-1 text-[3.2rem] font-black uppercase leading-none tracking-[0.08em]" style={{ fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>Details</h1>
          </div>
          <div className="mt-7 space-y-4">
            {detailCards.map(({ title: cardTitle, body: cardBody, Icon }) => (
              <div key={cardTitle} className="grid grid-cols-[4.5rem_1fr] items-center gap-3 text-left">
                <div className="grid h-[4.5rem] w-[4.5rem] place-items-center rounded-full border shadow-xl backdrop-blur-md" style={{ background: alpha('#F5F0EA', 0.42), borderColor: alpha('#FFFFFF', 0.36) }}>
                  <Icon className="h-8 w-8 text-white" weight="duotone" />
                </div>
                <div className="rounded-2xl border px-4 py-3 shadow-xl backdrop-blur-md" style={{ background: alpha('#F5F0EA', 0.38), borderColor: alpha('#FFFFFF', 0.32), color: '#FFF7EA' }}>
                  <p className="text-[1.15rem] font-black uppercase leading-none tracking-[0.08em]" style={{ fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>{cardTitle}</p>
                  <p className="mt-2 whitespace-pre-line text-[0.78rem] font-black uppercase leading-5 tracking-[0.12em]">{cardBody}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mx-auto mt-8 rounded-2xl border px-5 py-4 shadow-xl backdrop-blur-md" style={{ background: alpha('#0A1421', 0.34), borderColor: alpha(theme.secondary_color, 0.42) }}>
            <BalloonDinnerTitle eyebrow="To celebrate" title={title} honoreeName={honoreeName} milestone={milestone} theme={theme} compact />
            <p className="mt-5 text-[0.72rem] font-black uppercase leading-5 tracking-[0.16em]" style={{ color: '#FFF7EA' }}>{rsvpText}</p>
          </div>
        </div>
      </section>
    );
  }

  if (birthdayChampagne) {
    const honoreeName = sectionText(section, 'name', displayNames(content));
    const milestone = sectionText(section, 'milestone', displayText(content.turning) || displayText(content.age) || '50');
    const milestoneSuffix = ordinalSuffix(milestone);
    const bubbleRail = Array.from({ length: 12 }, (_, index) => ({
      id: index,
      left: `${8 + ((index * 23) % 84)}%`,
      bottom: `${8 + index * 7}%`,
      size: `${6 + (index % 4) * 4}px`,
    }));
    return (
      <section
        id="invite-hero"
        className="web-invite-toast-hero relative isolate flex items-center justify-center overflow-hidden px-6 py-10 text-center"
        style={{
          minHeight: mobileHeight,
          color: '#FFF7EA',
          backgroundColor: theme.primary_color,
          backgroundImage: heroImage
            ? `linear-gradient(180deg, ${alpha('#1B0711', 0.2)} 0%, ${alpha(theme.primary_color, 0.08)} 32%, ${alpha('#1B0711', 0.66)} 100%), url(${resolveMediaUrl(heroImage)})`
            : `radial-gradient(circle at 24% 18%, ${alpha(theme.secondary_color, 0.36)}, transparent 30%), linear-gradient(160deg, ${theme.primary_color}, #2A0815)`,
          backgroundPosition: effects.parallax ? 'center calc(50% + var(--invite-scroll-shift, 0px))' : layout.background_position || 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <DecorativeEffect effect={effects.hero_effect} intensity={effects.intensity} speed={effects.speed} accent={theme.secondary_color} />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/5 via-transparent to-black/36" aria-hidden="true" />
        <div className="absolute inset-x-5 top-5 z-[1] h-[calc(100%-2.5rem)] rounded-[2rem] border border-white/30 shadow-[inset_0_0_0_1px_rgba(215,165,79,0.28)]" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 z-[2]" aria-hidden="true">
          {bubbleRail.map((bubble) => (
            <span
              key={bubble.id}
              className="web-invite-toast-hero-bubble absolute rounded-full border"
              style={{
                left: bubble.left,
                bottom: bubble.bottom,
                width: bubble.size,
                height: bubble.size,
                borderColor: alpha(theme.secondary_color, 0.52),
                background: alpha('#FFF7EA', 0.14),
                animationDelay: `${bubble.id * 140}ms`,
              }}
            />
          ))}
        </div>
        <div className="web-invite-toast-hero-label relative z-[3] mx-auto grid w-full max-w-sm grid-rows-[auto_1fr_auto]">
          <p className="mx-auto max-w-[16rem] rounded-full border px-4 py-2 text-[0.68rem] font-black uppercase leading-5 tracking-[0.16em] backdrop-blur-md" style={{ color: '#F8DDE5', background: alpha('#2A0714', 0.36), borderColor: alpha(theme.secondary_color, 0.42), textShadow: '0 2px 16px rgba(0,0,0,.74)' }}>{eventName}</p>
          <div className="mx-auto mt-7 w-full max-w-[18.5rem] overflow-hidden rounded-[2.25rem] border shadow-2xl backdrop-blur-sm" style={{ background: alpha('#FFF7EA', 0.88), borderColor: alpha(theme.secondary_color, 0.78), color: theme.primary_color }}>
            <div className="px-5 py-5" style={{ background: `linear-gradient(180deg, ${alpha('#FFF7EA', 0.95)}, ${alpha('#F8DDE5', 0.72)})` }}>
              <p className="text-[0.56rem] font-black uppercase tracking-[0.22em]" style={{ color: theme.accent_color }}>Milestone Reserve</p>
              <h1 className="mx-auto mt-3 max-w-[15rem] text-[3.55rem] font-normal leading-[0.76]" style={{ color: theme.primary_color, fontFamily: '"Great Vibes", "Playfair Display", cursive', overflowWrap: 'anywhere' }}>{honoreeName}</h1>
              <div className="mx-auto mt-3 flex w-fit items-end justify-center gap-1 border-y px-4 py-2" style={{ borderColor: alpha(theme.secondary_color, 0.52) }}>
                <span className="text-[5.7rem] font-black leading-none" style={{ color: theme.secondary_color, fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>{milestone}</span>
                <span className="mb-4 text-[1.35rem] font-black uppercase leading-none tracking-[0.02em]" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>{milestoneSuffix}</span>
              </div>
              <p className="mt-3 text-[0.96rem] font-black uppercase tracking-[0.22em]" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, "Cormorant Garamond", Georgia, serif` }}>Birthday Toast</p>
            </div>
            <div className="px-5 py-4" style={{ background: `linear-gradient(145deg, ${alpha(theme.primary_color, 0.98)}, ${alpha('#2A0714', 0.96)})`, color: '#FFF7EA' }}>
              <p className="text-[0.88rem] font-semibold leading-6">{body}</p>
            </div>
          </div>
          <div className="mx-auto mt-6 flex max-w-[20rem] flex-col items-center gap-1.5 rounded-full border px-5 py-3 text-[0.65rem] font-black uppercase tracking-[0.16em] shadow-xl backdrop-blur-md" style={{ color: '#FFF7EA', background: alpha('#2B0718', 0.62), borderColor: alpha(theme.secondary_color, 0.58) }}>
            {startDate && <span>{readableDate(startDate)}</span>}
            {venueName && <span>{venueName}</span>}
          </div>
        </div>
      </section>
    );
  }

  if (birthdayMilestone) {
    const honoreeName = displayNames(content);
    const milestone = sectionText(section, 'headline', displayText(content.turning) || displayText(content.age) || '60');
    return (
      <section
        id="invite-hero"
        className="relative isolate flex items-center justify-center overflow-hidden px-6 pb-14 pt-10 text-center"
        style={{
          minHeight: mobileHeight,
          color: '#FFF7EA',
          backgroundColor: theme.primary_color,
          backgroundImage: heroImage
            ? `linear-gradient(180deg, ${alpha('#14060B', 0.24)} 0%, ${alpha(theme.primary_color, 0.12)} 34%, ${alpha('#14060B', 0.62)} 100%), url(${resolveMediaUrl(heroImage)})`
            : `radial-gradient(circle at 20% 18%, ${alpha(theme.secondary_color, 0.28)}, transparent 30%), linear-gradient(160deg, ${theme.primary_color}, #1B0810)`,
          backgroundPosition: effects.parallax ? 'center calc(50% + var(--invite-scroll-shift, 0px))' : layout.background_position || 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <DecorativeEffect effect={effects.hero_effect} intensity={effects.intensity} speed={effects.speed} accent={theme.secondary_color} />
        <div className="absolute inset-5 z-[1] border border-white/35" aria-hidden="true" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/10 via-transparent to-black/38" aria-hidden="true" />
        <div className="relative z-[3] mx-auto w-full max-w-sm">
          <p className="mx-auto max-w-[17rem] text-[0.66rem] font-black uppercase tracking-[0.28em]" style={{ color: theme.secondary_color, textShadow: '0 2px 15px rgba(0,0,0,.72)' }}>{eventName}</p>
          <div className="mx-auto mt-5 grid aspect-square w-44 place-items-center rounded-full border-[5px] border-double shadow-2xl backdrop-blur-md" style={{ borderColor: alpha(theme.secondary_color, 0.86), background: alpha('#FFF3E5', 0.38) }}>
            <p className="text-[6.2rem] font-normal leading-none" style={{ color: '#F9E6AA', fontFamily: `${theme.heading_font}, serif`, textShadow: '0 8px 30px rgba(0,0,0,.45)' }}>{milestone}</p>
          </div>
          <p className="mt-6 text-[0.72rem] font-black uppercase tracking-[0.28em] text-white">Birthday celebration</p>
          <h1 className="mt-3 text-[3.75rem] font-normal leading-[0.78] drop-shadow-xl" style={{ fontFamily: '"Great Vibes", "Playfair Display", cursive' }}>{honoreeName}</h1>
          <p className="mx-auto mt-6 max-w-xs rounded-2xl px-4 py-3 text-[0.98rem] font-semibold leading-7 text-white shadow-xl backdrop-blur-md" style={{ background: alpha('#260815', 0.42), textShadow: '0 2px 12px rgba(0,0,0,.72)' }}>{body}</p>
          <div className="mx-auto mt-6 flex max-w-[20rem] flex-col items-center gap-1.5 rounded-2xl border px-5 py-3 text-[0.66rem] font-black uppercase tracking-[0.17em] shadow-xl backdrop-blur-md" style={{ color: '#FFF7EA', background: alpha('#2B0718', 0.78), borderColor: alpha(theme.secondary_color, 0.64) }}>
            {startDate && <span>{readableDate(startDate)}</span>}
            {venueName && <span>{venueName}</span>}
          </div>
          <div className="mx-auto mt-5 max-w-[20rem] rounded-full border px-5 py-2 shadow-xl backdrop-blur-md" style={{ color: theme.secondary_color, background: alpha('#2B0718', 0.78), borderColor: alpha(theme.secondary_color, 0.6) }}>
            <p className="text-[0.66rem] font-black uppercase tracking-[0.2em]">Please keep it a surprise</p>
          </div>
        </div>
      </section>
    );
  }

  if (kashiRiverfront) {
    return (
      <section
        id="invite-hero"
        className="relative isolate flex items-start justify-center overflow-hidden px-7 pb-16 pt-10 text-center"
        style={{
          minHeight: mobileHeight,
          color: theme.text_color,
          backgroundColor: theme.background_color,
          backgroundImage: heroImage
            ? `linear-gradient(180deg, ${alpha(theme.background_color, overlayStrength * 0.18)} 0%, transparent 48%, ${alpha(theme.primary_color, 0.32)} 100%), url(${resolveMediaUrl(heroImage)})`
            : `linear-gradient(180deg, ${theme.background_color}, ${theme.primary_color})`,
          backgroundPosition: layout.background_position || 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="web-invite-kashi-hero-copy relative z-[3] mx-auto w-full max-w-sm px-2 py-4" style={{ background: `radial-gradient(ellipse at center, ${alpha(theme.background_color, 0.94)} 0%, ${alpha(theme.background_color, 0.72)} 52%, transparent 78%)` }}>
          <RiverRippleMark color={theme.primary_color} className="mx-auto h-9 w-28 opacity-55" />
          <p className="mt-1 text-[0.64rem] font-semibold uppercase tracking-[0.3em]" style={{ color: theme.accent_color }}>{eventName}</p>
          <h1 className="mt-4 text-[3.8rem] font-normal leading-[0.78]" style={{ color: theme.text_color, fontFamily: `${theme.heading_font}, serif`, textShadow: `0 2px 20px ${alpha(theme.background_color, 0.98)}` }}>
            <CoupleNameStack names={names} ampersandClassName="text-[0.42em]" />
          </h1>
          <p className="mx-auto mt-6 max-w-[18rem] text-[0.9rem] leading-7" style={{ color: alpha(theme.text_color, 0.82), textShadow: `0 1px 14px ${alpha(theme.background_color, 0.96)}` }}>{body}</p>
          <div className="mx-auto mt-6 flex max-w-[19rem] flex-col items-center gap-1.5 rounded-2xl border px-5 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.16em] shadow-lg backdrop-blur-md" style={{ color: '#173F46', background: 'rgba(255,249,234,.94)', borderColor: alpha(theme.accent_color, 0.42) }}>
            {startDate && <span>{readableDate(startDate)}</span>}
            {venueName && <span>{venueName}</span>}
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-40 bg-gradient-to-t from-[#173F46]/40 to-transparent" aria-hidden="true" />
        <RiverRippleMark color="#F3E7CF" className="pointer-events-none absolute bottom-8 left-1/2 z-[4] h-12 w-48 -translate-x-1/2 opacity-60" />
        <div className="pointer-events-none absolute -bottom-px inset-x-0 z-[5] h-9" style={{ background: theme.background_color, clipPath: 'polygon(0 58%, 12% 47%, 25% 61%, 39% 45%, 52% 57%, 67% 43%, 82% 59%, 100% 46%, 100% 100%, 0 100%)' }} aria-hidden="true" />
      </section>
    );
  }

  if (templeCourtyard) {
    return (
      <section
        id="invite-hero"
        className="relative isolate flex items-center justify-center overflow-hidden px-7 pb-20 pt-12 text-center text-[#FFF8E8]"
        style={{
          minHeight: mobileHeight,
          backgroundColor: theme.text_color,
          backgroundImage: heroImage
             ? `linear-gradient(180deg, ${alpha(theme.text_color, overlayStrength * 0.25)} 0%, ${alpha(theme.text_color, overlayStrength * 0.55)} 45%, ${alpha(theme.text_color, Math.min(1, overlayStrength + 0.18))} 100%), url(${resolveMediaUrl(heroImage)})`
            : `linear-gradient(165deg, ${theme.primary_color}, ${theme.text_color})`,
           backgroundPosition: effects.parallax ? 'center calc(50% + var(--invite-scroll-shift, 0px))' : layout.background_position || 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
         <DecorativeEffect effect={effects.hero_effect} intensity={effects.intensity} speed={effects.speed} accent="#F2D28A" />
         {layout.show_frame && layout.frame_image_url && <img src={resolveMediaUrl(layout.frame_image_url)} alt="" className="pointer-events-none absolute inset-0 z-[2] h-full w-full object-contain" aria-hidden="true" />}
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/45 to-transparent" aria-hidden="true" />
        <div className="relative z-[3] mx-auto w-full max-w-sm">
          <TempleKolamMark color="#E7C76F" className="mx-auto mb-4 h-16 w-16 opacity-80" />
          <p className="text-[0.66rem] font-semibold uppercase tracking-[0.3em] text-[#F4D992]">{eventName}</p>
          <h1 className="mt-4 text-[4.15rem] font-normal leading-[0.76] drop-shadow-xl" style={{ fontFamily: `${theme.heading_font}, serif` }}>
            <CoupleNameStack names={names} />
          </h1>
          <p className="mx-auto mt-6 max-w-xs text-[0.96rem] leading-7 text-white/85">{body}</p>
          <div className="mt-6 flex flex-col items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-[#F4D992]">
            {startDate && <span>{readableDate(startDate)}</span>}
            {venueName && <span>{venueName}</span>}
          </div>
        </div>
        <div
          className="pointer-events-none absolute -bottom-px inset-x-0 z-[5] h-16"
          style={{
            background: theme.background_color,
            clipPath: 'polygon(0 68%, 11% 68%, 11% 57%, 22% 57%, 22% 45%, 34% 45%, 34% 31%, 44% 31%, 44% 15%, 56% 15%, 56% 31%, 66% 31%, 66% 45%, 78% 45%, 78% 57%, 89% 57%, 89% 68%, 100% 68%, 100% 100%, 0 100%)',
            filter: `drop-shadow(0 -5px 8px ${alpha(theme.text_color, 0.25)})`,
          }}
          aria-hidden="true"
        />
      </section>
    );
  }

  if (royalTapestry) {
    const portrait = coupleHeroPhoto(section, eventData);
    const tapestry = layout.background_image_url;
    return (
      <section id="invite-hero" className="relative isolate overflow-hidden" style={{ minHeight: mobileHeight, background: theme.primary_color }}>
        <div className="relative overflow-hidden" style={{ height: { compact: '300px', tall: '440px', full: '58svh' }[layout.mobile_hero_height] || '440px' }}>
          {portrait ? (
             <img src={resolveMediaUrl(portrait)} alt={names} className={`absolute inset-0 h-full w-full object-cover ${effects.parallax ? 'web-invite-parallax-portrait' : ''}`} style={{ objectPosition: layout.background_position || 'center top' }} />
          ) : tapestry ? (
            <div className="absolute inset-0 bg-cover" style={{ backgroundImage: `url(${resolveMediaUrl(tapestry)})`, backgroundPosition: effects.parallax ? 'center calc(50% + var(--invite-scroll-shift, 0px))' : layout.background_position || 'center' }} />
          ) : null}
           <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${alpha('#101533', overlayStrength * 0.5)}, transparent 45%, ${alpha('#101533', Math.min(1, overlayStrength + 0.15))})` }} />
          <DecorativeEffect effect={effects.hero_effect} intensity={effects.intensity} speed={effects.speed} accent={theme.secondary_color} />
          <p className="absolute inset-x-6 top-9 text-center text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#F6DC9B] drop-shadow-lg">{eventName}</p>
        </div>
         {layout.show_frame && layout.frame_image_url && <img src={resolveMediaUrl(layout.frame_image_url)} alt="" className="pointer-events-none absolute inset-0 z-[3] h-full w-full object-contain" aria-hidden="true" />}
         <div className="relative z-[2] -mt-20 px-7 pb-16 pt-8 text-center text-[#FFF7E6]">
          {tapestry && <div className="absolute inset-x-0 bottom-0 top-0 -z-[1] bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${resolveMediaUrl(tapestry)})`, WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 30%)', maskImage: 'linear-gradient(to bottom, transparent, black 30%)' }} />}
          <div className="mx-auto mb-5 flex items-center justify-center gap-3 text-[#D9B865]" aria-hidden="true"><span className="h-px w-12 bg-current opacity-60" /><span className="h-2 w-2 rotate-45 border border-current" /><span className="h-px w-12 bg-current opacity-60" /></div>
          <h1 className="text-[4rem] font-normal leading-[0.76] drop-shadow-xl" style={{ fontFamily: `${theme.heading_font}, serif` }}><CoupleNameStack names={names} /></h1>
          <p className="mx-auto mt-7 max-w-sm text-[0.95rem] leading-7 text-white/80">{body}</p>
          <div className="mt-7 space-y-2 text-[0.7rem] uppercase tracking-[0.18em] text-[#F0D89D]">
            {startDate && <p>{readableDate(startDate)}</p>}
            {venueName && <p>{venueName}</p>}
          </div>
        </div>
        {tapestry && <div className="absolute inset-x-0 bottom-0 z-[3] h-12 bg-cover bg-[center_72%]" style={{ backgroundImage: `url(${resolveMediaUrl(tapestry)})`, WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 55%)', maskImage: 'linear-gradient(to bottom, transparent, black 55%)' }} aria-hidden="true" />}
      </section>
    );
  }

  return (
    <section
      id="invite-hero"
       className={`relative isolate flex overflow-hidden px-6 py-20 ${cinematic ? 'items-end justify-center text-center' : editorial ? 'items-center justify-start text-left' : 'items-center justify-center text-center'} ${splitPanel ? 'web-invite-split-hero-section' : ''}`}
      style={{
        minHeight: mobileHeight,
        color: heroColor,
        backgroundImage: heroImage
          ? gardenFrame
            ? `linear-gradient(145deg, ${alpha(theme.background_color, overlayStrength * 0.82)}, ${alpha(theme.primary_color, overlayStrength * 0.42)}), url(${resolveMediaUrl(heroImage)})`
            : `linear-gradient(135deg, ${alpha(theme.primary_color, overlayStrength)}, ${alpha(theme.text_color, overlayStrength * 0.76)}), url(${resolveMediaUrl(heroImage)})`
          : `radial-gradient(circle at 80% 15%, ${alpha(theme.secondary_color, 0.48)}, transparent 34%), linear-gradient(145deg, ${theme.primary_color}, ${theme.text_color})`,
         backgroundPosition: effects.parallax ? 'center calc(50% + var(--invite-scroll-shift, 0px))' : layout.background_position,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
         backgroundAttachment: 'scroll',
      }}
    >
      <DecorativeEffect effect={effects.hero_effect} intensity={effects.intensity} speed={effects.speed} accent={theme.secondary_color} />
      {layout.show_frame && layout.frame_image_url ? (
        <img
          src={resolveMediaUrl(layout.frame_image_url)}
          alt=""
          className="pointer-events-none absolute left-1/2 top-1/2 z-[2] h-auto w-full max-w-[408px] -translate-x-1/2 -translate-y-1/2 object-contain"
          aria-hidden="true"
        />
      ) : (
        <div className="absolute inset-5 z-[2] border border-current opacity-20" style={{ borderRadius: RADIUS[theme.corner_style] }} />
      )}
       <div className={`relative z-[3] ${editorial ? 'mr-auto max-w-[88%] border-l-2 pl-5' : 'mx-auto'} ${gardenFrame ? 'max-w-[66%] -translate-y-[3%] px-2 py-6' : 'max-w-3xl'} ${cinematic ? 'pb-12' : ''}`} style={editorial ? { borderColor: alpha(theme.secondary_color, 0.85) } : undefined}>
         <div className={`mb-6 flex items-center gap-3 opacity-70 ${editorial ? 'justify-start' : 'justify-center'}`}>
          <span className="h-px w-12 bg-current" />
          <Sparkle className="h-4 w-4" weight="fill" />
          <span className="h-px w-12 bg-current" />
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.32em] opacity-85">{eventName}</p>
        <h1
          className={`${section.variant === 'minimal' ? 'text-4xl' : 'text-6xl'} mt-4 font-normal leading-[0.82]`}
          style={{ fontFamily: `${theme.heading_font}, cursive`, textShadow: gardenFrame ? '0 2px 18px rgba(255,248,239,.92)' : '0 2px 22px rgba(0,0,0,.24)' }}
        >
          <CoupleNameStack names={names} />
        </h1>
         <p className={`${editorial ? '' : 'mx-auto'} mt-6 max-w-xl text-base leading-8 opacity-85`}>{body}</p>
         <div className={`mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm uppercase tracking-[0.16em] opacity-80 ${editorial ? 'justify-start' : 'justify-center'}`}>
          {startDate && <span>{readableDate(startDate)}</span>}
          {venueName && <span>· {venueName}</span>}
        </div>
      </div>
      {gardenFrame && (
        <div
          className="pointer-events-none absolute -bottom-px inset-x-0 z-[5] h-12"
          style={{
            background: theme.background_color,
            clipPath: 'polygon(0 68%, 8% 52%, 17% 65%, 27% 46%, 38% 61%, 49% 43%, 60% 64%, 72% 47%, 83% 63%, 92% 49%, 100% 60%, 100% 100%, 0 100%)',
            filter: `drop-shadow(0 -4px 4px ${alpha(theme.primary_color, 0.08)})`,
          }}
          aria-hidden="true"
        />
      )}
    </section>
  );
}

function CountdownSection({ section, eventData, theme, layout }) {
  const content = eventSource(section, eventData);
  const target = content.target_date || (usesEventContent(section) ? eventData.start_date : null);
  const countdown = useCountdown(target);
  const cells = [
    ['Days', countdown.days], ['Hours', countdown.hours], ['Minutes', countdown.minutes], ['Seconds', countdown.seconds],
  ];
  const gardenMedallions = section.variant === 'garden_medallions';
  const gardenStatement = section.variant === 'garden_statement';
  const muhurthamDial = section.variant === 'muhurtham_dial' || section.variant === 'brass_bells';
  const wovenNumerals = section.variant === 'woven_numerals';
  const ghatCountdown = section.variant === 'ghat_countdown';
  const milestoneCountdown = section.variant === 'milestone_countdown';
  const champagneCountdown = milestoneCountdown && layout?.section_style === 'champagne_toast';

  if (champagneCountdown) {
    return (
      <section className="web-invite-toast-night relative isolate overflow-hidden px-5 py-14 text-center text-[#FFF8EB]">
        {layout.background_image_url && <div className="absolute inset-0 -z-[2] bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})` }} aria-hidden="true" />}
        <div className="absolute inset-0 -z-[1] bg-gradient-to-b from-[#270911]/90 via-[#4B0D22]/95 to-[#21070E]/95" aria-hidden="true" />
        <p className="text-[0.62rem] font-semibold uppercase tracking-[0.28em] text-[#E9C36A]">{sectionText(section, 'eyebrow', 'The golden countdown')}</p>
        <h2 className="mx-auto mt-2 max-w-xs text-[2.55rem] font-normal leading-[0.96]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', countdown.isPast ? 'The toast has begun' : 'Until the toast begins')}</h2>
        <div className="web-invite-toast-clock relative mx-auto mt-8 grid aspect-square w-[min(82vw,320px)] place-items-center rounded-full">
          <div className="absolute inset-[10px] rounded-full border border-[#E9C36A]/40" aria-hidden="true" />
          <div className="absolute inset-[25px] rounded-full border border-dashed border-[#E9C36A]/25" aria-hidden="true" />
          <div className="grid w-[72%] grid-cols-2">
            {cells.map(([label, value], index) => (
              <div key={label} className="relative flex min-h-[88px] flex-col items-center justify-center px-1" style={{ borderRight: index % 2 === 0 ? '1px solid rgba(233,195,106,.32)' : undefined, borderBottom: index < 2 ? '1px solid rgba(233,195,106,.32)' : undefined }}>
                <p className="text-[2.25rem] leading-none text-[#F3D587]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{String(value).padStart(2, '0')}</p>
                <p className="mt-1 text-[0.52rem] uppercase tracking-[0.16em] text-white/60">{label}</p>
              </div>
            ))}
          </div>
          {Array.from({ length: 7 }, (_, index) => <span key={index} className="web-invite-toast-clock-bubble absolute rounded-full border border-[#F3D587]/45" style={{ left: `${14 + ((index * 19) % 70)}%`, bottom: `${8 + ((index * 17) % 74)}%`, width: `${5 + (index % 3) * 3}px`, height: `${5 + (index % 3) * 3}px`, animationDelay: `${index * 180}ms` }} aria-hidden="true" />)}
        </div>
        <p className="mt-7 text-[0.66rem] uppercase tracking-[0.18em] text-white/58">{sectionText(section, 'body', target ? readableDate(target) : 'Add the celebration date to begin the countdown.')}</p>
      </section>
    );
  }

  if (ghatCountdown) {
    return (
      <section className="relative overflow-hidden px-5 py-10 text-center">
        <SectionHeading
          eyebrow="The river keeps time"
          title={sectionText(section, 'headline', countdown.isPast ? 'The celebration has begun' : 'Until we meet by the Ganga')}
          body={sectionText(section, 'body', target ? `For ${readableDate(target)}` : 'Add the celebration date to begin the countdown.')}
          theme={theme}
        />
        <div className="relative mx-auto mt-7 flex h-48 max-w-sm items-end justify-center" aria-label="Wedding countdown">
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14" style={{ background: `linear-gradient(to top, ${alpha(theme.primary_color, 0.18)}, transparent)` }} aria-hidden="true" />
          {cells.map(([label, value], index) => (
            <div
              key={label}
              className="relative flex min-w-0 flex-1 flex-col items-center justify-start border-x border-t px-1 pt-5"
              style={{
                height: `${112 + index * 19}px`,
                color: theme.text_color,
                borderColor: alpha(theme.accent_color, 0.42),
                background: `linear-gradient(180deg, ${alpha(theme.secondary_color, 0.08)}, ${alpha(theme.accent_color, 0.14)})`,
              }}
            >
              <p className="text-[2rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{String(value).padStart(2, '0')}</p>
              <p className="mt-2 text-[0.56rem] uppercase tracking-[0.08em] opacity-65">{label}</p>
            </div>
          ))}
          <RiverRippleMark color={theme.primary_color} className="pointer-events-none absolute -bottom-3 left-1/2 h-9 w-44 -translate-x-1/2 opacity-45" />
        </div>
      </section>
    );
  }

  if (milestoneCountdown) {
    return (
      <section className="relative overflow-hidden px-5 py-12 text-center" style={{ background: `linear-gradient(180deg, ${theme.background_color}, ${alpha(theme.secondary_color, 0.13)})` }}>
        <div className="mx-auto max-w-sm">
          <SectionHeading
            eyebrow={sectionText(section, 'eyebrow', 'The secret countdown')}
            title={sectionText(section, 'headline', countdown.isPast ? 'The celebration has begun' : 'Until the secret is out')}
            body={sectionText(section, 'body', target ? `For ${readableDate(target)}` : 'Add the celebration date to begin the countdown.')}
            theme={theme}
          />
          <div className="mt-8 grid grid-cols-4 overflow-hidden rounded-2xl border bg-white/75 shadow-xl" style={{ borderColor: alpha(theme.secondary_color, 0.48) }}>
            {cells.map(([label, value], index) => (
              <div key={label} className="relative px-1 py-5">
                <p className="text-[2.15rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{String(value).padStart(2, '0')}</p>
                <p className="mt-2 text-[0.58rem] uppercase tracking-[0.1em] opacity-60">{label}</p>
                {index < cells.length - 1 && <span className="absolute bottom-4 right-0 top-4 w-px bg-gradient-to-b from-transparent via-[#D7B56D]/60 to-transparent" aria-hidden="true" />}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (muhurthamDial) {
    return (
      <section className="relative overflow-hidden px-6 py-10 text-center">
        <SectionHeading
          eyebrow="The auspicious hour"
          title={sectionText(section, 'headline', countdown.isPast ? 'The celebration has begun' : 'Until the muhūrtham')}
          body={sectionText(section, 'body', target ? `For ${readableDate(target)}` : 'Add the ceremony date to begin the countdown.')}
          theme={theme}
        />
        <div
          className="relative mx-auto mt-7 grid aspect-square max-w-[310px] grid-cols-2 overflow-hidden rounded-full border p-9"
          style={{ borderColor: alpha(theme.secondary_color, 0.68), boxShadow: `inset 0 0 0 8px ${alpha(theme.secondary_color, 0.08)}` }}
        >
          <TempleKolamMark color={theme.secondary_color} className="pointer-events-none absolute inset-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 opacity-[0.09]" />
          {cells.map(([label, value], index) => (
            <div
              key={label}
              className="relative flex flex-col items-center justify-center"
              style={{
                borderRight: index % 2 === 0 ? `1px solid ${alpha(theme.secondary_color, 0.3)}` : undefined,
                borderBottom: index < 2 ? `1px solid ${alpha(theme.secondary_color, 0.3)}` : undefined,
              }}
            >
              <p className="text-[2.35rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{String(value).padStart(2, '0')}</p>
              <p className="mt-1 text-[0.62rem] uppercase tracking-[0.13em] opacity-60">{label}</p>
            </div>
          ))}
          <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border" style={{ background: theme.background_color, borderColor: theme.secondary_color }} aria-hidden="true" />
        </div>
      </section>
    );
  }

  if (wovenNumerals) {
    return (
      <section className="relative isolate overflow-hidden px-5 py-10 text-center text-[#FFF7E6]" style={{ background: theme.primary_color }}>
        {layout.background_image_url && <div className="absolute inset-0 -z-[1] bg-cover bg-[center_36%] opacity-[0.32]" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})` }} aria-hidden="true" />}
        <div className="absolute inset-0 -z-[1] bg-[#111632]/70" aria-hidden="true" />
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#E9CB83]">Save the date</p>
        <h2 className="mt-2 text-[2.6rem] leading-none" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', countdown.isPast ? 'The celebration has begun' : 'Counting every beautiful moment')}</h2>
        <div className="mx-auto mt-7 grid max-w-sm grid-cols-4">
          {cells.map(([label, value], index) => (
            <div key={label} className="relative px-1">
              <p className="text-[2rem] leading-none text-[#F1D38E]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{String(value).padStart(2, '0')}</p>
              <p className="mt-2 text-[0.58rem] uppercase tracking-[0.1em] text-white/70">{label}</p>
              {index < cells.length - 1 && <span className="absolute -right-px top-1 h-11 w-px bg-gradient-to-b from-transparent via-[#D8B563]/70 to-transparent" aria-hidden="true" />}
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs uppercase tracking-[0.14em] text-white/65">{sectionText(section, 'body', target ? `Until ${readableDate(target)}` : 'Add an event date to start the countdown.')}</p>
      </section>
    );
  }

  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
      <SectionHeading
        eyebrow="Save the date"
        title={sectionText(section, 'headline', countdown.isPast ? 'The celebration has begun' : 'Counting every beautiful moment')}
        body={sectionText(section, 'body', target ? `Until ${readableDate(target)}` : 'Add an event date to start the countdown.')}
        theme={theme}
      />
       <div className={`web-invite-scroll-bloom relative mx-auto grid max-w-2xl grid-cols-4 ${gardenStatement ? 'mt-4 gap-0 py-4' : section.variant === 'statement' ? 'mt-6 gap-1 border-y py-6' : section.variant === 'inline' ? 'mt-6 gap-0 divide-x' : 'mt-6 gap-2'}`} style={section.variant === 'statement' ? { borderColor: alpha(theme.secondary_color, 0.45) } : undefined}>
        {cells.map(([label, value], index) => (
           gardenStatement || section.variant === 'statement' ? (
            <div key={label} className="relative flex flex-col items-center justify-center px-1 text-center">
               <p className={`${section.variant === 'statement' ? 'text-[2.7rem]' : 'text-4xl'} leading-none`} style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{String(value).padStart(2, '0')}</p>
              <p className="mt-1 text-[0.65rem] uppercase tracking-[0.1em] opacity-65">{label}</p>
            </div>
          ) : <Surface
              key={label}
              theme={theme}
              className={`${gardenMedallions ? 'aspect-square self-center border-4 px-1 py-3' : 'px-1 py-4'} flex flex-col items-center justify-center text-center`}
              style={gardenMedallions ? {
                borderColor: alpha(theme.secondary_color, 0.38),
                borderRadius: '50%',
                background: `radial-gradient(circle, rgba(255,253,247,.96), ${alpha(theme.secondary_color, 0.16)})`,
                boxShadow: `inset 0 0 0 2px rgba(255,255,255,.9), 0 12px 30px ${alpha(theme.primary_color, 0.12)}`,
              } : {}}
            >
            <p className="text-2xl" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{value}</p>
            <p className="mt-1 text-[0.65rem] uppercase tracking-[0.14em] opacity-60">{label}</p>
            </Surface>
        ))}
      </div>
    </section>
  );
}

function StorySection({ section, eventData, theme, layout }) {
  const content = eventSource(section, eventData);
  const body = sectionText(section, 'body', content.description || (usesEventContent(section) ? eventData.description : '') || 'Add your story in the section editor to make this invitation unmistakably yours.');
  const vellumLetter = section.variant === 'vellum_letter';
  const stonePrologue = section.variant === 'stone_prologue' || section.variant === 'palm_leaf_letter';
  const illuminatedFolio = section.variant === 'illuminated_folio';
  const riverLetter = section.variant === 'river_letter';
  const champagneStory = section.variant === 'birthday_note' && layout?.section_style === 'champagne_toast';
  if (champagneStory) {
    return (
      <section className="web-invite-toast-stationery relative overflow-hidden bg-[#F4E7D4] px-7 py-14 text-[#35151F]">
        <div className="pointer-events-none absolute -left-12 top-5 h-36 w-36 rounded-full border border-[#B88732]/20" aria-hidden="true" />
        <article className="web-invite-toast-place-card relative mx-auto max-w-sm rotate-[-1.5deg] border border-[#C99E51] bg-[#FFF9EE] px-7 py-10 text-center shadow-[0_22px_45px_rgba(67,22,35,0.18)]">
          <div className="pointer-events-none absolute inset-2 border border-[#C99E51]/35" aria-hidden="true" />
          <p className="relative text-[0.58rem] font-semibold uppercase tracking-[0.25em] text-[#A56F20]">{sectionText(section, 'eyebrow', 'A card from the table')}</p>
          <h2 className="relative mt-3 text-[2.35rem] font-normal leading-[0.98] text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', 'A toast to a beautiful life')}</h2>
          <span className="relative mx-auto my-5 block h-px w-20 bg-[#C99E51]" aria-hidden="true" />
          <p className="web-invite-toast-letter-copy relative text-[1.02rem] leading-7 text-[#4B2931]/85">{body}</p>
          <p className="relative mt-6 text-[0.55rem] font-semibold uppercase tracking-[0.22em] text-[#A56F20]">With love, your hosts</p>
        </article>
        <div className="pointer-events-none absolute -bottom-10 right-4 h-28 w-28 rounded-full border-[10px] border-[#D2A94B]/10 shadow-[inset_0_0_0_1px_rgba(183,130,40,.25)]" aria-hidden="true" />
      </section>
    );
  }
  if (riverLetter) {
    const firstCharacter = body.trim().charAt(0);
    const remainingBody = body.trim().slice(1);
    return (
      <section className="relative overflow-hidden px-7 py-10">
        <RiverRippleMark color={theme.primary_color} className="pointer-events-none absolute -right-10 top-4 h-20 w-56 opacity-[0.09]" />
        <div className="mx-auto max-w-3xl border-l-2 py-3 pl-6" style={{ borderColor: alpha(theme.accent_color, 0.46) }}>
          <SectionHeading eyebrow="Carried by the river" title={sectionText(section, 'headline', 'Where our journey meets the river')} align="left" theme={theme} />
          <p className="mt-6 text-left text-[1.02rem] leading-8 opacity-85">
            {firstCharacter && <span className="float-left mr-3 mt-1 text-[4.6rem] leading-[0.7]" style={{ color: theme.accent_color, fontFamily: `${theme.heading_font}, serif` }}>{firstCharacter}</span>}
            {remainingBody}
          </p>
          <div className="mt-6 flex items-center gap-3 text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: theme.primary_color }}><span className="h-px w-12 bg-current opacity-50" /><span>From this shore onward</span></div>
        </div>
      </section>
    );
  }
  if (stonePrologue) {
    const firstCharacter = body.trim().charAt(0);
    const remainingBody = body.trim().slice(1);
    return (
      <section className="relative overflow-hidden px-7 py-9">
        <TempleKolamMark color={theme.secondary_color} className="pointer-events-none absolute -right-8 top-0 h-32 w-32 opacity-[0.08]" />
        <div className="mx-auto max-w-3xl border-y py-8" style={{ borderColor: alpha(theme.secondary_color, 0.38) }}>
          <SectionHeading eyebrow="Written in stone" title={sectionText(section, 'headline', 'Our beginning')} theme={theme} />
          <p className="mt-6 text-left text-[1.05rem] leading-8 opacity-85">
            {firstCharacter && <span className="float-left mr-3 mt-1 text-[4.8rem] leading-[0.7]" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{firstCharacter}</span>}
            {remainingBody}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3" style={{ color: theme.secondary_color }} aria-hidden="true"><span className="h-px w-14 bg-current opacity-50" /><span className="h-2 w-2 rotate-45 border border-current" /><span className="h-px w-14 bg-current opacity-50" /></div>
        </div>
      </section>
    );
  }
  if (illuminatedFolio) {
    const firstCharacter = body.trim().charAt(0);
    const remainingBody = body.trim().slice(1);
    return (
      <section className="px-7 py-10">
        <div className="mx-auto max-w-3xl border-y py-8" style={{ borderColor: alpha(theme.secondary_color, 0.38) }}>
          <SectionHeading eyebrow="From the archives" title={sectionText(section, 'headline', 'Our story')} theme={theme} />
          <p className="mt-6 text-left text-[1rem] leading-8 opacity-85">
            {firstCharacter && <span className="float-left mr-3 mt-1 text-[4.5rem] leading-[0.72]" style={{ color: theme.accent_color, fontFamily: `${theme.heading_font}, serif` }}>{firstCharacter}</span>}
            {remainingBody}
          </p>
          <div className="mt-6 flex items-center justify-end gap-2 text-[0.62rem] uppercase tracking-[0.2em]" style={{ color: theme.accent_color }}><span className="h-px w-12 bg-current opacity-50" /><span>Written with love</span></div>
        </div>
      </section>
    );
  }
  const storyContent = (
    <>
      {vellumLetter && <p className="mb-3 text-center text-2xl" style={{ color: theme.accent_color }} aria-hidden="true">❦</p>}
      <SectionHeading eyebrow="From the heart" title={sectionText(section, 'headline', 'Our story')} theme={theme} />
      <p
         className={`${section.variant === 'quote' ? 'text-center text-2xl italic leading-10' : section.variant === 'letter' ? 'text-left text-lg italic leading-9' : 'text-center text-lg leading-8'} mt-4 opacity-80`}
         style={{ fontFamily: section.variant === 'quote' || section.variant === 'letter' || vellumLetter ? `${theme.heading_font}, serif` : undefined }}
      >
        {body}
      </p>
      {vellumLetter && <div className="mx-auto mt-4 flex justify-center gap-2" aria-hidden="true"><span className="h-1.5 w-1.5 rotate-45" style={{ background: alpha(theme.accent_color, 0.35) }} /><span className="h-2 w-2 rotate-45" style={{ background: alpha(theme.accent_color, 0.55) }} /><span className="h-1.5 w-1.5 rotate-45" style={{ background: alpha(theme.accent_color, 0.35) }} /></div>}
    </>
  );
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing], background: vellumLetter ? 'transparent' : alpha(theme.secondary_color, 0.10) }}>
       <div className={`mx-auto max-w-3xl ${section.variant === 'letter' ? 'border-y px-5 py-7' : ''}`} style={section.variant === 'letter' ? { borderColor: alpha(theme.accent_color, 0.4), background: alpha(theme.background_color, 0.65) } : undefined}>
        {vellumLetter ? (
          <Surface theme={theme} className="relative overflow-hidden border-0 px-4 py-4" style={{ background: 'transparent', borderColor: 'transparent', boxShadow: 'none' }}>
            {storyContent}
          </Surface>
        ) : storyContent}
      </div>
    </section>
  );
}

function HostsSection({ section, eventData, theme }) {
  const content = eventSource(section, eventData);
  const names = displayNames({ ...(usesEventContent(section) ? eventData : {}), ...content });
  const hostNames = names.split(' & ');
  const namesOnly = section.variant === 'names';
  const cameos = section.variant === 'cameos';
  const split = section.variant === 'split';
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
      <SectionHeading eyebrow="With love" title={sectionText(section, 'headline', 'Meet the hosts')} body={sectionText(section, 'body', names)} theme={theme} />
       <div className={`mx-auto mt-8 flex max-w-xl items-center justify-center ${split ? 'gap-0 border-y' : 'gap-5'}`} style={split ? { borderColor: alpha(theme.secondary_color, 0.45) } : undefined}>
         {hostNames.map((name, index) => (
          <React.Fragment key={`${name}-${index}`}>
            {index > 0 && <Heart className="h-5 w-5" weight="fill" style={{ color: theme.accent_color }} />}
             <Surface theme={theme} className={`${namesOnly || split ? 'border-0 bg-transparent shadow-none' : ''} ${cameos ? 'aspect-square rounded-full' : ''} min-w-0 flex-1 px-4 py-7 text-center`} style={namesOnly || split ? { background: 'transparent', borderColor: 'transparent', boxShadow: 'none' } : cameos ? { borderRadius: '50%', borderWidth: '2px' } : {}}>
              <span className="text-2xl" style={{ fontFamily: `${theme.heading_font}, serif` }}>{name}</span>
            </Surface>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

function itineraryItems(section, eventData) {
  if (!usesEventContent(section)) return [];
  const contentItems = section.content?.items;
  if (Array.isArray(contentItems)) return contentItems;
  const allItems = eventData.itinerary || [];
  if (section.settings?.include_all_items) return allItems;
  const selected = new Set(section.settings?.selected_item_ids || []);
  return allItems.filter((item) => selected.has(item.id));
}

function CeremonyTimeline({ items, theme }) {
  const timelineRef = useRef(null);
  const [activeStop, setActiveStop] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const node = timelineRef.current;
      if (!node || items.length < 2) return;
      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const raw = (viewportHeight * 0.5 - rect.top) / Math.max(1, rect.height);
      const bounded = Math.min(1, Math.max(0, raw));
      setScrollProgress(bounded);
      setActiveStop(Math.round(bounded * (items.length - 1)));
    };
    const requestUpdate = () => {
      if (frame == null) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', requestUpdate, true);
    window.addEventListener('resize', requestUpdate);
    return () => {
      window.removeEventListener('scroll', requestUpdate, true);
      window.removeEventListener('resize', requestUpdate);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [items.length]);

  const markerTop = items.length > 1
    ? `calc(${scrollProgress * 100}% + ${12 - scrollProgress * 24}px)`
    : '12px';

  return (
    <div ref={timelineRef} className="relative mx-auto mt-6 max-w-sm py-2">
      <div className="absolute bottom-5 left-[81px] top-5 w-px" style={{ background: `linear-gradient(${theme.accent_color}, ${alpha(theme.primary_color, 0.26)})` }} />
      <span
        className="absolute left-[81px] z-[3] grid h-8 w-8 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-2xl shadow-sm transition-[top] duration-150 ease-out"
        style={{ top: markerTop, color: theme.accent_color }}
        aria-hidden="true"
      >
        ❀
      </span>
      {items.map((item, index) => {
        const itemVenue = venueLabel(item.venue);
        const active = index === activeStop;
        return (
          <div key={item.id || `${item.name}-${index}`} className={`grid min-h-24 grid-cols-[68px_26px_minmax(0,1fr)] items-start transition-opacity duration-500 ${active ? 'opacity-100' : 'opacity-65'}`}>
            <div className="pr-3 pt-1 text-right">
              <p className="text-xl font-medium" style={{ color: active ? theme.primary_color : undefined, fontFamily: `${theme.heading_font}, serif` }}>{item.start_time || 'TBA'}</p>
              {item.date && <p className="mt-1 text-[0.65rem] uppercase leading-4 tracking-[0.08em] opacity-60">{readableDate(item.date)}</p>}
            </div>
            <div className="relative z-[1] flex justify-center pt-3"><span className={`h-2.5 w-2.5 rotate-45 border transition-transform duration-500 ${active ? 'scale-125' : ''}`} style={{ borderColor: theme.accent_color, background: '#FFFFFF' }} /></div>
            <div className="pl-4 pt-1">
              <h3 className="text-[1.35rem] leading-tight" style={{ color: active ? theme.primary_color : undefined, fontFamily: `${theme.heading_font}, serif` }}>{item.name || 'Celebration'}</h3>
              {itemVenue && <p className="mt-2 text-sm leading-6 opacity-70">{itemVenue}</p>}
              {item.dress_code && <p className="mt-1 text-xs uppercase tracking-[0.1em] opacity-55">{item.dress_code}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MuhurthamProgram({ items, theme }) {
  const days = useMemo(() => {
    const grouped = new Map();
    items.forEach((item) => {
      const key = item.date || 'undated';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(item);
    });
    return [...grouped.entries()].map(([date, dayItems]) => ({ date, items: dayItems }));
  }, [items]);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    setActiveDay((current) => Math.min(current, Math.max(0, days.length - 1)));
  }, [days.length]);

  const selectedDay = days[activeDay];
  return (
    <div className="mx-auto mt-7 max-w-3xl">
      {days.length > 1 && (
        <div className="flex flex-wrap justify-center gap-2" role="tablist" aria-label="Wedding programme days">
          {days.map((day, index) => (
            <button
              key={day.date}
              type="button"
              role="tab"
              aria-selected={index === activeDay}
              onClick={() => setActiveDay(index)}
              className="min-w-[92px] border-b-2 px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.12em] transition-colors"
              style={{
                color: index === activeDay ? theme.primary_color : alpha(theme.text_color, 0.58),
                borderColor: index === activeDay ? theme.secondary_color : alpha(theme.secondary_color, 0.2),
              }}
            >
              {day.date === 'undated' ? `Day ${index + 1}` : readableShortDate(day.date)}
            </button>
          ))}
        </div>
      )}
      {selectedDay && (
        <div className="mt-6 border-y" style={{ borderColor: alpha(theme.secondary_color, 0.38) }} role="tabpanel">
          <div className="flex items-center justify-center gap-3 py-4 text-center" style={{ color: theme.secondary_color }}>
            <span className="h-px w-10 bg-current opacity-50" />
            <TempleKolamMark color={theme.secondary_color} className="h-7 w-7" />
            <span className="h-px w-10 bg-current opacity-50" />
          </div>
          {selectedDay.items.map((item, index) => {
            const itemVenue = venueLabel(item.venue);
            return (
              <article key={item.id || `${item.name}-${index}`} className="grid grid-cols-[78px_minmax(0,1fr)] gap-5 border-t px-1 py-5 first:border-t-0" style={{ borderColor: alpha(theme.secondary_color, 0.24) }}>
                <div className="pt-1 text-right">
                  <p className="text-[1.3rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{item.start_time || 'TBA'}</p>
                </div>
                <div className="min-w-0 border-l pl-5" style={{ borderColor: alpha(theme.secondary_color, 0.42) }}>
                  <h3 className="break-words text-[1.65rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{item.name || 'Celebration'}</h3>
                  {itemVenue && <p className="mt-2 break-words text-sm leading-6 opacity-70">{itemVenue}</p>}
                  {item.dress_code && <p className="mt-1 text-[0.66rem] uppercase tracking-[0.12em] opacity-55">Attire · {item.dress_code}</p>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GhatProgramme({ items, theme }) {
  return (
    <div className="relative mx-auto mt-7 max-w-3xl pb-4">
      <div className="pointer-events-none absolute bottom-0 left-0 top-3 w-1" style={{ background: `linear-gradient(${theme.secondary_color}, ${theme.primary_color})` }} aria-hidden="true" />
      {items.map((item, index) => {
        const itemVenue = venueLabel(item.venue);
        const inset = Math.min(index * 11, 44);
        return (
          <article
            key={item.id || `${item.name}-${index}`}
            className="relative border-b border-l px-5 py-5"
            style={{
              marginLeft: `${inset}px`,
              borderColor: alpha(theme.accent_color, 0.34),
              background: `linear-gradient(100deg, ${alpha(theme.secondary_color, 0.13)}, transparent 78%)`,
            }}
          >
            <span className="absolute -left-[7px] top-6 h-3 w-3 rotate-45 border" style={{ background: theme.background_color, borderColor: theme.accent_color }} aria-hidden="true" />
            <div className="flex min-w-0 items-start gap-4">
              <p className="w-[68px] shrink-0 text-right text-[1.15rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{item.start_time || 'TBA'}</p>
              <div className="min-w-0">
                <h3 className="break-words text-[1.55rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{item.name || 'Celebration'}</h3>
                <p className="mt-2 break-words text-[0.66rem] uppercase tracking-[0.1em] opacity-55">{readableDate(item.date) || 'Date to be announced'}</p>
                {itemVenue && <p className="mt-2 break-words text-sm leading-6 opacity-72">{itemVenue}</p>}
                {item.dress_code && <p className="mt-1 text-xs opacity-60">Attire · {item.dress_code}</p>}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function CeremonyCarousel({ items, theme, layout }) {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(0, items.length - 1)));
  }, [items.length]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    let frame = null;
    const updateActiveCard = () => {
      frame = null;
      const scrollerRect = scroller.getBoundingClientRect();
      const center = scrollerRect.left + scrollerRect.width / 2;
      const cards = [...scroller.querySelectorAll('[data-ceremony-card]')];
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const distance = Math.abs(cardRect.left + cardRect.width / 2 - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      setActiveIndex(closestIndex);
    };
    const requestUpdate = () => {
      if (frame == null) frame = window.requestAnimationFrame(updateActiveCard);
    };
    updateActiveCard();
    scroller.addEventListener('scroll', requestUpdate, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', requestUpdate);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [items.length]);

  const goToCard = (nextIndex) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const cards = [...scroller.querySelectorAll('[data-ceremony-card]')];
    const targetIndex = Math.min(Math.max(nextIndex, 0), cards.length - 1);
    const card = cards[targetIndex];
    if (!card) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const left = scroller.scrollLeft + cardRect.left - scrollerRect.left - (scroller.clientWidth - cardRect.width) / 2;
    scroller.scrollTo({ left, behavior: 'smooth' });
    setActiveIndex(targetIndex);
  };

  return (
    <>
      <div className="mt-6 flex items-center justify-between px-7">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] opacity-60">Event {activeIndex + 1} of {items.length}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => goToCard(activeIndex - 1)}
            disabled={activeIndex === 0}
            className="grid h-10 w-10 place-items-center border transition-colors disabled:cursor-not-allowed disabled:opacity-30"
            style={{ color: theme.primary_color, borderColor: alpha(theme.secondary_color, 0.62), background: alpha(theme.background_color, 0.9) }}
            aria-label="Previous ceremony"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => goToCard(activeIndex + 1)}
            disabled={activeIndex === items.length - 1}
            className="grid h-10 w-10 place-items-center border transition-colors disabled:cursor-not-allowed disabled:opacity-30"
            style={{ color: '#FFF7E6', borderColor: theme.primary_color, background: theme.primary_color }}
            aria-label="Next ceremony"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="web-invite-horizontal-scroll mt-3 flex w-full max-w-full snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden px-7 pb-5"
        style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorInline: 'contain', scrollPaddingInline: '28px' }}
        role="region"
        aria-label="Ceremony programme"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToCard(activeIndex - 1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToCard(activeIndex + 1);
          }
        }}
      >
        {items.map((item, index) => {
          const itemVenue = venueLabel(item.venue);
          const ink = index % 2 === 0 ? theme.primary_color : theme.accent_color;
          return (
            <article
              key={item.id || `${item.name}-${index}`}
              data-ceremony-card
              className="relative min-h-64 min-w-0 shrink-0 snap-center overflow-hidden p-7 text-[#FFF7E6] shadow-xl"
              style={{ flexBasis: 'calc(100% - 64px)', maxWidth: 'calc(100% - 64px)', background: `linear-gradient(145deg, ${ink}, ${alpha(ink, 0.9)})` }}
            >
              {layout.background_image_url && <div className="absolute inset-x-0 top-0 h-20 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})`, WebkitMaskImage: 'linear-gradient(to bottom, black, transparent)', maskImage: 'linear-gradient(to bottom, black, transparent)' }} aria-hidden="true" />}
              <div className="relative z-[1] flex h-full min-w-0 flex-col">
                <p className="text-[0.65rem] uppercase tracking-[0.24em] text-[#E8CA83]">Chapter {String(index + 1).padStart(2, '0')}</p>
                <h3 className="mt-8 break-words text-[2.25rem] leading-none" style={{ fontFamily: `${theme.heading_font}, serif` }}>{item.name || 'Celebration'}</h3>
                <div className="mt-auto min-w-0 pt-10">
                  <p className="break-words text-[0.72rem] uppercase tracking-[0.16em] text-white/70">{[readableDate(item.date), item.start_time].filter(Boolean).join(' · ') || 'Timing to be announced'}</p>
                  {itemVenue && <p className="mt-3 break-words text-sm leading-6 text-white/85">{itemVenue}</p>}
                  {item.dress_code && <p className="mt-2 break-words text-xs text-[#E8CA83]">Attire · {item.dress_code}</p>}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}

function ItinerarySection({ section, eventData, theme, layout }) {
  const items = itineraryItems(section, eventData);
  const botanicalCards = section.variant === 'botanical_cards';
  const partyProgram = section.variant === 'party_program';
  const gardenTimeline = section.variant === 'garden_timeline';
  const muhurthamProgram = section.variant === 'muhurtham_program' || section.variant === 'lamp_procession';
  const ceremonyCarousel = section.variant === 'ceremony_carousel';
  const ghatProgramme = section.variant === 'ghat_programme';
  const champagneProgramme = partyProgram && layout?.section_style === 'champagne_toast';

  if (champagneProgramme) {
    return (
      <section className="web-invite-toast-stationery relative overflow-hidden bg-[#F4E7D4] px-6 py-14 text-[#35151F]">
        <div className="mx-auto max-w-sm border-y border-[#B88732]/45 py-3 text-center">
          <p className="text-[0.58rem] font-semibold uppercase tracking-[0.26em] text-[#A56F20]">{sectionText(section, 'eyebrow', 'The dinner programme')}</p>
          <h2 className="mt-2 text-[2.7rem] font-normal leading-none text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', "Tonight's celebration")}</h2>
          <p className="mx-auto mt-3 max-w-xs text-sm leading-6 text-[#4B2931]/70">{sectionText(section, 'body', 'An evening arranged in courses, moments and music.')}</p>
        </div>
        <div className="relative mx-auto mt-9 max-w-sm pl-8">
          <span className="web-invite-toast-program-line absolute bottom-2 left-[11px] top-2 w-px origin-top bg-gradient-to-b from-[#E1C16C] via-[#A9711D] to-[#E1C16C]" aria-hidden="true" />
          {(items.length ? items : [{ name: 'Cocktails & welcome', start_time: 'Evening' }, { name: 'Dinner is served' }, { name: 'Music, memories & the birthday toast' }]).map((item, index) => {
            const itemVenue = venueLabel(item.venue);
            const timing = [readableDate(item.date), item.start_time].filter(Boolean).join(' / ');
            return (
              <article key={item.id || `${item.name}-${index}`} className="web-invite-toast-menu-item relative border-b border-[#B88732]/25 py-5 first:pt-1 last:border-b-0">
                <span className="absolute -left-[27px] top-[25px] h-[7px] w-[7px] rotate-45 border border-[#9D681A] bg-[#F4E7D4]" aria-hidden="true" />
                <p className="text-[0.55rem] font-semibold uppercase tracking-[0.2em] text-[#A56F20]">{timing || `Course ${String(index + 1).padStart(2, '0')}`}</p>
                <h3 className="mt-1 break-words text-[1.7rem] font-normal leading-tight text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{item.name || 'Celebration moment'}</h3>
                {itemVenue && <p className="mt-1 break-words text-sm text-[#4B2931]/65">{itemVenue}</p>}
                {item.dress_code && <p className="mt-1 text-xs italic text-[#4B2931]/55">Attire: {item.dress_code}</p>}
              </article>
            );
          })}
        </div>
      </section>
    );
  }

  if (partyProgram && !items.length) {
    const body = sectionText(section, 'body', 'Add the celebration plan in the supporting message.');
    return (
      <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
        <SectionHeading eyebrow="The celebration" title={sectionText(section, 'headline', 'Program highlights')} theme={theme} />
        <p className="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 opacity-80">
          {body}
        </p>
      </section>
    );
  }

  if (ghatProgramme) {
    return (
      <section className="relative overflow-hidden px-6 py-10">
        <SectionHeading eyebrow="The wedding days" title={sectionText(section, 'headline', 'A day by the Ganga')} body={sectionText(section, 'body', 'Each celebration finds its place along the steps.')} theme={theme} />
        {items.length ? <GhatProgramme items={items} theme={theme} /> : <div className="mx-auto mt-7 border-y px-5 py-9 text-center text-sm opacity-65" style={{ borderColor: alpha(theme.accent_color, 0.32) }}>Select itinerary items in the editor, or include the full itinerary.</div>}
        <RiverRippleMark color={theme.primary_color} className="pointer-events-none absolute -bottom-2 -right-10 h-20 w-56 opacity-[0.1]" />
      </section>
    );
  }

  if (muhurthamProgram) {
    return (
      <section className="px-6 py-10">
        <SectionHeading eyebrow="The wedding days" title={sectionText(section, 'headline', 'Muhūrtham programme')} body={sectionText(section, 'body', 'Choose a day to see each auspicious moment.')} theme={theme} />
        {items.length ? (
          <MuhurthamProgram items={items} theme={theme} />
        ) : <div className="mx-auto mt-7 border-y px-5 py-9 text-center text-sm opacity-65" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}>Select itinerary items in the editor, or include the full itinerary.</div>}
      </section>
    );
  }

  if (ceremonyCarousel) {
    return (
      <section className="overflow-hidden py-10">
        <div className="px-6"><SectionHeading eyebrow="Order of celebration" title={sectionText(section, 'headline', 'The royal programme')} body={sectionText(section, 'body', 'Swipe through each chapter of the day.')} theme={theme} /></div>
        {items.length ? (
          <CeremonyCarousel items={items} theme={theme} layout={layout} />
        ) : <div className="mx-6 mt-7 border-y px-5 py-9 text-center text-sm opacity-65" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}>Select itinerary items in the editor, or include the full itinerary.</div>}
      </section>
    );
  }

  if (gardenTimeline) {
    return (
      <section className="px-6" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
        <SectionHeading eyebrow="The celebration" title={sectionText(section, 'headline', 'Schedule of events')} body={sectionText(section, 'body', 'Every moment, all in one place.')} theme={theme} />
        {items.length ? (
          <CeremonyTimeline items={items} theme={theme} />
        ) : <Surface theme={theme} className="mx-auto mt-8 px-6 py-10 text-center text-sm opacity-65">Select itinerary items in the editor, or include the full itinerary.</Surface>}
      </section>
    );
  }
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
      <SectionHeading eyebrow="The celebration" title={sectionText(section, 'headline', 'Itinerary')} body={sectionText(section, 'body', 'Every moment, all in one place.')} theme={theme} />
       <div className={`${section.variant === 'cards' || botanicalCards ? 'grid' : 'flex flex-col'} mx-auto mt-8 max-w-3xl gap-4 ${section.variant === 'timeline' ? 'border-l-2 pl-4' : ''}`} style={section.variant === 'timeline' ? { borderColor: alpha(theme.accent_color, 0.45) } : undefined}>
        {items.length ? items.map((item, index) => {
          const itemVenue = venueLabel(item.venue);
          return (
          <Surface
            key={item.id || `${item.name}-${index}`}
            theme={theme}
             className={`${botanicalCards ? 'overflow-hidden border-double border-4 px-6 py-6' : section.variant === 'compact' ? 'border-0 border-b px-0 py-3' : section.variant === 'timeline' ? 'border-0 px-2 py-4' : 'px-5 py-5'} relative`}
             style={botanicalCards ? { background: 'linear-gradient(150deg, rgba(255,253,247,.98), rgba(242,224,205,.72))' } : section.variant === 'compact' || section.variant === 'timeline' ? { background: 'transparent', boxShadow: 'none', borderRadius: 0, borderColor: alpha(theme.secondary_color, 0.36) } : {}}
          >
            {botanicalCards && <span className="absolute -right-1 -top-2 text-4xl opacity-20" style={{ color: theme.accent_color }} aria-hidden="true">❧</span>}
            <div className="flex gap-4">
               <span className={`grid shrink-0 place-items-center rounded-full text-sm font-bold text-white ${section.variant === 'compact' ? 'h-7 w-7' : 'h-10 w-10'}`} style={{ background: botanicalCards ? `linear-gradient(145deg, ${theme.primary_color}, ${theme.accent_color})` : theme.primary_color }}>
                {index + 1}
              </span>
              <div className="min-w-0">
                <h3 className="text-[1.35rem]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{item.name || 'Celebration'}</h3>
                <p className="mt-1 text-sm uppercase tracking-[0.12em] opacity-65">
                  {[readableDate(item.date), item.start_time].filter(Boolean).join(' · ') || 'Timing to be announced'}
                </p>
                {itemVenue && <p className="mt-2 text-base opacity-75">{itemVenue}</p>}
                {item.dress_code && <p className="mt-2 text-sm opacity-60">Dress code: {item.dress_code}</p>}
              </div>
            </div>
          </Surface>
          );
        }) : (
          <Surface theme={theme} className="px-6 py-10 text-center text-sm opacity-65">
            Select itinerary items in the editor, or include the full itinerary.
          </Surface>
        )}
      </div>
    </section>
  );
}

function VenueSection({ section, eventData, theme }) {
  const venues = usesEventContent(section) ? (section.content?.venues || eventData.venues || []) : [];
  const estateFeature = section.variant === 'estate_feature';
  const templeInscriptions = section.variant === 'temple_inscriptions' || section.variant === 'mandapam_feature';
  const royalVenue = section.variant === 'royal_venue';
  const riverfrontPlaces = section.variant === 'riverfront_places';
  if (riverfrontPlaces) {
    return (
      <section className="relative overflow-hidden px-7 py-10">
        <SectionHeading eyebrow="Along the riverbank" title={sectionText(section, 'headline', 'Where we gather')} body={sectionText(section, 'body', 'The places where our families will celebrate together.')} theme={theme} />
        <div className="mx-auto mt-7 max-w-3xl">
          {venues.length ? venues.map((venue, index) => (
            <article key={venue.id || `${venue.name}-${index}`} className="relative py-6" style={{ marginLeft: `${Math.min(index * 14, 42)}px`, borderTop: `1px solid ${alpha(theme.accent_color, 0.34)}` }}>
              <div className="flex items-start gap-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-[0.72rem]" style={{ color: theme.accent_color, borderColor: alpha(theme.accent_color, 0.55), background: alpha(theme.secondary_color, 0.1) }}>{String(index + 1).padStart(2, '0')}</span>
                <div className="min-w-0">
                  <h3 className="break-words text-[1.8rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{venue.name || 'Celebration venue'}</h3>
                  <p className="mt-3 break-words text-sm leading-6 opacity-70">{venueAddress(venue) || 'Address to be announced'}</p>
                </div>
              </div>
            </article>
          )) : <div className="border-y py-8 text-center text-sm opacity-65" style={{ borderColor: alpha(theme.accent_color, 0.3) }}>Venue details will appear here from your event.</div>}
        </div>
        <RiverRippleMark color={theme.primary_color} className="mx-auto mt-2 h-10 w-36 opacity-35" />
      </section>
    );
  }
  if (templeInscriptions) {
    return (
      <section className="relative overflow-hidden px-7 py-10 text-center">
        <TempleKolamMark color={theme.secondary_color} className="pointer-events-none absolute -left-10 top-10 h-32 w-32 opacity-[0.07]" />
        <SectionHeading eyebrow="The sacred setting" title={sectionText(section, 'headline', 'Where we gather')} body={sectionText(section, 'body', 'The places where our families will celebrate together.')} theme={theme} />
        <div className="mx-auto mt-7 max-w-3xl border-y" style={{ borderColor: alpha(theme.secondary_color, 0.42) }}>
          {venues.length ? venues.map((venue, index) => (
            <div key={venue.id || `${venue.name}-${index}`} className="grid grid-cols-[3.2rem_1fr] gap-4 border-t py-6 text-left first:border-t-0" style={{ borderColor: alpha(theme.secondary_color, 0.26) }}>
              <span className="text-[2.35rem] leading-none opacity-40" style={{ color: theme.secondary_color, fontFamily: `${theme.heading_font}, serif` }}>{String(index + 1).padStart(2, '0')}</span>
              <div className="border-l pl-4" style={{ borderColor: alpha(theme.secondary_color, 0.42) }}>
                <h3 className="text-[1.8rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{venue.name || 'Celebration venue'}</h3>
                <p className="mt-3 text-sm leading-6 opacity-70">{venueAddress(venue) || 'Address to be announced'}</p>
              </div>
            </div>
          )) : <div className="py-8 text-center text-sm opacity-65">Venue details will appear here from your event.</div>}
        </div>
      </section>
    );
  }
  if (royalVenue) {
    return (
      <section className="px-7 py-10">
        <SectionHeading eyebrow="The setting" title={sectionText(section, 'headline', 'Where the chapter unfolds')} body={sectionText(section, 'body', 'The places where we will celebrate together.')} theme={theme} />
        <div className="mx-auto mt-7 max-w-3xl">
          {venues.length ? venues.map((venue, index) => (
            <div key={venue.id || `${venue.name}-${index}`} className="grid grid-cols-[3rem_1fr] gap-4 border-t py-6 first:border-t-0" style={{ borderColor: alpha(theme.secondary_color, 0.32) }}>
              <span className="text-[2.3rem] leading-none opacity-45" style={{ color: theme.accent_color, fontFamily: `${theme.heading_font}, serif` }}>{String(index + 1).padStart(2, '0')}</span>
              <div><h3 className="text-[1.8rem] leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{venue.name || 'Celebration venue'}</h3><p className="mt-3 text-sm leading-6 opacity-70">{venueAddress(venue) || 'Address to be announced'}</p></div>
            </div>
          )) : <div className="border-y py-8 text-center text-sm opacity-65" style={{ borderColor: alpha(theme.secondary_color, 0.32) }}>Venue details will appear here from your event.</div>}
        </div>
      </section>
    );
  }
  return (
     <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing], background: estateFeature || section.variant === 'minimal' ? 'transparent' : alpha(theme.primary_color, 0.055) }}>
      <SectionHeading eyebrow="Find your way" title={sectionText(section, 'headline', 'Venues')} body={sectionText(section, 'body', 'The places where we will celebrate together.')} theme={theme} />
      <div className="mx-auto mt-5 grid max-w-3xl grid-cols-1 gap-3">
        {venues.length ? venues.map((venue, index) => (
           <Surface key={venue.id || `${venue.name}-${index}`} theme={theme} className={`${estateFeature ? 'border-0 p-3 text-center' : section.variant === 'minimal' ? 'border-0 border-b px-0 py-5 text-center' : section.variant === 'feature' ? 'border-2 p-7 text-center' : 'p-5'}`} style={estateFeature || section.variant === 'minimal' ? { background: 'transparent', borderColor: section.variant === 'minimal' ? alpha(theme.secondary_color, 0.5) : 'transparent', borderRadius: 0, boxShadow: 'none' } : section.variant === 'feature' ? { background: alpha(theme.secondary_color, 0.22), borderColor: alpha(theme.accent_color, 0.5) } : {}}>
             <MapPin className={`web-invite-scroll-drift ${estateFeature || section.variant === 'feature' || section.variant === 'minimal' ? 'mx-auto h-8 w-8' : 'h-6 w-6'}`} weight="duotone" style={{ color: theme.accent_color }} />
             <h3 className={`${estateFeature || section.variant === 'feature' ? 'mt-5 text-3xl' : 'mt-4 text-xl'}`} style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>{venue.name || 'Celebration venue'}</h3>
            <p className={`${estateFeature ? 'mx-auto max-w-xl' : ''} mt-3 text-base leading-7 opacity-70`}>{venueAddress(venue) || 'Address to be announced'}</p>
          </Surface>
        )) : <Surface theme={theme} className="p-8 text-center text-sm opacity-65">Venue details will appear here from your event.</Surface>}
      </div>
    </section>
  );
}

function selectedMapVenue(section, eventData) {
  const isBirthday = eventData?.event_type === 'birthday';
  const customVenue = section.overrides?.venue;
  if (!isBirthday && customVenue && (venueLabel(customVenue) || venueAddress(customVenue))) return customVenue;
  const hydrated = eventSource(section, eventData).venue;
  if (hydrated) return hydrated;
  if (!usesEventContent(section)) return null;
  const venues = eventData.venues || [];
  const selectedKey = String(section.settings?.selected_venue_id || '');
  if (selectedKey.startsWith('index:')) {
    const index = Number(selectedKey.split(':')[1]);
    if (Number.isInteger(index) && venues[index]) return venues[index];
  }
  if (selectedKey) {
    const selected = venues.find((venue) => String(venue.id || '') === selectedKey);
    if (selected) return selected;
  }
  return venues[0] || null;
}

function VenueMapSection({ section, eventData, theme, layout }) {
  const venue = selectedMapVenue(section, eventData);
  const address = venueAddress(venue);
  const fullAddress = venueFullAddress(venue);
  const coordinates = venue?.lat != null && venue?.lng != null ? `${venue.lat},${venue.lng}` : '';
  const query = coordinates || address || venueLabel(venue);
  const mapUrl = query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : '';
  const directionsUrl = query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
  const kolamMap = section.variant === 'kolam_map' || section.variant === 'brass_map';
  const gildedMap = section.variant === 'gilded_map';
  const riverBendMap = section.variant === 'river_bend_map';
  const champagneMap = section.variant === 'paper_map' && layout?.section_style === 'champagne_toast';
  if (champagneMap) {
    return (
      <section className="bg-[#F4E7D4] px-6 py-14 text-[#35151F]">
        <p className="text-center text-[0.58rem] font-semibold uppercase tracking-[0.26em] text-[#A56F20]">The way to the table</p>
        <h2 className="mt-2 text-center text-[2.55rem] font-normal leading-none text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', 'Find the celebration')}</h2>
        <p className="mx-auto mt-3 max-w-xs text-center text-sm leading-6 text-[#4B2931]/68">{sectionText(section, 'body', venue ? venueLabel(venue) : 'Choose an event location in the editor.')}</p>
        {mapUrl ? (
          <div className="mx-auto mt-7 max-w-sm">
            <div className="relative overflow-hidden border border-[#B88732] bg-[#FFF9EE] p-2 shadow-[0_18px_38px_rgba(67,22,35,0.16)]">
              <iframe title={`Map to ${venueLabel(venue) || 'event venue'}`} src={mapUrl} className="h-64 w-full border-0 grayscale-[0.32] sepia-[0.18]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              <div className="flex items-center justify-between gap-3 border-t border-[#B88732]/35 bg-[#FFF9EE] px-3 py-3">
                <div className="min-w-0"><p className="truncate text-lg text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{venue?.name || venue?.hall_name || 'Celebration venue'}</p>{address && <p className="truncate text-[0.66rem] text-[#4B2931]/60">{address}</p>}</div>
                <a href={directionsUrl} target="_blank" rel="noreferrer" className="shrink-0 border-b border-[#A56F20] px-1 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#741A3A]">Directions</a>
              </div>
            </div>
            {fullAddress && <p className="mt-4 text-center text-xs leading-5 text-[#4B2931]/65">{fullAddress}</p>}
          </div>
        ) : <div className="mx-auto mt-7 max-w-sm border-y border-[#B88732]/35 py-8 text-center text-sm text-[#4B2931]/60">The selected event location will appear here.</div>}
      </section>
    );
  }
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
      <SectionHeading eyebrow="The way there" title={sectionText(section, 'headline', 'Find the celebration')} body={sectionText(section, 'body', venue ? venueLabel(venue) : 'Choose an event location in the editor.')} theme={theme} />
      {mapUrl ? (
        <div className="mt-5">
          <div
            className={`relative overflow-hidden ${section.variant === 'full_bleed' ? '-mx-5' : kolamMap ? 'border-[6px] border-double' : gildedMap ? 'border-[6px] border-double' : riverBendMap ? 'border-2' : 'rounded-[1.5rem]'}`}
            style={{ boxShadow: `0 16px 42px ${alpha(theme.primary_color, 0.14)}`, borderColor: kolamMap || gildedMap ? alpha(theme.secondary_color, 0.78) : riverBendMap ? alpha(theme.primary_color, 0.46) : undefined, borderRadius: riverBendMap ? '46% 12% 42% 10% / 12% 38% 14% 34%' : undefined }}
          >
            {kolamMap && <TempleKolamMark color={theme.secondary_color} className="pointer-events-none absolute -right-9 -top-9 z-[2] h-28 w-28 rounded-full bg-[#F4E8CE]/90 p-5 opacity-90" />}
            {riverBendMap && <RiverRippleMark color="#FFFFFF" className="pointer-events-none absolute -right-6 top-3 z-[2] h-16 w-44 opacity-55" />}
            <iframe
              title={`Map to ${venueLabel(venue) || 'event venue'}`}
              src={mapUrl}
              className="h-64 w-full border-0 grayscale-[0.18]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-4 pb-4 pt-12 text-white">
              <div className="min-w-0"><p className="truncate text-lg" style={{ fontFamily: `${theme.heading_font}, cursive` }}>{venue?.name || venue?.hall_name || 'Celebration venue'}</p>{address && <p className="mt-0.5 line-clamp-1 text-xs text-white/80">{address}</p>}</div>
              <a href={directionsUrl} target="_blank" rel="noreferrer" className="shrink-0 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold" style={{ color: theme.primary_color }}>Directions</a>
            </div>
          </div>
          {fullAddress && <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-6 opacity-75">{fullAddress}</p>}
        </div>
      ) : (
        <div className="mt-5 py-8 text-center text-sm opacity-60">The selected event location will appear here.</div>
      )}
    </section>
  );
}

function TempleSlideshow({ images, theme, sampleImages = false }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(0, images.length - 1)));
  }, [images.length]);

  const showImage = (nextIndex) => {
    if (!images.length) return;
    setActiveIndex((nextIndex + images.length) % images.length);
  };

  return (
    <div className="relative -mx-5 mt-7 overflow-hidden bg-black" role="region" aria-label="Wedding photographs">
      <figure className="relative aspect-[4/5] overflow-hidden">
        <img key={images[activeIndex]} src={resolveMediaUrl(images[activeIndex])} alt={sampleImages ? `Sample wedding photograph ${activeIndex + 1}` : `Wedding memory ${activeIndex + 1}`} className="web-invite-reveal-fade h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/65" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 px-6 pb-6 text-white">
          <div>
            <p className="text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-white/70">{sampleImages ? 'Sample imagery' : 'From our story'}</p>
            <p className="mt-1 text-[1.65rem] leading-none" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sampleImages ? 'Your moments go here' : 'A moment we keep'}</p>
          </div>
          <p className="text-[0.68rem] font-semibold tracking-[0.16em] text-[#F2D28A]">{String(activeIndex + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}</p>
        </div>
      </figure>
      <div className="flex items-center justify-between px-6 py-4" style={{ background: theme.primary_color }}>
        <button type="button" onClick={() => showImage(activeIndex - 1)} disabled={images.length < 2} className="grid h-11 w-11 place-items-center border text-[#FFF8E8] disabled:opacity-30" style={{ borderColor: alpha(theme.secondary_color, 0.64) }} aria-label="Previous photograph"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex max-w-[190px] gap-1.5" aria-hidden="true">
          {images.map((url, index) => <span key={`${url}-${index}`} className="h-1 transition-[width,background-color]" style={{ width: index === activeIndex ? '24px' : '8px', background: index === activeIndex ? theme.secondary_color : alpha('#FFFFFF', 0.32) }} />)}
        </div>
        <button type="button" onClick={() => showImage(activeIndex + 1)} disabled={images.length < 2} className="grid h-11 w-11 place-items-center border text-[#FFF8E8] disabled:opacity-30" style={{ borderColor: alpha(theme.secondary_color, 0.64) }} aria-label="Next photograph"><ArrowRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function GallerySection({ section, eventData, theme, layout }) {
  const filmstripRef = useRef(null);
  const [memoryIndex, setMemoryIndex] = useState(0);
  const customImages = Array.isArray(section.overrides?.image_urls) ? section.overrides.image_urls : [];
  const hydratedImages = Array.isArray(section.content?.images) ? section.content.images : null;
  const eventImages = usesEventContent(section) ? (eventData.wedding_photos_preview || []) : [];
  const selectedImages = new Set(section.settings?.selected_image_urls || []);
  const contentImages = hydratedImages || (section.settings?.include_all_images === false
    ? eventImages.filter((url) => selectedImages.has(url))
    : eventImages);
  const heroImages = usesEventContent(section) ? (eventData.guest_experience?.hero_media || []).filter((item) => item.url).map((item) => item.url) : [];
  const useHeroFallback = section.settings?.include_all_images !== false && !contentImages.length;
  const eventOrCustomImages = [...customImages, ...contentImages, ...(useHeroFallback && !customImages.length ? heroImages : [])].filter(Boolean).slice(0, 6);
  const sampleImages = !eventOrCustomImages.length && section.settings?.include_all_images !== false;
  const images = sampleImages ? SAMPLE_COUPLE_IMAGES : eventOrCustomImages;
  const gardenCameos = section.variant === 'garden_cameos';
  const templeSlideshow = section.variant === 'temple_slideshow' || section.variant === 'temple_windows';
  const tapestryMasonry = section.variant === 'tapestry_masonry';
  const riverEditorial = section.variant === 'river_editorial';
  const memoryWall = section.variant === 'memory_wall';
  const filmstrip = section.variant === 'filmstrip';
  const editorialGallery = section.variant === 'editorial';
  const champagneMemoryTable = memoryWall && layout?.section_style === 'champagne_toast';
  const memoryCount = images.length;

  useEffect(() => {
    setMemoryIndex((current) => Math.min(current, Math.max(0, memoryCount - 1)));
  }, [memoryCount]);

  const showMemory = (nextIndex) => {
    if (!memoryCount) return;
    setMemoryIndex((nextIndex + memoryCount) % memoryCount);
  };

  if (champagneMemoryTable) {
    const photoPositions = [
      'left-[5%] top-10 -rotate-[6deg]',
      'right-[4%] top-24 rotate-[5deg]',
      'left-[19%] top-[190px] rotate-[2deg]',
      'right-[12%] top-[245px] -rotate-[4deg]',
    ];
    return (
      <section className="web-invite-memory-table relative isolate overflow-hidden bg-[#17090D] px-5 py-14 text-[#FFF8EB]">
        <div className="absolute inset-0 -z-[2] bg-[radial-gradient(circle_at_24%_14%,rgba(157,51,77,.24),transparent_28%),radial-gradient(circle_at_86%_72%,rgba(202,157,72,.14),transparent_26%),linear-gradient(135deg,#16090d,#35101c_58%,#12070a)]" aria-hidden="true" />
        <div className="absolute inset-0 -z-[1] opacity-20" style={{ backgroundImage: 'repeating-linear-gradient(94deg, rgba(255,255,255,.04) 0, rgba(255,255,255,.04) 1px, transparent 1px, transparent 7px)' }} aria-hidden="true" />
        <p className="text-center text-[0.58rem] font-semibold uppercase tracking-[0.26em] text-[#E9C36A]">{sectionText(section, 'eyebrow', 'The memory table')}</p>
        <h2 className="mx-auto mt-2 max-w-sm text-center text-[2.7rem] font-normal leading-none" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', 'Memories from the years')}</h2>
        <p className="mx-auto mt-3 max-w-xs text-center text-sm leading-6 text-white/62">{sectionText(section, 'body', 'Photographs gathered like favorite stories around the table.')}</p>
        {images.length ? (
          <div className="relative mx-auto mt-6 h-[430px] max-w-sm" role="region" aria-label="Birthday memory table">
            {images.map((url, index) => {
              const active = index === memoryIndex;
              return (
                <button key={`${url}-${index}`} type="button" onClick={() => setMemoryIndex(index)} aria-label={`Focus birthday memory ${index + 1}`} className={`web-invite-memory-print absolute w-[58%] bg-[#FFF9EE] p-2 pb-7 shadow-[0_18px_32px_rgba(0,0,0,.48)] transition-[transform,filter,opacity] duration-500 ${photoPositions[index % photoPositions.length]} ${active ? 'z-[5] !left-1/2 !top-12 !-translate-x-1/2 !rotate-0 scale-110' : 'z-[2] opacity-75 saturate-[.72]'}`}>
                  <img src={resolveMediaUrl(url)} alt={sampleImages ? `Sample birthday memory ${index + 1}` : `Birthday memory ${index + 1}`} className="aspect-[4/5] w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-2 text-center text-[0.5rem] font-semibold uppercase tracking-[0.16em] text-[#5B2935]/58">Memory {String(index + 1).padStart(2, '0')}</span>
                </button>
              );
            })}
            <div className="absolute inset-x-0 bottom-0 z-[8] flex items-center justify-center gap-5">
              <button type="button" onClick={() => showMemory(memoryIndex - 1)} disabled={memoryCount < 2} className="grid h-10 w-10 place-items-center rounded-full border border-[#E9C36A]/60 bg-[#270C15]/90 text-[#F5D681] disabled:opacity-30" aria-label="Previous birthday memory"><ArrowLeft className="h-4 w-4" /></button>
              <span className="min-w-[72px] text-center text-[0.62rem] font-semibold tracking-[0.18em] text-white/58" aria-live="polite">{String(memoryIndex + 1).padStart(2, '0')} / {String(memoryCount).padStart(2, '0')}</span>
              <button type="button" onClick={() => showMemory(memoryIndex + 1)} disabled={memoryCount < 2} className="grid h-10 w-10 place-items-center rounded-full border border-[#E9C36A]/60 bg-[#270C15]/90 text-[#F5D681] disabled:opacity-30" aria-label="Next birthday memory"><ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        ) : <div className="mx-auto mt-8 max-w-sm border-y border-[#E9C36A]/35 py-10 text-center"><Camera className="mx-auto h-8 w-8 text-[#E9C36A]/60" /><p className="mt-3 text-sm text-white/60">Birthday photos added to the event will appear here.</p></div>}
        {sampleImages && <p className="mt-3 text-center text-xs text-white/48">Sample imagery - add your own photos to personalize this invite.</p>}
      </section>
    );
  }
  if (memoryWall) {
    return (
      <section className="px-5 py-12">
        <SectionHeading eyebrow="Memory wall" title={sectionText(section, 'headline', 'A few favorite memories')} body={sectionText(section, 'body', 'Photos, laughter and little moments from the years we are celebrating.')} theme={theme} />
        {images.length ? (
          <div className="mx-auto mt-7 grid max-w-sm grid-cols-2 gap-3">
            {images.map((url, index) => (
              <figure key={`${url}-${index}`} className={`${index === 0 ? 'col-span-2' : ''} rotate-[-1deg] rounded-sm border bg-white p-2 shadow-xl odd:rotate-[1.5deg]`} style={{ borderColor: alpha(theme.secondary_color, 0.3) }}>
                <img src={resolveMediaUrl(url)} alt={sampleImages ? `Sample birthday memory ${index + 1}` : `Birthday memory ${index + 1}`} className={`${index === 0 ? 'aspect-[5/3]' : 'aspect-square'} w-full object-cover`} />
                <figcaption className="px-1 pb-1 pt-2 text-left text-[0.58rem] uppercase tracking-[0.15em] opacity-55">{String(index + 1).padStart(2, '0')} / Memory</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <Surface theme={theme} className="mx-auto mt-8 grid max-w-3xl place-items-center p-12 text-center">
            <Camera className="h-8 w-8 opacity-40" /><p className="mt-3 text-sm opacity-60">Birthday photos added to the event will appear here.</p>
          </Surface>
        )}
        {sampleImages && <p className="mt-3 text-center text-xs opacity-60">Sample imagery - add your own photos to personalize this invite.</p>}
      </section>
    );
  }
  if (riverEditorial) {
    return (
      <section className="overflow-hidden px-5 py-10">
        <SectionHeading eyebrow="What the river remembers" title={sectionText(section, 'headline', 'Moments carried with us')} body={sectionText(section, 'body', 'Portraits and pauses from the journey so far.')} theme={theme} />
        {images.length ? (
          <div className="mt-7">
            {images.map((url, index) => (
              <figure key={`${url}-${index}`} className={`relative ${index ? 'mt-4' : ''} ${index % 3 === 0 ? '-mx-5' : index % 3 === 1 ? 'ml-8 -mr-2' : '-ml-2 mr-8'}`}>
                <div className="overflow-hidden" style={{ borderRadius: index % 3 === 0 ? 0 : index % 3 === 1 ? '46% 8% 8% 12% / 18% 8% 10% 24%' : '8% 44% 14% 8% / 8% 20% 26% 10%' }}>
                  <img src={resolveMediaUrl(url)} alt={sampleImages ? `Sample wedding photograph ${index + 1}` : `Wedding memory ${index + 1}`} className={`${index % 3 === 0 ? 'aspect-[4/5]' : 'aspect-[5/6]'} web-invite-scroll-gallery h-full w-full object-cover`} />
                </div>
                <figcaption className="mt-2 flex items-center gap-3 text-[0.58rem] uppercase tracking-[0.18em] opacity-55"><span>{String(index + 1).padStart(2, '0')}</span><span className="h-px flex-1 bg-current opacity-35" /><span>{index % 2 ? 'A quiet pause' : 'From our story'}</span></figcaption>
              </figure>
            ))}
            <RiverRippleMark color={theme.primary_color} className="mx-auto mt-5 h-12 w-44 opacity-35" />
          </div>
        ) : <div className="mx-auto mt-7 border-y py-10 text-center" style={{ borderColor: alpha(theme.accent_color, 0.3) }}><Camera className="mx-auto h-8 w-8 opacity-40" /><p className="mt-3 text-sm opacity-60">Photos added to the event will appear here.</p></div>}
        {sampleImages && <p className="mt-3 text-center text-xs opacity-60">Sample imagery — add your own photos to personalize this invite.</p>}
      </section>
    );
  }
  if (templeSlideshow) {
    return (
      <section className="overflow-hidden px-5 py-10">
        <SectionHeading eyebrow="Portraits before the vows" title={sectionText(section, 'headline', 'The two of us')} body={sectionText(section, 'body', 'A few memories before the wedding days begin.')} theme={theme} />
        {images.length ? <TempleSlideshow images={images} theme={theme} sampleImages={sampleImages} /> : <div className="mx-auto mt-7 border-y py-10 text-center" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}><Camera className="mx-auto h-8 w-8 opacity-40" /><p className="mt-3 text-sm opacity-60">Photos added to the event will appear here.</p></div>}
      </section>
    );
  }
  if (tapestryMasonry) {
    return (
      <section className="px-5 py-10">
        <SectionHeading eyebrow="The portrait room" title={sectionText(section, 'headline', 'Portraits from our story')} body={sectionText(section, 'body', 'Collected moments, arranged like a family folio.')} theme={theme} />
        {images.length ? (
          <div className="mx-auto mt-7 columns-2 gap-3">
            {images.map((url, index) => (
              <figure key={`${url}-${index}`} className="mb-3 break-inside-avoid overflow-hidden border-4 bg-white p-1 shadow-lg" style={{ borderColor: index % 3 === 0 ? alpha(theme.secondary_color, 0.78) : alpha(theme.primary_color, 0.22) }}>
                <img src={resolveMediaUrl(url)} alt={sampleImages ? `Sample wedding photograph ${index + 1}` : 'Celebration'} className={`${index % 3 === 1 ? 'aspect-[4/5]' : index % 3 === 2 ? 'aspect-square' : 'aspect-[3/4]'} w-full object-cover`} />
              </figure>
            ))}
          </div>
        ) : <div className="mx-auto mt-7 border-y py-10 text-center" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}><Camera className="mx-auto h-8 w-8 opacity-40" /><p className="mt-3 text-sm opacity-60">Photos added to the event will appear here.</p></div>}
        {sampleImages && <p className="mt-3 text-center text-xs opacity-60">Sample imagery — add your own photos to personalize this invite.</p>}
      </section>
    );
  }
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
      <SectionHeading eyebrow="Memories" title={sectionText(section, 'headline', 'Our gallery')} body={sectionText(section, 'body', 'A few moments close to our hearts.')} theme={theme} />
      {images.length ? (
         <div ref={filmstripRef} className={`${filmstrip ? 'web-invite-horizontal-scroll flex snap-x snap-mandatory overflow-x-auto pb-4' : editorialGallery ? 'flex flex-col' : 'grid grid-cols-2'} mx-auto mt-5 max-w-4xl ${gardenCameos ? 'gap-3' : 'gap-2'}`}>
          {images.map((url, index) => (
            <img
              key={`${url}-${index}`}
              src={resolveMediaUrl(url)}
              alt={sampleImages ? `Sample wedding photograph ${index + 1}` : 'Celebration'}
               className={`web-invite-scroll-gallery ${index % 2 ? 'web-invite-scroll-gallery-reverse' : ''} ${filmstrip ? 'aspect-[3/4] w-[76%] shrink-0 snap-center' : editorialGallery ? 'aspect-[4/5] w-full' : index === 0 && section.variant === 'mosaic' ? 'col-span-2 row-span-2 h-full w-full' : gardenCameos ? 'aspect-[3/4] w-full' : 'aspect-square w-full'} border-4 object-cover shadow-lg`}
              style={{ borderColor: gardenCameos ? alpha(theme.secondary_color, 0.52) : 'transparent', borderRadius: gardenCameos ? '48% 48% 16px 16px' : RADIUS[theme.corner_style] }}
            />
          ))}
         </div>
      ) : (
        <Surface theme={theme} className="mx-auto mt-8 grid max-w-3xl place-items-center p-12 text-center">
          <Camera className="h-8 w-8 opacity-40" /><p className="mt-3 text-sm opacity-60">Photos added to the event will appear here.</p>
        </Surface>
      )}
      {filmstrip && images.length > 1 && <div className="mt-3 flex justify-center gap-4"><button type="button" aria-label="Previous photo" onClick={() => filmstripRef.current?.scrollBy({ left: -filmstripRef.current.clientWidth * 0.78, behavior: 'smooth' })} className="rounded-full border p-2" style={{ borderColor: theme.accent_color }}><ArrowLeft className="h-5 w-5" /></button><button type="button" aria-label="Next photo" onClick={() => filmstripRef.current?.scrollBy({ left: filmstripRef.current.clientWidth * 0.78, behavior: 'smooth' })} className="rounded-full border p-2" style={{ borderColor: theme.accent_color }}><ArrowRight className="h-5 w-5" /></button></div>}
      {sampleImages && <p className="mt-3 text-center text-xs opacity-60">Sample imagery — add your own photos to personalize this invite.</p>}
    </section>
  );
}

function WishesSection({ section, eventData, theme, layout, preview, onSubmitWish, wishAuthorName }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [wishIndex, setWishIndex] = useState(0);
  const wishes = section.content?.items || (usesEventContent(section) ? eventData.guest_testimonials || [] : []);
  const activeWishIndex = wishes.length ? wishIndex % wishes.length : 0;
  const activeWish = wishes[activeWishIndex];
  const wishPager = wishes.length > 1 && (
    <div className="mt-4 flex items-center justify-center gap-4" aria-label="Guest wishes navigation">
      <button
        type="button"
        aria-label="Previous wish"
        onClick={() => setWishIndex((current) => (current - 1 + wishes.length) % wishes.length)}
        className="rounded-full border p-2 transition-transform hover:scale-105"
        style={{ borderColor: theme.accent_color }}
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <span className="min-w-16 text-center text-xs tabular-nums opacity-60" aria-live="polite">
        {activeWishIndex + 1} of {wishes.length}
      </span>
      <button
        type="button"
        aria-label="Next wish"
        onClick={() => setWishIndex((current) => (current + 1) % wishes.length)}
        className="rounded-full border p-2 transition-transform hover:scale-105"
        style={{ borderColor: theme.accent_color }}
      >
        <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
  const wishForm = !preview && onSubmitWish && (
    <form className="mx-auto mt-7 max-w-xl space-y-3 rounded-xl border p-5" style={{ borderColor: alpha(theme.secondary_color, 0.35) }} onSubmit={async (event) => {
      event.preventDefault();
      setSubmitting(true);
      setSubmitError('');
      try {
        await onSubmitWish({ name: wishAuthorName ? undefined : name.trim(), message: message.trim() });
        setName('');
        setMessage('');
      } catch (error) {
        setSubmitError(error?.response?.data?.detail || 'Could not send your wish. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }}>
      <p className="text-center font-semibold">Leave a wish</p>
      {wishAuthorName ? <p className="text-center text-sm opacity-70">Posting as {wishAuthorName}</p> : (
        <label className="block text-sm">Your name<input className="mt-1 w-full rounded-md border bg-white p-3 text-stone-900" value={name} onChange={(event) => setName(event.target.value)} maxLength={100} required /></label>
      )}
      <label className="block text-sm">Your wish<textarea className="mt-1 w-full rounded-md border bg-white p-3 text-stone-900" value={message} onChange={(event) => setMessage(event.target.value)} maxLength={1000} minLength={2} rows={3} required /></label>
      {submitError && <p role="alert" className="text-sm text-red-700">{submitError}</p>}
      <button type="submit" disabled={submitting} className="w-full rounded-md px-4 py-3 font-semibold text-white disabled:opacity-60" style={{ background: theme.primary_color }}>{submitting ? 'Sending…' : 'Send wish'}</button>
    </form>
  );
  const quote = section.variant === 'quote';
  const loveLetters = section.variant === 'love_letters';
  const blessingChorus = section.variant === 'blessing_chorus' || section.variant === 'blessing_scrolls';
  const courtlyNotes = section.variant === 'courtly_notes';
  const rippleBlessings = section.variant === 'ripple_blessings';
  const champagneToasts = section.variant === 'toast_notes' && layout?.section_style === 'champagne_toast';
  if (champagneToasts) {
    return (
      <section className="web-invite-toast-night relative isolate overflow-hidden bg-[#300B18] px-6 py-14 text-[#FFF8EB]">
        <div className="absolute inset-0 -z-[1] bg-[radial-gradient(circle_at_18%_18%,rgba(190,86,112,.2),transparent_24%),linear-gradient(180deg,#300b18,#16080d)]" aria-hidden="true" />
        {Array.from({ length: 12 }, (_, index) => <span key={index} className="web-invite-wish-bubble pointer-events-none absolute rounded-full border border-[#E9C36A]/35" style={{ left: `${4 + ((index * 23) % 92)}%`, bottom: `${3 + ((index * 17) % 88)}%`, width: `${8 + (index % 4) * 6}px`, height: `${8 + (index % 4) * 6}px`, animationDelay: `${index * 160}ms` }} aria-hidden="true" />)}
        <div className="relative mx-auto max-w-sm text-center">
          <p className="text-[0.58rem] font-semibold uppercase tracking-[0.26em] text-[#E9C36A]">{sectionText(section, 'eyebrow', 'Words worth keeping')}</p>
          <h2 className="mt-2 text-[2.75rem] font-normal leading-none" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', 'Raise a glass')}</h2>
          <p className="mx-auto mt-3 max-w-xs text-sm leading-6 text-white/62">{sectionText(section, 'body', 'Birthday wishes from the people who make every year brighter.')}</p>
          <div className="relative mt-8 min-h-[245px]">
            <span className="pointer-events-none absolute left-1/2 top-1/2 h-52 w-52 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#E9C36A]/28 shadow-[inset_0_0_45px_rgba(233,195,106,.08),0_0_0_12px_rgba(233,195,106,.03)]" aria-hidden="true" />
            {activeWish ? (
              <blockquote key={activeWish.id || activeWishIndex} className="web-invite-toast-wish relative flex min-h-[245px] flex-col items-center justify-center px-9 py-7">
                <p className="text-[1.45rem] italic leading-8 text-[#FFF9ED]" style={{ fontFamily: `${theme.heading_font}, serif` }}>&ldquo;{activeWish.message}&rdquo;</p>
                <footer className="mt-5 text-[0.58rem] font-semibold uppercase tracking-[0.2em] text-[#E9C36A]">{activeWish.guest_name || activeWish.name || 'A guest'}</footer>
              </blockquote>
            ) : <div className="relative flex min-h-[245px] items-center justify-center px-10 text-base text-white/60">Be the first to raise a glass.</div>}
          </div>
          {wishes.length > 1 && <div className="mt-2 flex items-center justify-center gap-5" aria-label="Guest wishes navigation">
            <button type="button" aria-label="Previous wish" onClick={() => setWishIndex((current) => (current - 1 + wishes.length) % wishes.length)} className="grid h-10 w-10 place-items-center rounded-full border border-[#E9C36A]/55 text-[#F5D681]"><ArrowLeft className="h-4 w-4" /></button>
            <span className="min-w-16 text-center text-[0.62rem] tracking-[0.16em] text-white/55" aria-live="polite">{activeWishIndex + 1} of {wishes.length}</span>
            <button type="button" aria-label="Next wish" onClick={() => setWishIndex((current) => (current + 1) % wishes.length)} className="grid h-10 w-10 place-items-center rounded-full border border-[#E9C36A]/55 text-[#F5D681]"><ArrowRight className="h-4 w-4" /></button>
          </div>}
          <div className="text-left">{wishForm}</div>
        </div>
      </section>
    );
  }
  if (rippleBlessings) {
    return (
      <section className="relative overflow-hidden px-7 py-10">
        <SectionHeading eyebrow="Across the water" title={sectionText(section, 'headline', 'Blessings across the water')} body={sectionText(section, 'body', 'Words from the people who have carried us here.')} theme={theme} />
        <div className="mx-auto mt-6 max-w-3xl">
          {activeWish ? (
            <blockquote key={activeWish.id || activeWishIndex} className="relative min-h-40 overflow-hidden border-y px-4 py-6 text-center animate-in fade-in-0 duration-300" style={{ borderColor: alpha(theme.primary_color, 0.2) }}>
              <span className="pointer-events-none absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border opacity-[0.08]" style={{ borderColor: theme.primary_color, boxShadow: `0 0 0 18px ${alpha(theme.primary_color, 0.16)}, 0 0 0 36px ${alpha(theme.primary_color, 0.1)}` }} aria-hidden="true" />
              <p className="relative text-[1.3rem] italic leading-8" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>“{activeWish.message}”</p>
              <footer className="relative mt-3 text-[0.62rem] font-semibold uppercase tracking-[0.16em] opacity-55">— {activeWish.guest_name || activeWish.name || 'A guest'}</footer>
            </blockquote>
          ) : <div className="border-y py-8 text-center text-base opacity-65" style={{ borderColor: alpha(theme.primary_color, 0.25) }}>Be the first to leave a wish.</div>}
          {wishPager}
        </div>
        {wishForm}
      </section>
    );
  }
  if (blessingChorus) {
    return (
      <section className="relative overflow-hidden px-7 py-10">
        <TempleKolamMark color={theme.secondary_color} className="pointer-events-none absolute -bottom-12 -right-12 h-40 w-40 opacity-[0.07]" />
        <SectionHeading eyebrow="Voices of our families" title={sectionText(section, 'headline', 'Blessings carried with us')} body={sectionText(section, 'body', 'Words from the people who made us who we are.')} theme={theme} />
        <div className="mx-auto mt-6 max-w-3xl">
          {activeWish ? (
            <blockquote key={activeWish.id || activeWishIndex} className="min-h-40 border-y py-5 text-center animate-in fade-in-0 duration-300" style={{ borderColor: alpha(theme.secondary_color, 0.3) }}>
              <p className="text-[1.35rem] italic leading-8" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>“{activeWish.message}”</p>
              <footer className="mt-3 text-[0.64rem] font-semibold uppercase tracking-[0.16em] opacity-55">— {activeWish.guest_name || activeWish.name || 'A guest'}</footer>
            </blockquote>
          ) : <div className="border-y py-8 text-center text-base opacity-65" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}>Be the first to leave a wish.</div>}
          {wishPager}
        </div>
        {wishForm}
      </section>
    );
  }
  if (courtlyNotes) {
    return (
      <section className="px-7 py-10">
        <SectionHeading eyebrow="From our people" title={sectionText(section, 'headline', 'Notes from our court')} body={sectionText(section, 'body', 'Words we will keep long after the celebration.')} theme={theme} />
        <div className="mx-auto mt-6 max-w-3xl">
          {activeWish ? (
            <blockquote key={activeWish.id || activeWishIndex} className="min-h-40 border-y py-5 text-center animate-in fade-in-0 duration-300" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}>
              <p className="text-[1.35rem] italic leading-8" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, serif` }}>“{activeWish.message}”</p>
              <footer className="mt-3 text-[0.65rem] font-semibold uppercase tracking-[0.16em] opacity-55">— {activeWish.guest_name || activeWish.name || 'A guest'}</footer>
            </blockquote>
          ) : <div className="border-y py-8 text-center text-base opacity-65" style={{ borderColor: alpha(theme.secondary_color, 0.35) }}>Be the first to leave a wish.</div>}
          {wishPager}
        </div>
        {wishForm}
      </section>
    );
  }
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing], background: loveLetters ? 'transparent' : alpha(theme.secondary_color, 0.12) }}>
      <SectionHeading eyebrow="With warm wishes" title={sectionText(section, 'headline', 'Love from our people')} body={sectionText(section, 'body', 'Messages from friends and family.')} theme={theme} />
      <div className="mx-auto mt-5 max-w-3xl">
        {activeWish ? (
          loveLetters ? (
            <div key={activeWish.id || activeWishIndex} className="min-h-40 border-y px-5 py-5 text-center animate-in fade-in-0 duration-300" style={{ borderColor: alpha(theme.accent_color, 0.2) }}>
              <p className="text-xl italic leading-8" style={{ fontFamily: `${theme.heading_font}, serif` }}>“{activeWish.message}”</p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] opacity-60">— {activeWish.guest_name || activeWish.name || 'A guest'}</p>
            </div>
          ) : (
            <Surface key={activeWish.id || activeWishIndex} theme={theme} className={`${quote ? 'text-center' : ''} min-h-40 p-6 animate-in fade-in-0 duration-300`} style={quote ? { background: 'transparent', boxShadow: 'none', borderRadius: 0, borderColor: alpha(theme.secondary_color, 0.5), borderLeftWidth: 0, borderRightWidth: 0 } : {}}>
              <p className={`${quote ? 'text-2xl' : 'text-lg'} italic leading-8`} style={quote ? { fontFamily: `${theme.heading_font}, serif` } : undefined}>“{activeWish.message}”</p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] opacity-55">{activeWish.guest_name || activeWish.name || 'A guest'}</p>
            </Surface>
          )
        ) : <Surface theme={theme} className="p-8 text-center text-base opacity-65">Be the first to leave a wish.</Surface>}
      </div>
      {wishPager}
      {wishForm}
    </section>
  );
}

function ActionSection({ section, theme, layout, onAction, preview, coupleNames, couplePhotoUrl }) {
  if (section.type === 'actions') {
    const shortcuts = [
      { action: 'guest_home', label: 'Guest home', Icon: House },
      { action: 'itinerary', label: 'Itinerary', Icon: CalendarBlank },
      { action: 'photos', label: 'Photos', Icon: Camera },
      { action: 'travel', label: 'Tickets & ID', Icon: Ticket },
      { action: 'accommodation', label: 'My room', Icon: MapPin },
      { action: 'wardrobe', label: 'Wardrobe', Icon: TShirt },
    ];
    return (
      <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
        <Surface theme={theme} className="mx-auto max-w-4xl overflow-hidden p-7 text-center" style={{ background: `linear-gradient(135deg, ${alpha(theme.primary_color, 0.11)}, ${alpha(theme.secondary_color, 0.16)})` }}>
          <SectionHeading eyebrow="For our guests" title={sectionText(section, 'headline', 'Everything you need')} body={sectionText(section, 'body', 'Private event details open securely when you select them.')} theme={theme} />
          <div className={`${section.variant === 'pills' ? 'flex flex-wrap justify-center' : 'grid grid-cols-2'} mt-7 gap-3`}>
            {shortcuts.map(({ action, label, Icon }) => (
              <button
                key={action}
                type="button"
                onClick={() => onAction?.(action)}
                className={`flex items-center justify-center gap-2 border bg-white/65 px-3 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${section.variant === 'pills' ? 'min-h-12 flex-row rounded-full' : 'min-h-24 flex-col'}`}
                style={{ borderColor: alpha(theme.primary_color, 0.14), borderRadius: section.variant === 'pills' ? '999px' : RADIUS[theme.corner_style] }}
              >
                <Icon className="h-6 w-6" weight="duotone" style={{ color: theme.accent_color }} />
                {preview ? `Preview ${label}` : label}
              </button>
            ))}
          </div>
        </Surface>
      </section>
    );
  }

  const champagneAction = layout?.section_style === 'champagne_toast';
  if (champagneAction && section.type === 'rsvp') {
    return (
      <section className="web-invite-toast-stationery relative overflow-hidden bg-[#F4E7D4] px-6 py-14 text-[#35151F]">
        <div className="pointer-events-none absolute -right-14 top-8 h-40 w-40 rounded-full border-[12px] border-[#B88732]/10" aria-hidden="true" />
        <article className="web-invite-toast-place-card relative mx-auto max-w-sm rotate-[1deg] border border-[#B88732] bg-[#FFF9EE] px-7 py-10 text-center shadow-[0_22px_45px_rgba(67,22,35,0.18)]">
          <div className="pointer-events-none absolute inset-2 border border-[#C99E51]/35" aria-hidden="true" />
          <Heart className="relative mx-auto h-5 w-5 text-[#B88732]" weight="fill" />
          <p className="relative mt-4 text-[0.58rem] font-semibold uppercase tracking-[0.26em] text-[#A56F20]">{sectionText(section, 'eyebrow', 'Reserved for you')}</p>
          <h2 className="relative mt-2 text-[2.7rem] font-normal leading-none text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', 'Your place awaits')}</h2>
          <p className="relative mx-auto mt-4 max-w-xs text-sm leading-6 text-[#4B2931]/72">{sectionText(section, 'body', 'Let the hosts know whether we should set a place for you.')}</p>
          <button type="button" onClick={() => onAction?.('rsvp')} className="relative mt-7 inline-flex items-center gap-2 border-b border-[#A56F20] px-2 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[#741A3A] transition-transform hover:-translate-y-0.5">
            {preview ? 'Preview RSVP' : 'Reserve my place'}<ArrowRight className="h-4 w-4" />
          </button>
        </article>
      </section>
    );
  }

  if (champagneAction && section.type === 'contacts') {
    return (
      <section className="relative isolate overflow-hidden bg-[#1B090E] px-6 py-14 text-[#FFF8EB]">
        {layout.background_image_url && <div className="absolute inset-0 -z-[2] bg-cover bg-[center_72%] opacity-20" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})` }} aria-hidden="true" />}
        <div className="absolute inset-0 -z-[1] bg-gradient-to-b from-[#220A12]/92 to-[#100508]/96" aria-hidden="true" />
        <div className="mx-auto max-w-sm text-center">
          <Phone className="mx-auto h-5 w-5 text-[#E9C36A]" weight="duotone" />
          <p className="mt-4 text-[0.58rem] font-semibold uppercase tracking-[0.26em] text-[#E9C36A]">{sectionText(section, 'eyebrow', 'At your service')}</p>
          <h2 className="mt-2 text-[2.75rem] font-normal leading-none" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', "The hosts' table")}</h2>
          <p className="mx-auto mt-3 max-w-xs text-sm leading-6 text-white/62">{sectionText(section, 'body', 'Questions about the evening? The hosts are close at hand.')}</p>
          <div className="relative mx-auto mt-8 w-[82%] border border-[#E9C36A]/45 bg-[#FFF9EE] px-5 py-6 text-[#4B2931] shadow-[0_18px_38px_rgba(0,0,0,.38)]">
            <span className="text-[0.54rem] font-semibold uppercase tracking-[0.22em] text-[#A56F20]">Host concierge</span>
            <p className="mt-2 text-[1.55rem] text-[#741A3A]" style={{ fontFamily: `${theme.heading_font}, serif` }}>Everything for the evening</p>
            <button type="button" onClick={() => onAction?.('contacts')} className="mt-4 inline-flex items-center gap-2 border-b border-[#A56F20] px-1 py-2 text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-[#741A3A]">
              {preview ? 'Preview contacts' : 'Contact the hosts'}<ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    );
  }

  const labels = {
    rsvp: ['Kindly respond', 'RSVP', Heart],
    registry: ['A note on gifts', 'View our registry', Gift],
    contacts: ['Need a hand?', 'View key contacts', Phone],
  };
  const [eyebrow, fallbackTitle, Icon] = labels[section.type];
  const waxSeal = section.type === 'rsvp' && section.variant === 'wax_seal';
  const betelLeaf = section.type === 'rsvp' && (section.variant === 'betel_leaf' || section.variant === 'lotus_seal');
  const royalDecree = section.type === 'rsvp' && section.variant === 'royal_decree';
  const riverbankRsvp = section.type === 'rsvp' && section.variant === 'riverbank_rsvp';
  const botanicalCard = section.variant === 'botanical_cards';
  const familyDirectory = section.type === 'contacts' && (section.variant === 'family_directory' || section.variant === 'brass_inscriptions');
  const courtDirectory = section.type === 'contacts' && section.variant === 'court_directory';
  const kashiDirectory = section.type === 'contacts' && section.variant === 'kashi_directory';
  const minimalAction = section.variant === 'minimal';
  const bannerAction = section.variant === 'banner';
  const seamlessAction = waxSeal || betelLeaf || royalDecree || riverbankRsvp || botanicalCard || familyDirectory || courtDirectory || kashiDirectory || minimalAction;
  const sectionCouplePhotoUrl = section.overrides?.couple_photo_url || couplePhotoUrl;
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
       <Surface theme={theme} className={`${seamlessAction ? 'border-0' : ''} ${bannerAction ? 'rounded-none border-y py-9' : ''} relative mx-auto max-w-3xl overflow-hidden p-5 text-center`} style={seamlessAction ? { background: 'transparent', borderColor: 'transparent', boxShadow: 'none' } : bannerAction ? { background: alpha(theme.secondary_color, 0.4), borderColor: theme.secondary_color, borderRadius: 0 } : { background: `linear-gradient(135deg, ${alpha(theme.primary_color, 0.11)}, ${alpha(theme.secondary_color, 0.16)})` }}>
        {botanicalCard && <span className="absolute left-5 top-3 text-4xl opacity-[0.15]" style={{ color: theme.accent_color }} aria-hidden="true">❦</span>}
        {riverbankRsvp || kashiDirectory ? (
          <div className="relative mx-auto mb-4 h-16 w-40" aria-hidden="true">
            <RiverRippleMark color={theme.primary_color} className="absolute inset-0 h-full w-full opacity-55" />
            {riverbankRsvp ? <Heart className="absolute left-1/2 top-5 h-5 w-5 -translate-x-1/2" weight="fill" style={{ color: theme.accent_color }} /> : <Phone className="absolute left-1/2 top-5 h-5 w-5 -translate-x-1/2" weight="duotone" style={{ color: theme.accent_color }} />}
          </div>
        ) : royalDecree ? (
          <div className="mx-auto mb-5 flex items-center justify-center gap-3" style={{ color: theme.secondary_color }} aria-hidden="true"><span className="h-px w-14 bg-current opacity-60" /><span className="h-3 w-3 rotate-45 border-2 border-current" /><span className="h-px w-14 bg-current opacity-60" /></div>
        ) : betelLeaf ? (
          <div className="relative mx-auto h-20 w-24" aria-hidden="true">
            <span className="absolute left-3 top-2 h-14 w-9 -rotate-[32deg] border" style={{ background: alpha(theme.accent_color, 0.15), borderColor: theme.accent_color, borderRadius: '85% 8% 85% 8%' }} />
            <span className="absolute right-3 top-2 h-14 w-9 rotate-[32deg] border" style={{ background: alpha(theme.accent_color, 0.15), borderColor: theme.accent_color, borderRadius: '8% 85% 8% 85%' }} />
            <Heart className="absolute left-1/2 top-7 h-5 w-5 -translate-x-1/2" weight="fill" style={{ color: theme.primary_color }} />
          </div>
        ) : waxSeal ? (
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border-4 text-white shadow-lg" style={{ background: `linear-gradient(145deg, ${theme.primary_color}, ${theme.accent_color})`, borderColor: alpha(theme.secondary_color, 0.72) }}>
            <Heart className="h-6 w-6" weight="fill" />
          </div>
        ) : <Icon className="mx-auto h-8 w-8" weight="duotone" style={{ color: familyDirectory ? theme.secondary_color : theme.accent_color }} />}
        <SectionHeading eyebrow={kashiDirectory ? 'Your riverbank hosts' : riverbankRsvp ? 'Come ashore with us' : courtDirectory ? 'The court directory' : familyDirectory ? 'Family directory' : eyebrow} title={sectionText(section, 'headline', fallbackTitle)} body={sectionText(section, 'body', section.type === 'rsvp' ? 'Sign in or register when you are ready to confirm your attendance.' : 'This guest action will open securely when selected.')} theme={theme} />
        {(waxSeal || betelLeaf || riverbankRsvp) && coupleNames && <p className="mt-3 text-3xl font-normal leading-none" style={{ color: theme.primary_color, fontFamily: `${theme.heading_font}, cursive` }}><CoupleNameStack names={coupleNames} /></p>}
        <button
          type="button"
          onClick={() => onAction?.(section.type)}
          className={`mt-4 inline-flex items-center gap-2 px-6 py-3 text-base font-semibold transition-transform hover:-translate-y-0.5 ${waxSeal || familyDirectory || courtDirectory || kashiDirectory ? 'border-b bg-transparent' : betelLeaf ? 'rounded-full border-2 text-[#FFF8E8]' : 'text-white'}`}
          style={riverbankRsvp ? { color: '#FFF9EA', background: `linear-gradient(145deg, ${theme.primary_color}, #173F46)`, borderRadius: '999px', border: `1px solid ${alpha(theme.secondary_color, 0.8)}`, boxShadow: `0 12px 30px ${alpha(theme.primary_color, 0.22)}` } : waxSeal || familyDirectory || courtDirectory || kashiDirectory ? { color: theme.primary_color, borderColor: alpha(theme.primary_color, 0.42) } : betelLeaf ? { background: theme.accent_color, borderColor: alpha(theme.secondary_color, 0.8), boxShadow: `0 10px 25px ${alpha(theme.accent_color, 0.22)}` } : royalDecree ? { color: '#FFF7E6', background: `linear-gradient(145deg, ${theme.accent_color}, #551522)`, borderRadius: '0', boxShadow: `inset 0 0 0 2px ${alpha(theme.secondary_color, 0.6)}, 0 12px 30px ${alpha(theme.primary_color, 0.18)}` } : bannerAction ? { color: theme.primary_color, background: '#FFFFFF', borderRadius: RADIUS[theme.corner_style] } : { background: theme.primary_color, borderRadius: RADIUS[theme.corner_style] }}
        >
          {preview ? 'Preview action' : fallbackTitle}<ArrowRight className="h-4 w-4" />
        </button>
      </Surface>
      {waxSeal && sectionCouplePhotoUrl && (
        <div className="web-invite-scroll-drift-reverse -mx-5 -mb-5 mt-2 h-64 overflow-hidden" style={{ WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 18%, black 84%, transparent 100%)', maskImage: 'linear-gradient(to bottom, transparent 0%, black 18%, black 84%, transparent 100%)' }}>
          <img src={resolveMediaUrl(sectionCouplePhotoUrl)} alt={coupleNames || 'The couple'} className="h-full w-full object-cover object-top" />
        </div>
      )}
    </section>
  );
}

function CustomSection({ section, theme }) {
  return (
    <section className="px-5" style={{ paddingTop: SPACING[theme.spacing], paddingBottom: SPACING[theme.spacing] }}>
      <Surface theme={theme} className={`mx-auto max-w-3xl p-8 text-center ${section.variant === 'feature' ? 'border-2 py-12' : section.variant === 'minimal' ? 'border-0 border-y' : ''}`} style={section.variant === 'feature' ? { background: alpha(theme.secondary_color, 0.28), borderColor: theme.accent_color } : section.variant === 'minimal' ? { background: 'transparent', boxShadow: 'none', borderRadius: 0, borderColor: alpha(theme.secondary_color, 0.5) } : {}}>
        <SectionHeading eyebrow={sectionText(section, 'eyebrow', 'A personal note')} title={sectionText(section, 'headline', 'Something special')} body={sectionText(section, 'body', 'Use this space for a poem, tradition, travel note or anything meaningful to you.')} theme={theme} />
      </Surface>
    </section>
  );
}

function FooterSection({ section, eventData, theme, layout, coupleNames }) {
  const gardenSignature = section.variant === 'garden_signature';
  const courtyardSignature = section.variant === 'courtyard_signature' || section.variant === 'deepam_signature';
  const wovenSignature = section.variant === 'woven_signature';
  const duskSignature = section.variant === 'dusk_signature';
  const champagneSignature = section.variant === 'birthday_signature' && layout?.section_style === 'champagne_toast';
  if (champagneSignature) {
    return (
      <footer className="relative isolate min-h-[520px] overflow-hidden px-6 py-16 text-center text-[#FFF8EB]">
        {layout.background_image_url && <div className="absolute inset-0 -z-[3] bg-cover bg-center" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})` }} aria-hidden="true" />}
        <div className="absolute inset-0 -z-[2] bg-gradient-to-b from-[#240A12]/88 via-[#3B0B1C]/78 to-[#110508]/94" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-[1] h-48 bg-gradient-to-t from-black/55 to-transparent" aria-hidden="true" />
        <div className="mx-auto flex min-h-[390px] max-w-sm flex-col items-center justify-center">
          <p className="text-[0.58rem] font-semibold uppercase tracking-[0.28em] text-[#E9C36A]">{sectionText(section, 'eyebrow', 'The final toast')}</p>
          <h2 className="mt-3 max-w-[350px] text-[2.65rem] font-normal leading-[0.98]" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'headline', "Here's to 50 beautiful years")}</h2>
          <div className="relative my-8 h-24 w-16" aria-hidden="true">
            <span className="absolute left-1/2 top-0 h-14 w-10 -translate-x-1/2 rounded-b-[45%] border border-[#F3D587]/70 bg-gradient-to-t from-[#D8A638]/35 to-transparent" />
            <span className="absolute left-1/2 top-14 h-7 w-px -translate-x-1/2 bg-[#F3D587]/70" />
            <span className="absolute bottom-1 left-1/2 h-px w-12 -translate-x-1/2 bg-[#F3D587]/70" />
            {Array.from({ length: 4 }, (_, index) => <span key={index} className="web-invite-toast-clock-bubble absolute rounded-full border border-[#F3D587]/60" style={{ left: `${24 + index * 14}%`, bottom: `${36 + (index % 2) * 16}%`, width: `${4 + index}px`, height: `${4 + index}px`, animationDelay: `${index * 200}ms` }} />)}
          </div>
          <p className="text-[1.2rem] italic text-white/78" style={{ fontFamily: `${theme.heading_font}, serif` }}>{sectionText(section, 'body', 'And to many more.')}</p>
          <span className="mt-8 h-px w-24 bg-gradient-to-r from-transparent via-[#E9C36A] to-transparent" aria-hidden="true" />
        </div>
      </footer>
    );
  }
  return (
    <footer className="relative overflow-hidden px-5 py-12 text-center text-white" style={{ background: gardenSignature ? `linear-gradient(145deg, ${theme.primary_color}, #3D2928)` : courtyardSignature ? theme.text_color : duskSignature ? '#173F46' : theme.primary_color }}>
      {gardenSignature && layout.decorative_image_url && <img src={resolveMediaUrl(layout.decorative_image_url)} alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-20 -left-20 w-64 opacity-35" />}
      {courtyardSignature && layout.background_image_url && <div className="pointer-events-none absolute inset-0 bg-cover bg-[center_78%] opacity-55" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})` }} aria-hidden="true" />}
      {courtyardSignature && <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#2B231C]/70 via-[#2B231C]/82 to-[#2B231C]/95" aria-hidden="true" />}
      {wovenSignature && layout.background_image_url && <div className="pointer-events-none absolute inset-0 bg-cover bg-[center_80%] opacity-45" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})` }} aria-hidden="true" />}
      {wovenSignature && <div className="pointer-events-none absolute inset-0 bg-[#111632]/75" aria-hidden="true" />}
      {duskSignature && layout.background_image_url && <div className="pointer-events-none absolute inset-0 bg-cover bg-[center_72%] opacity-60" style={{ backgroundImage: `url(${resolveMediaUrl(layout.background_image_url)})`, filter: 'saturate(.72) brightness(.62) hue-rotate(8deg)' }} aria-hidden="true" />}
      {duskSignature && <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#173F46]/55 via-[#173F46]/78 to-[#302621]/95" aria-hidden="true" />}
      <div className="relative z-[2]">
        {wovenSignature && <p className="mb-4 text-[0.65rem] uppercase tracking-[0.28em]" style={{ color: '#E9CB83' }}>Our story continues</p>}
        {courtyardSignature && <TempleKolamMark color={theme.secondary_color} className="mx-auto mb-4 h-14 w-14 opacity-80" />}
        {courtyardSignature && <p className="mb-3 text-xs uppercase tracking-[0.3em]" style={{ color: theme.secondary_color }}>Until we meet in the courtyard</p>}
        {gardenSignature && <p className="mb-4 text-2xl" style={{ color: theme.secondary_color }} aria-hidden="true">❦</p>}
        {duskSignature && <><p className="mb-2 text-[0.65rem] uppercase tracking-[0.28em]" style={{ color: '#F0C777' }}>Until we meet by the river</p><RiverRippleMark color="#F3E7CF" className="mx-auto mb-2 h-12 w-40 opacity-70" /></>}
         {section.variant !== 'minimal' && !duskSignature && <Heart className="mx-auto h-6 w-6" weight="fill" style={{ color: theme.secondary_color }} />}
         <p className={`${section.variant === 'signature' ? 'mt-6 border-y py-6 text-5xl' : section.variant === 'statement' ? 'mt-3 text-3xl' : 'mt-3 text-4xl'} font-normal leading-none`} style={{ fontFamily: `${theme.heading_font}, cursive`, borderColor: alpha(theme.secondary_color, 0.45) }}><CoupleNameStack names={sectionText(section, 'headline', coupleNames || displayNames(usesEventContent(section) ? eventData : {}))} /></p>
         <p className={`${section.variant === 'statement' ? 'mx-auto mt-6 max-w-xs text-xl normal-case tracking-normal' : 'mt-3 text-sm uppercase tracking-[0.16em]'} leading-6 text-white/70`}>{sectionText(section, 'body', 'We cannot wait to celebrate with you')}</p>
      </div>
    </footer>
  );
}

function InviteSection({ section, eventData, theme, layout, effects, onAction, preview, coupleNames, couplePhotoUrl, splitPanel = false, onSubmitWish, wishAuthorName }) {
  const props = { section, eventData, theme, layout, effects, onAction, preview, coupleNames, couplePhotoUrl, splitPanel, onSubmitWish, wishAuthorName };
  switch (section.type) {
    case 'hero': return <HeroSection {...props} />;
    case 'countdown': return <CountdownSection {...props} />;
    case 'story': return <StorySection {...props} />;
    case 'hosts': return <HostsSection {...props} />;
    case 'itinerary': return <ItinerarySection {...props} />;
    case 'venue': return <VenueSection {...props} />;
    case 'map': return <VenueMapSection {...props} />;
    case 'gallery': return <GallerySection {...props} />;
    case 'wishes': return <WishesSection {...props} />;
    case 'rsvp':
    case 'registry':
    case 'contacts':
    case 'actions': return <ActionSection {...props} />;
    case 'custom': return <CustomSection {...props} />;
    case 'footer': return <FooterSection {...props} />;
    default: return null;
  }
}

export default function WebInviteRenderer({ config, eventData = {}, onAction, onSubmitWish, wishAuthorName, onVisibleSectionChange, onEditSection, onOpeningStateChange, scrollToSection, initiallyOpened = false, preview = false, className = '' }) {
  const theme = { ...FALLBACK_THEME, ...(config?.theme || {}) };
  const layout = runtimeLayout(config);
  const effects = { hero_effect: 'none', intensity: 0.8, speed: 0.5, ...(config?.effects || {}) };
  const opening = { ...FALLBACK_OPENING, ...(config?.opening || {}) };
  if (config?.template_id === 'galago.south-indian-temple-brass' && opening.action_label === 'Light the first lamp') opening.action_label = 'Ring the wedding bell';
  const music = { ...FALLBACK_MUSIC, ...(config?.music || {}) };
  const sections = [...(config?.sections || [])].filter((section) => section.enabled).sort((a, b) => a.order - b.order);
  const sectionIdentity = sections.map((section) => section.id).join('|');
  const heroSection = sections.find((section) => section.type === 'hero');
  const heroContent = heroSection ? eventSource(heroSection, eventData) : eventData;
  const heroEchoUrl = heroArtwork(heroSection, eventData, layout);
  const coupleNames = displayNames(heroContent);
  const couplePhotoUrl = heroContent.couple_photo_url || heroContent.guest_experience?.couple_photo_url || heroContent.wedding_photos_preview?.[0] || null;
  const botanicalPaper = layout.section_style === 'botanical_paper';
  const templeCourtyardCanvas = layout.section_style === 'temple_courtyard' || layout.section_style === 'temple_parchment';
  const tapestryFolio = layout.section_style === 'tapestry_folio';
  const riverPaper = layout.section_style === 'river_paper';
  const champagneToastCanvas = layout.section_style === 'champagne_toast';
  const [opened, setOpened] = useState(opening.enabled === false || initiallyOpened);
  const [entryRevealing, setEntryRevealing] = useState(false);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const articleRef = useRef(null);
  const audioRef = useRef(null);
  const hasMusic = music.enabled && (music.track_id !== 'custom' || Boolean(music.custom_audio_url));
  const musicUrl = music.track_id === 'custom'
    ? resolveMediaUrl(music.custom_audio_url)
    : BUILT_IN_MUSIC_URLS[music.track_id];
  const canvasStyle = botanicalPaper ? {
    backgroundColor: theme.background_color,
    backgroundImage: `radial-gradient(circle at 8% 18%, ${alpha(theme.secondary_color, 0.18)}, transparent 25%), radial-gradient(circle at 92% 52%, ${alpha(theme.accent_color, 0.1)}, transparent 28%), linear-gradient(180deg, rgba(255,255,255,.38), transparent 42%, rgba(255,255,255,.18))`,
    backgroundAttachment: 'local',
  } : templeCourtyardCanvas ? {
    backgroundColor: theme.background_color,
    backgroundImage: `radial-gradient(circle at 8% 16%, ${alpha(theme.secondary_color, 0.12)}, transparent 22%), radial-gradient(circle at 92% 68%, ${alpha(theme.accent_color, 0.07)}, transparent 25%), repeating-linear-gradient(110deg, rgba(70,48,28,.018) 0, rgba(70,48,28,.018) 1px, transparent 1px, transparent 7px), linear-gradient(180deg, rgba(255,249,226,.2), transparent 42%, ${alpha(theme.secondary_color, 0.06)})`,
    backgroundAttachment: 'local',
  } : tapestryFolio ? {
    backgroundColor: theme.background_color,
    backgroundImage: `radial-gradient(circle at 12% 18%, ${alpha(theme.secondary_color, 0.13)}, transparent 25%), radial-gradient(circle at 88% 62%, ${alpha(theme.accent_color, 0.08)}, transparent 27%), repeating-linear-gradient(45deg, rgba(29,33,69,.018) 0, rgba(29,33,69,.018) 1px, transparent 1px, transparent 5px)`,
    backgroundAttachment: 'local',
  } : riverPaper ? {
    backgroundColor: theme.background_color,
    backgroundImage: `radial-gradient(ellipse at 8% 12%, ${alpha(theme.secondary_color, 0.14)}, transparent 24%), radial-gradient(ellipse at 94% 58%, ${alpha(theme.primary_color, 0.09)}, transparent 27%), repeating-linear-gradient(2deg, rgba(48,38,33,.018) 0, rgba(48,38,33,.018) 1px, transparent 1px, transparent 6px), linear-gradient(180deg, rgba(255,250,235,.32), transparent 44%, ${alpha(theme.primary_color, 0.035)})`,
    backgroundAttachment: 'local',
  } : champagneToastCanvas ? {
    backgroundColor: '#F7E8D9',
  } : {};

  const playMusic = async () => {
    if (!hasMusic) return;
    if (!audioRef.current || !musicUrl) return;
    audioRef.current.volume = music.volume;
    try {
      await audioRef.current.play();
      setMusicPlaying(true);
    } catch (_error) {
      setMusicPlaying(false);
    }
  };

  const pauseMusic = async () => {
    audioRef.current?.pause();
    setMusicPlaying(false);
  };

  const enterInvitation = () => {
    setEntryRevealing(true);
    setOpened(true);
    playMusic();
  };

  useEffect(() => {
    setOpened(opening.enabled === false || initiallyOpened);
    setEntryRevealing(false);
    if (audioRef.current) audioRef.current.pause();
    setMusicPlaying(false);
  }, [config?.template_id, initiallyOpened, opening.enabled]);

  useEffect(() => {
    onOpeningStateChange?.(!opened);
  }, [onOpeningStateChange, opened]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.pause();
    setMusicPlaying(false);
  }, [music.custom_audio_url, music.track_id, music.volume]);

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
  }, []);

  useEffect(() => {
    const article = articleRef.current;
    if (!article) return undefined;
    const scrollParent = findScrollParent(article);
    const updateViewportHeight = () => {
      const height = scrollParent?.clientHeight || window.innerHeight;
      article.style.setProperty('--invite-scrollport-height', `${height}px`);
    };
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    const observer = typeof ResizeObserver === 'undefined' || !scrollParent ? null : new ResizeObserver(updateViewportHeight);
    if (scrollParent) observer?.observe(scrollParent);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (!opened || !onVisibleSectionChange) return undefined;
    const article = articleRef.current;
    if (!article) return undefined;
    const scrollParent = findScrollParent(article);
    const sectionNodes = [...article.querySelectorAll('[data-invite-section-id]')];
    let frame = null;
    let lastSectionId = null;
    const updateVisibleSection = () => {
      frame = null;
      const rootRect = scrollParent?.getBoundingClientRect();
      const viewportTop = rootRect?.top ?? 0;
      const viewportBottom = rootRect?.bottom ?? window.innerHeight;
      const viewportCenter = viewportTop + (viewportBottom - viewportTop) / 2;
      let closest = null;
      sectionNodes.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.bottom <= viewportTop || rect.top >= viewportBottom) return;
        const containsCenter = rect.top <= viewportCenter && rect.bottom >= viewportCenter;
        const distance = containsCenter ? 0 : Math.min(Math.abs(rect.top - viewportCenter), Math.abs(rect.bottom - viewportCenter));
        if (!closest || distance < closest.distance) closest = { id: node.dataset.inviteSectionId, distance };
      });
      if (closest?.id && closest.id !== lastSectionId) {
        lastSectionId = closest.id;
        onVisibleSectionChange(closest.id);
      }
    };
    const requestVisibleUpdate = () => {
      if (frame == null) frame = window.requestAnimationFrame(updateVisibleSection);
    };
    updateVisibleSection();
    const target = scrollParent || window;
    target.addEventListener('scroll', requestVisibleUpdate, { passive: true });
    window.addEventListener('resize', requestVisibleUpdate);
    return () => {
      target.removeEventListener('scroll', requestVisibleUpdate);
      window.removeEventListener('resize', requestVisibleUpdate);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [onVisibleSectionChange, opened, sectionIdentity]);

  useEffect(() => {
    if (!opened || !scrollToSection?.id) return;
    const article = articleRef.current;
    if (!article) return;
    const node = [...article.querySelectorAll('[data-invite-section-id]')]
      .find((item) => item.dataset.inviteSectionId === scrollToSection.id);
    if (!node) return;
    const scrollParent = findScrollParent(article);
    if (!scrollParent) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const parentRect = scrollParent.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const top = scrollParent.scrollTop + nodeRect.top - parentRect.top - Math.max(0, (scrollParent.clientHeight - Math.min(nodeRect.height, scrollParent.clientHeight)) / 2);
    scrollParent.scrollTo({ top, behavior: 'smooth' });
  }, [opened, scrollToSection]);

  useEffect(() => {
    const extraFonts = String(config?.template_id || '').startsWith('galago.birthday-') ? ['Great Vibes', 'Playfair Display'] : [];
    const families = [...new Set([theme.heading_font, theme.body_font, ...extraFonts])]
      .filter((font) => font && !['Georgia', 'serif', 'sans-serif'].includes(font));
    if (!families.length) return;
    const linkId = 'web-invite-google-fonts';
    let link = document.getElementById(linkId);
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?${families.map((font) => `family=${encodeURIComponent(font).replace(/%20/g, '+')}`).join('&')}&display=swap`;
  }, [config?.template_id, theme.body_font, theme.heading_font]);

  return (
    <article
      ref={articleRef}
      onPointerMove={effects.pointer_interaction ? (event) => {
        // The spotlight is a mouse-hover effect, not a touch gesture. Updating
        // its paint layers during a tap competes with native mobile hit testing.
        if (event.pointerType !== 'mouse') return;
        const bounds = articleRef.current?.getBoundingClientRect();
        if (!bounds) return;
        articleRef.current.style.setProperty('--invite-pointer-x', `${event.clientX - bounds.left}px`);
        articleRef.current.style.setProperty('--invite-pointer-y', `${event.clientY - bounds.top}px`);
      } : undefined}
      className={`web-invite-renderer relative isolate mx-auto w-full max-w-[430px] overflow-clip shadow-2xl ${opened ? 'web-invite-opened' : ''} ${entryRevealing ? 'web-invite-entry-revealing' : ''} ${opening.enabled && !opened ? '' : 'min-h-screen'} ${effects.pointer_interaction ? 'web-invite-interactive' : ''} ${className}`}
      style={{ color: theme.text_color, background: theme.background_color, fontFamily: `${theme.body_font}, sans-serif`, ...canvasStyle, ...(opening.enabled && !opened ? { height: 'var(--invite-scrollport-height, 100svh)', minHeight: 'var(--invite-scrollport-height, 100svh)' } : {}) }}
    >
      <style>{`
        @keyframes web-invite-fall {
          0% { transform: translate3d(0, -8vh, 0) rotate(0deg); }
          50% { transform: translate3d(22px, 52vh, 0) rotate(160deg); }
          100% { transform: translate3d(-10px, 112vh, 0) rotate(330deg); }
        }
        @keyframes web-invite-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes web-invite-rise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes web-invite-scale { from { opacity: 0; transform: scale(.975); } to { opacity: 1; transform: scale(1); } }
        @keyframes web-invite-section-petal-fall {
          0% { opacity: 0; transform: translate3d(0, -20px, 0) rotate(0deg); }
          12% { opacity: .75; }
          100% { opacity: 0; transform: translate3d(24px, 145px, 0) rotate(240deg); }
        }
        @keyframes web-invite-temple-bell-swing {
          0% { transform: rotate(0deg); }
          18% { transform: rotate(8deg); }
          36% { transform: rotate(-7deg); }
          54% { transform: rotate(5deg); }
          72% { transform: rotate(-3deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes web-invite-temple-ring {
          0% { opacity: .75; transform: scale(.45); }
          100% { opacity: 0; transform: scale(2.3); }
        }
        @keyframes web-invite-kashi-voyage {
          0% { transform: translate3d(-62%, 14px, 0) scale(1.02) rotate(-1deg); }
          20% { transform: translate3d(-30%, 18px, 0) scale(1) rotate(-.7deg); }
          40% { transform: translate3d(4%, 14px, 0) scale(.97) rotate(-.2deg); }
          60% { transform: translate3d(40%, 2px, 0) scale(.9) rotate(.35deg); }
          80% { transform: translate3d(79%, -20px, 0) scale(.81) rotate(.7deg); }
          100% { transform: translate3d(124%, -52px, 0) scale(.7) rotate(.95deg); }
        }
        @keyframes web-invite-kashi-boat-bob {
          0%, 100% { transform: rotate(-.55deg); }
          50% { transform: rotate(.7deg); }
        }
        @keyframes web-invite-kashi-wake {
          0% { opacity: .18; transform: translateX(-8px) scaleX(.55); }
          28% { opacity: .72; }
          100% { opacity: 0; transform: translateX(-42px) scaleX(1.65); }
        }
        @keyframes web-invite-kashi-hero-arrive {
          from { opacity: 0; transform: translateY(14px) scale(.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes web-invite-kashi-portal-depart {
          from { opacity: 1; transform: scale(1); filter: none; }
          to { opacity: 0; transform: scale(1.1); filter: blur(1.25px); }
        }
        @keyframes web-invite-toast-cork-pop {
          0% { transform: translate3d(-50%, 0, 0) scale(1) rotate(0deg); opacity: 1; }
          12% { transform: translate3d(-50%, 2px, 0) scale(.96) rotate(1deg); opacity: 1; }
          22% { transform: translate3d(calc(-50% + 1px), -5px, 0) scale(1.02) rotate(-3deg); opacity: 1; }
          66% { transform: translate3d(calc(-50% - 18px), -58px, 0) scale(.98) rotate(-18deg); opacity: 1; }
          100% { transform: translate3d(calc(-50% - 30px), -86px, 0) scale(.82) rotate(-34deg); opacity: 0; }
        }
        @keyframes web-invite-mouth-pressure {
          0% { opacity: 0; transform: translateX(-50%) translateY(8px) scale(.32); filter: blur(0); }
          24% { opacity: .68; transform: translateX(-50%) translateY(0) scale(.62); filter: blur(.2px); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-36px) scale(.98); filter: blur(1.2px); }
        }
        @keyframes web-invite-mouth-droplet-rise {
          0% { opacity: 0; transform: translate3d(0, 0, 0) scale(.7); }
          18% { opacity: .78; }
          100% { opacity: 0; transform: translate3d(var(--drop-drift), -42px, 0) scale(.55); }
        }
        @keyframes web-invite-toast-spark {
          0% { opacity: .22; transform: translateX(-50%) scale(.72); }
          45% { opacity: .56; transform: translateX(-50%) scale(1.14); }
          100% { opacity: 0; transform: translateX(-50%) scale(1.38); }
        }
        @keyframes web-invite-toast-bubble-rise {
          0% { opacity: 0; transform: translate3d(0, 36px, 0) scale(.62); }
          18% { opacity: .86; }
          100% { opacity: 0; transform: translate3d(12px, -74vh, 0) scale(1.08); }
        }
        @keyframes web-invite-toast-hero-bubble {
          0%, 100% { transform: translateY(0) scale(.94); opacity: .38; }
          50% { transform: translateY(-15px) scale(1.08); opacity: .72; }
        }
        @keyframes web-invite-toast-label-arrive {
          from { opacity: 0; transform: translateY(20px) scale(.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes web-invite-toast-clock-turn {
          from { transform: rotate(-18deg); opacity: .36; }
          to { transform: rotate(0); opacity: 1; }
        }
        @keyframes web-invite-toast-paper-arrive {
          from { opacity: 0; transform: translateY(18px) rotate(-3deg); }
          to { opacity: 1; transform: translateY(0) rotate(-1.5deg); }
        }
        @keyframes web-invite-toast-copy-reveal {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes web-invite-toast-line-grow {
          from { transform: scaleY(0); }
          to { transform: scaleY(1); }
        }
        @keyframes web-invite-toast-menu-arrive {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes web-invite-wish-bubble-rise {
          0%, 100% { transform: translateY(8px) scale(.86); opacity: .16; }
          50% { transform: translateY(-13px) scale(1.06); opacity: .52; }
        }
        @keyframes web-invite-toast-wish-arrive {
          from { opacity: 0; transform: scale(.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes web-invite-balloon-piece-release {
          0% { opacity: 1; transform: translate3d(0, 0, 0) rotate(0deg); }
          25% { opacity: 1; transform: translate3d(var(--balloon-x1), -28vh, 0) rotate(var(--balloon-r1)); }
          50% { opacity: 1; transform: translate3d(var(--balloon-x2), -56vh, 0) rotate(var(--balloon-r2)); }
          75% { opacity: 1; transform: translate3d(var(--balloon-x3), -84vh, 0) rotate(var(--balloon-r3)); }
          100% { opacity: 1; transform: translate3d(var(--balloon-x5), -112vh, 0) rotate(var(--balloon-r4)); }
        }
        @keyframes web-invite-balloon-knot-release {
          0% { opacity: 0; transform: translate3d(-50%, 0, 0) scale(.82) rotate(0deg); }
          18% { opacity: .92; transform: translate3d(-50%, -1px, 0) scale(1) rotate(-3deg); }
          48% { opacity: .74; transform: translate3d(-50%, -5px, 0) scale(1.08) rotate(4deg); }
          100% { opacity: 0; transform: translate3d(-50%, -15px, 0) scale(.92) rotate(-6deg); }
        }
        @keyframes web-invite-balloon-pre-float {
          0%, 100% { transform: translate3d(0, 0, 0) rotate(var(--balloon-rest-rotate)); }
          50% { transform: translate3d(0, -5px, 0) rotate(var(--balloon-float-rotate)); }
        }
        @keyframes web-invite-balloon-title-leave {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(-24px) scale(.96); }
        }
        @keyframes web-invite-balloon-detail-arrive {
          from { opacity: 0; transform: translateY(24px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes web-invite-balloon-orb-float {
          0%, 100% { transform: translateY(0) rotate(-2deg); opacity: .58; }
          50% { transform: translateY(-18px) rotate(2deg); opacity: .82; }
        }
        @keyframes web-invite-music-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .web-invite-particle { animation-name: web-invite-fall; animation-timing-function: linear; animation-iteration-count: infinite; }
        .web-invite-section-petal { animation: web-invite-section-petal-fall 1.8s ease-in both; }
        .web-invite-temple-bell-ringing { animation: web-invite-temple-bell-swing .9s ease-in-out both; transform-origin: 50% 2%; }
        .web-invite-temple-ring { animation: web-invite-temple-ring .85s ease-out both; }
        .web-invite-kashi-voyage { transform: translate3d(-62%, 14px, 0) scale(1.02) rotate(-1deg); will-change: transform; }
        .web-invite-kashi-voyage-crossing { animation: web-invite-kashi-voyage 3.8s linear both; }
        .web-invite-kashi-boat { animation: web-invite-kashi-boat-bob 2.8s ease-in-out infinite; filter: drop-shadow(0 9px 8px rgba(23,63,70,.24)); transform-origin: 16% 70%; will-change: transform; }
        .web-invite-kashi-wake-active { animation: web-invite-kashi-wake 3.6s ease-out both; }
        .web-invite-kashi-portal { opacity: 1; transform: none; transform-origin: center; }
        .web-invite-kashi-opening-crossing .web-invite-kashi-portal { animation: web-invite-kashi-portal-depart 1.4s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-kashi-opening-scene { opacity: 1; transition: opacity .9s ease; }
        .web-invite-kashi-opening-revealing .web-invite-kashi-opening-scene { opacity: 0; }
        .web-invite-kashi-mist { background: linear-gradient(90deg, rgba(243,231,207,.3), transparent 32%, rgba(243,231,207,.18) 72%, transparent), radial-gradient(ellipse at 50% 58%, rgba(243,231,207,.28), transparent 52%); transition: opacity 900ms ease, transform 1100ms ease; }
        .web-invite-kashi-mist-clearing { opacity: 0; transform: translateY(-14px); }
        .web-invite-kashi-opening-copy, .web-invite-kashi-entry-action, .web-invite-kashi-entry-note { transition: opacity .7s ease, transform .8s cubic-bezier(.22,.61,.36,1); }
        .web-invite-kashi-opening-copy-leaving { opacity: 0; transform: translateY(-12px); }
        .web-invite-kashi-entry-action-leaving, .web-invite-kashi-entry-note-leaving { opacity: 0; transform: translateY(10px); }
        .web-invite-kashi-hero-copy { opacity: 0; transform: translateY(14px) scale(.985); }
        .web-invite-renderer.web-invite-entry-revealing .web-invite-kashi-hero-copy,
        .web-invite-renderer.web-invite-opened .web-invite-kashi-hero-copy { animation: web-invite-kashi-hero-arrive 1s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-toast-bubble { opacity: 0; animation: web-invite-toast-bubble-rise 1.6s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-bottle-static { transform: none; opacity: 1; }
        .web-invite-real-cork { transform-origin: 50% 84%; will-change: transform, opacity; }
        .web-invite-toast-opening-popped .web-invite-toast-cork-popped { animation: web-invite-toast-cork-pop .92s cubic-bezier(.2,.74,.24,1) both; }
        .web-invite-toast-opening-popped .web-invite-toast-spark { animation: web-invite-toast-spark 1.15s ease-out both; }
        .web-invite-toast-opening-popped .web-invite-real-pressure { animation: web-invite-mouth-pressure .86s ease-out both; transform-origin: 50% 100%; }
        .web-invite-toast-opening-popped .web-invite-mouth-droplet { animation: web-invite-mouth-droplet-rise .78s cubic-bezier(.18,.74,.22,1) both; animation-delay: var(--drop-delay); }
        .web-invite-toast-hero-bubble { animation: web-invite-toast-hero-bubble 3.2s ease-in-out infinite; }
        .web-invite-toast-divider-bubble { animation: web-invite-toast-hero-bubble 2.6s ease-in-out infinite; }
        .web-invite-renderer.web-invite-entry-revealing .web-invite-toast-hero-label,
        .web-invite-renderer.web-invite-opened .web-invite-toast-hero-label { animation: web-invite-toast-label-arrive .82s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-toast-clock { border: 1px solid rgba(233,195,106,.72); box-shadow: inset 0 0 0 6px rgba(233,195,106,.06), 0 22px 55px rgba(0,0,0,.28); }
        .web-invite-reveal-rise .web-invite-toast-clock { animation: web-invite-toast-clock-turn 1.1s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-toast-clock-bubble { animation: web-invite-toast-hero-bubble 3.2s ease-in-out infinite; }
        .web-invite-reveal-rise .web-invite-toast-place-card { animation: web-invite-toast-paper-arrive .85s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-reveal-rise .web-invite-toast-letter-copy { animation: web-invite-toast-copy-reveal .7s .32s ease both; }
        .web-invite-toast-program-line { transform: scaleY(0); }
        .web-invite-reveal-rise .web-invite-toast-program-line { animation: web-invite-toast-line-grow 1.15s .12s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-toast-menu-item { opacity: 0; }
        .web-invite-reveal-rise .web-invite-toast-menu-item { animation: web-invite-toast-menu-arrive .62s ease both; }
        .web-invite-reveal-rise .web-invite-toast-menu-item:nth-child(2) { animation-delay: .12s; }
        .web-invite-reveal-rise .web-invite-toast-menu-item:nth-child(3) { animation-delay: .24s; }
        .web-invite-reveal-rise .web-invite-toast-menu-item:nth-child(4) { animation-delay: .36s; }
        .web-invite-wish-bubble { animation: web-invite-wish-bubble-rise 3.8s ease-in-out infinite; }
        .web-invite-toast-wish { animation: web-invite-toast-wish-arrive .52s ease both; }
        .web-invite-balloon-clean-scene { filter: saturate(.96) brightness(.86); transform: scale(1.015); }
        .web-invite-balloon-release-scene { opacity: .96; transition: opacity .32s .08s ease; filter: saturate(.96) brightness(.86); transform: scale(1.015); }
        .web-invite-balloon-opening-releasing .web-invite-balloon-release-scene { opacity: 0; }
        .web-invite-balloon-complete-image { filter: drop-shadow(0 12px 13px rgba(0,0,0,.22)); }
        .web-invite-balloon-string-shadow, .web-invite-balloon-string-highlight { stroke-linecap: round; }
        .web-invite-balloon-string-shadow { stroke: rgba(52, 34, 24, .58); stroke-width: 1.45px; }
        .web-invite-balloon-string-highlight { stroke: rgba(246, 218, 163, .88); stroke-width: .62px; }
        .web-invite-balloon-source-piece { opacity: 0; will-change: transform, opacity; }
        .web-invite-balloon-opening-releasing .web-invite-balloon-source-piece { animation: web-invite-balloon-piece-release var(--balloon-duration) linear var(--balloon-delay) both; }
        .web-invite-balloon-release-knot { left: 50%; top: 72.5%; width: 46px; height: 30px; opacity: 0; transform: translateX(-50%); }
        .web-invite-balloon-release-knot::before, .web-invite-balloon-release-knot::after { content: ''; position: absolute; left: 50%; top: 2px; width: 24px; height: 22px; border-top: 1px solid rgba(255, 238, 196, .88); border-radius: 50%; transform-origin: 0 0; }
        .web-invite-balloon-release-knot::before { transform: rotate(18deg); }
        .web-invite-balloon-release-knot::after { transform: rotate(162deg); }
        .web-invite-balloon-opening-releasing .web-invite-balloon-release-knot { animation: web-invite-balloon-knot-release .46s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-balloon-title-leaving { animation: web-invite-balloon-title-leave .82s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-balloon-orb { animation: web-invite-balloon-orb-float 3.8s ease-in-out infinite; }
        .web-invite-renderer.web-invite-entry-revealing .web-invite-balloon-details-heading,
        .web-invite-renderer.web-invite-opened .web-invite-balloon-details-heading { animation: web-invite-balloon-detail-arrive .9s cubic-bezier(.22,.61,.36,1) both; }
        .web-invite-music-fab { animation: web-invite-music-float 2.8s ease-in-out infinite; }
        .web-invite-scroll-drift { transform: translate3d(0, var(--invite-scroll-shift, 0px), 0); transition: transform .12s linear; }
        .web-invite-scroll-drift-reverse { transform: translate3d(0, var(--invite-scroll-shift-reverse, 0px), 0) rotate(var(--invite-scroll-rotate, 0deg)); transition: transform .12s linear; }
        .web-invite-scroll-bloom { transform: scale(var(--invite-scroll-scale, 1)); transform-origin: center; transition: transform .12s linear; }
        .web-invite-scroll-gallery { transform: translate3d(0, var(--invite-scroll-shift, 0px), 0) rotate(var(--invite-scroll-rotate, 0deg)); transition: transform .12s linear; }
        .web-invite-scroll-gallery-reverse { transform: translate3d(0, var(--invite-scroll-shift-reverse, 0px), 0) rotate(var(--invite-scroll-rotate-reverse, 0deg)); }
        .web-invite-horizontal-scroll { -ms-overflow-style: none; scrollbar-width: none; touch-action: pan-x pan-y; }
        .web-invite-horizontal-scroll::-webkit-scrollbar { display: none; }
        .web-invite-renderer button { touch-action: manipulation; }
        @media (hover: hover) and (pointer: fine) {
          .web-invite-interactive:hover .web-invite-particle { filter: drop-shadow(0 0 7px currentColor); }
          .web-invite-interactive #invite-hero::after { content: ''; pointer-events: none; position: absolute; z-index: 2; inset: 0; background: radial-gradient(170px circle at var(--invite-pointer-x, 50%) var(--invite-pointer-y, 50%), rgba(255,255,255,.22), transparent 75%); mix-blend-mode: screen; }
        }
        .web-invite-parallax-portrait { transform: translate3d(0, var(--invite-scroll-shift, 0px), 0) scale(1.08); transition: transform .12s linear; }
        .web-invite-awaiting { opacity: 0; }
        .web-invite-reveal-fade { animation: web-invite-fade .7s ease both; }
        .web-invite-reveal-rise { animation: web-invite-rise .7s ease both; }
        .web-invite-reveal-scale { animation: web-invite-scale .7s ease both; }
        @media (prefers-reduced-motion: reduce) { .web-invite-particle, .web-invite-section-petal, .web-invite-temple-bell-ringing, .web-invite-temple-ring, .web-invite-kashi-voyage-crossing, .web-invite-kashi-boat, .web-invite-kashi-wake-active, .web-invite-kashi-opening-crossing .web-invite-kashi-portal, .web-invite-kashi-hero-copy, .web-invite-toast-bubble, .web-invite-toast-cork-popped, .web-invite-toast-spark, .web-invite-real-pressure, .web-invite-mouth-droplet, .web-invite-toast-hero-bubble, .web-invite-toast-divider-bubble, .web-invite-toast-clock, .web-invite-toast-clock-bubble, .web-invite-toast-place-card, .web-invite-toast-letter-copy, .web-invite-toast-program-line, .web-invite-toast-menu-item, .web-invite-wish-bubble, .web-invite-toast-wish, .web-invite-balloon-release-scene, .web-invite-balloon-source-piece, .web-invite-balloon-release-knot, .web-invite-balloon-title-leaving, .web-invite-balloon-orb, .web-invite-balloon-details-heading, .web-invite-music-fab, [class*="web-invite-reveal-"] { animation: none !important; } .web-invite-toast-program-line { transform: scaleY(1) !important; } .web-invite-toast-menu-item { opacity: 1 !important; transform: none !important; } .web-invite-kashi-opening-crossing .web-invite-kashi-portal { opacity: 0 !important; } .web-invite-kashi-hero-copy { opacity: 1 !important; transform: none !important; } .web-invite-scroll-drift, .web-invite-scroll-drift-reverse, .web-invite-scroll-bloom, .web-invite-scroll-gallery { transform: none !important; transition: none !important; } .web-invite-curtain, .web-invite-curtain-copy, .web-invite-kashi-opening-scene, .web-invite-kashi-mist, .web-invite-kashi-opening-copy, .web-invite-kashi-entry-action, .web-invite-kashi-entry-note, .web-invite-toast-opening-copy, .web-invite-toast-opening-note, .web-invite-balloon-note, .web-invite-balloon-release-action { transition-duration: 1ms !important; } .web-invite-awaiting { opacity: 1; } }
      `}</style>
      {hasMusic && musicUrl && <audio ref={audioRef} src={musicUrl} loop preload={preview ? 'none' : 'metadata'} />}

      {opening.enabled && !opened && (
        <OpeningScreen opening={opening} heroSection={heroSection} eventData={eventData} theme={theme} effects={effects} layout={layout} onEnter={enterInvitation} onReveal={() => setEntryRevealing(true)} withMusic={hasMusic} />
      )}

      {opened && hasMusic && (
        <div className="pointer-events-none sticky z-[60] flex h-0 w-full justify-end pr-4" style={{ top: 'calc(var(--invite-scrollport-height, 100svh) - 72px)' }}>
          <button
            type="button"
            onClick={() => { if (musicPlaying) pauseMusic(); else playMusic(); }}
            className="web-invite-music-fab pointer-events-auto grid h-[52px] w-[52px] place-items-center rounded-full border text-white shadow-2xl backdrop-blur hover:brightness-110"
            style={{ background: alpha(theme.primary_color, 0.94), borderColor: alpha(theme.secondary_color, 0.7), fontFamily: `${theme.body_font}, sans-serif` }}
            aria-label={musicPlaying ? 'Pause invitation music' : 'Play invitation music'}
            title={musicPlaying ? 'Pause music' : 'Play music'}
          >
            {musicPlaying ? <Pause className="h-5 w-5" weight="fill" /> : <Play className="ml-0.5 h-5 w-5" weight="fill" />}
          </button>
        </div>
      )}

      {/* Opening artwork obscures the entire invite. Do not paint/animate its
          long hidden body until the reveal actually starts, especially on iOS. */}
      {sections.length ? (opened || entryRevealing || opening.enabled === false) && sections.map((section, index) => (
        <React.Fragment key={section.id}>
          {index > 0 && layout.section_divider === 'botanical' && <BotanicalDivider theme={theme} sectionType={section.type} />}
          {index > 0 && (layout.section_divider === 'temple_steps' || layout.section_divider === 'temple_arch') && <TempleStepDivider theme={theme} sectionType={section.type} />}
          {index > 0 && layout.section_divider === 'woven_band' && <WovenBandDivider theme={theme} layout={layout} sectionType={section.type} />}
          {index > 0 && layout.section_divider === 'waterline' && <WaterlineDivider theme={theme} sectionType={section.type} />}
          {index > 0 && layout.section_divider === 'bubble_stream' && <BubbleStreamDivider theme={theme} sectionType={section.type} />}
          <RevealSection
            sectionId={section.id}
            sectionType={section.type}
            onEdit={onEditSection}
            effect={section.type === 'hero' ? 'none' : effects.scroll_reveal}
            petalBurst={botanicalPaper && BOTANICAL_PETAL_SECTIONS.has(section.type)}
            petalColor={theme.accent_color}
            style={layout.section_divider === 'botanical' && PAPER_PANEL_SECTIONS.has(section.type)
              ? paperPanelStyle(theme, index)
              : { background: 'transparent' }}
          >
            {botanicalPaper && BOTANICAL_SPRAY_SECTIONS.has(section.type) && <BotanicalSpray url={layout.decorative_image_url} index={index} />}
            {!botanicalPaper && layout.section_style !== 'temple_courtyard' && layout.section_style !== 'river_paper' && layout.section_style !== 'champagne_toast' && section.type === 'story' && layout.decorative_image_url && <img src={resolveMediaUrl(layout.decorative_image_url)} alt="" className="pointer-events-none absolute -right-8 top-1/2 z-[1] w-32 -translate-y-1/2 opacity-30" aria-hidden="true" />}
            {botanicalPaper && HERO_ECHO_SECTIONS.has(section.type) && <HeroEcho url={heroEchoUrl} index={index} theme={theme} />}
            <div className="relative z-[2]">
              <InviteSection section={section} eventData={eventData} theme={theme} layout={layout} effects={effects} onAction={onAction} onSubmitWish={onSubmitWish} wishAuthorName={wishAuthorName} preview={preview} coupleNames={coupleNames} couplePhotoUrl={couplePhotoUrl} />
            </div>
          </RevealSection>
        </React.Fragment>
      )) : (
        <div className="grid min-h-[420px] place-items-center px-6 text-center">
          <div><Sparkle className="mx-auto h-9 w-9 opacity-35" /><p className="mt-3 text-sm opacity-60">Enable sections to start building your invite.</p></div>
        </div>
      )}
    </article>
  );
}
